
import { Component, Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { StoreModel } from '@/models/store';
import { PurchaseOrderModel, IPurchaseOrder, IPurchaseOrderItem, IPurchaseOrderCostData, IPurchaseOrderHistoryItem, IPurchaseOrderFinancial, PoEmailRateLimitError, poEmailRateLimitMessage } from '@/models/purchase_order';
import { SupplierModel } from '@/models/supplier';
import { StoreLocationModel } from '@/models/store_location';
import { StoreLocationList } from '@/collections/store_locations';
import dateFormat from 'dateformat';
import { jsPDF } from 'jspdf';
import { PurchaseReceived, IPurchaseReceived, IPurchaseReceivedItem } from '@/models/purchase_received';
import { EventBus } from '@/main';
import { ax, getIdentityToken, get, post, put, del, extractApiErrorMessage } from '@/services/http';
import { isUsableImageUrl } from '@/helpers/variantImage';
import {
  canOfferCompletion,
  countOutstandingAfterReceive,
  countOutstandingLines,
  isNothingLeftToReceive,
  writeOffWarning,
} from '@/helpers/poCompletion';
import {
  OVERFLOW_ONLY_ACTIONS,
  hasSecondaryActions as computeHasSecondaryActions,
  buildReloadImagesMessage,
  parseApiErrorMessage,
  ReloadImagesResult,
} from '@/helpers/poReloadImages';
import {
  listUserVariables as listPOTemplateUserVariables,
  render as renderPOTemplate,
  humanizeVariableLabel as humanizePOTemplateVariable,
  POEmailTemplate as POEmailTemplateType,
  SubstitutionContext as POTemplateContext,
} from '@/services/po-email-template-substitution';
import {
  ColumnTemplate,
  DEFAULT_TEMPLATE_ID,
  shapeTemplateList,
  resolveTemplate,
  enabledColumnIds,
} from '@/services/po-column-template';
import { Route } from 'vue-router';
import { resolveBackToListPath, PO_LIST_BASE_PATH } from '@/views/purchase-order/purchaseOrderListQuery';
import { describeCostSync } from '@/views/purchase-order/poCostFinalization';
import {
  getCategoryChipColor,
  formatCategoryLabel,
  resolveLocationName,
  resolveItemLabel,
  buildFailedItemRows,
  runRetry,
  canRelinkSyncFailure,
  syncFailureFallbackMessage,
  FailedItemRow,
  SkippedRetryLine,
  TerminalRetry,
} from '@/views/purchase-order/retry_sync_helpers';
import ImportItemsDialog from '@/components/purchase-order/ImportItemsDialog.vue';
import CostSummaryDialog from '@/components/purchase-order/CostSummaryDialog.vue';
import PurchaseOrderAttachments from '@/components/purchase-order/PurchaseOrderAttachments.vue';
import POStatusTimeline from '@/components/purchase-order/POStatusTimeline.vue';
import POLifecycleGuide from '@/components/purchase-order/POLifecycleGuide.vue';
import POFinancialEditDialog from '@/components/purchase-order/POFinancialEditDialog.vue';
import POPaymentSection from '@/components/purchase-order/POPaymentSection.vue';
import LandedCostBreakdown from '@/components/purchase-order/LandedCostBreakdown.vue';
import AddProductsDialog from '@/components/purchase-order/AddProductsDialog.vue';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import { parseMetafieldConfigKey, MetafieldOwnerResource } from '@/utils/metafieldColumnKey';
import { generateIdempotencyKey } from '@/utils/idempotency';
import RelinkShopifyVariantDialog from '@/components/purchase-order/RelinkShopifyVariantDialog.vue';
import AdjustQuantityDialog from './AdjustQuantityDialog.vue';
import FinalisePoCostsDialog from './FinalisePoCostsDialog.vue';
import ApplyPoTaxDialog from './ApplyPoTaxDialog.vue';
import { canApplyTax } from './poTaxApply';
import { ccRecipientsRules, toRecipientsRules, uniqueCcRecipients } from './poEmailRecipients';
import { SupplierList } from '@/collections/suppliers';
import { FilterColumnModel } from '@/models/filter_column';
import { FilterColumnList } from '@/collections/filter_columns';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { TaxTypeList } from '@/collections/tax_types';
import { TaxTypeModel } from '@/models/tax_type';
import TryNewAtlasBanner from '@/components/TryNewAtlasBanner.vue';
import { getFeatureVersion, setFeatureVersion, purchaseOrderSwitchTarget } from '@/config/atlasFeatures';
import ManageColumnsMenu from '@/components/purchase-order/ManageColumnsMenu.vue';
import {
  TOGGLEABLE_COLUMN_LABELS,
  DEFAULT_VISIBLE_COLUMNS as DEFAULT_VISIBLE_RECEIVE_COLUMNS,
  filterReceiveHeaders,
  fetchReceiveColumnsConfig,
  saveReceiveColumnsConfig,
  LIVE_STOCK_COLUMN_IDS,
} from '@/views/purchase-order/poReceiveColumns';
import type { LiveStockColumnId } from '@/views/purchase-order/poReceiveColumns';
// AS-11685 — live Shopify stock columns in the receive dialog.
import { fetchInventoryLevels } from '@/services/inventoryLevels';
import type { LiveLevels } from '@/services/inventoryLevels';
import {
  liveStockNames,
  visibleLiveStockColumns as pickVisibleLiveStockColumns,
  liveStockRequestKey,
  effectiveLiveStockStatus,
  liveStockCell,
  projectedReceiveByItem,
  projectedReceiveFor,
  updatedAgoLabel,
} from '@/views/purchase-order/poReceiveLiveStock';
import type { LiveStockStatus, LiveStockCellView } from '@/views/purchase-order/poReceiveLiveStock';
// US-092 — cost arithmetic + merchant-wide supplier-cost toggle for the receive dialog.
import {
  buildInitialReceiveCosts,
  supplierToStoreRate,
  landedCostForEnteredCost,
  shouldShowSupplierCostReplacement,
  hasUsableSupplierToStoreRate,
} from '@/views/purchase-order/poReceiveCosts';
import {
  fetchUpdateSupplierCostPreference,
  saveUpdateSupplierCostPreference,
} from '@/views/purchase-order/poReceivePreferences';
import { fetchSupplierCostMap } from '@/helpers/poCostResolver';
import { fetchLocationStock, locationStockHeaderText, LocationStockByVariant } from '@/utils/purchase-order/locationStock';
import {
  extractReceiveError,
  isRelinkableReceiveError,
  RELINKABLE_CATEGORY,
  IUnresolvableReceiveLine,
} from './receiveErrors';
// Constants for Shopify sync retry logic
const RETRYABLE_SYNC_STATUSES = ['failed', 'pending', 'partial'] as const;

/**
 * Helper function to determine if a sync status is retryable
 * @param status - The sync status to check (can be undefined or null)
 * @returns true if the status is retryable, false otherwise
 */
function isRetryableStatus(status?: string | null): boolean {
  if (!status) return false;
  return RETRYABLE_SYNC_STATUSES.includes(status as any);
}

// AS-10834: rendered from a location-filtered fetch, not the main MasterData call
const LOCATION_STOCK_COLUMN = 'location_inventory_quantity';

// Phase 1: Shared column codes - maps internal column IDs to API field names
// These codes MUST match what the MasterData API returns in column.code
const COLUMN_API_CODES: { [key: string]: string } = {
  // PO Status (from PO line items, not MasterData)
  received_quantity: 'receivedQuantity',
  returned_quantity: 'returnedQuantity',
  units_left: 'unitsLeft', // Calculated: orderedQuantity - receivedQuantity

  // Basic Information
  variant_id: 'variant_id',
  product_id: 'product_id',
  item_description: 'Item[Item Description]',
  product_barcode: 'product_barcode',
  vendor_name: 'Vendor Name',
  product_type: 'product_type',
  product_status: 'product_status',

  // Pricing
  unit_cost: 'unit_cost',
  selling_price: 'unit_price',
  retail_value: '[retail_value]',
  actual_inventory_cost: '[Actual Inventory Cost]',

  // Quantities
  actual_inventory_quantity: '[Actual Inventory Quantity]',
  committed_quantity: '[committed_quantity]',
  incoming_quantity: '[incoming_quantity]',
  available_quantity: '[available_quantity]',

  // Product Attributes
  total_weight: 'total_weight',
  weight_unit: 'weight_unit',
  hs_code: 'hs_code',
  country_of_origin: 'country_of_origin',
  inventory_tracking: 'inventory_tracking',
  continue_selling_out_stock: 'continue_selling_out_stock',

  // Metadata
  product_tags: 'product_tags',
  product_collections: 'product_collections',
  product_published_date: 'product_published_date'
};

// Columns that come from PO line items instead of MasterData
const PO_ITEM_COLUMNS = ['note', 'received_quantity', 'returned_quantity', 'units_left', 'tax_type', 'tax_rate', 'tax_amount', 'landed_cost', 'landed_cost_shipping_per_unit', 'landed_cost_adjustment_per_unit', 'margin_amount', 'margin_percent', 'landed_margin_amount', 'landed_margin_percent'];

// Numeric columns that should be right-aligned
const NUMERIC_COLUMNS = [
  'received_quantity', 'returned_quantity', 'units_left',
  'actual_inventory_quantity', 'location_inventory_quantity', 'committed_quantity', 'incoming_quantity', 'available_quantity',
  'unit_cost', 'selling_price', 'retail_value', 'actual_inventory_cost',
  'tax_rate', 'tax_amount', 'landed_cost',
  'landed_cost_shipping_per_unit', 'landed_cost_adjustment_per_unit',
  'margin_amount', 'margin_percent', 'landed_margin_amount', 'landed_margin_percent'
];

// Reverse mapping: API code -> column ID (for parsing API responses)
const COLUMN_ID_BY_API_CODE: { [key: string]: string } = Object.fromEntries(
  Object.entries(COLUMN_API_CODES).map(([id, code]) => [code, id])
);

// Phase 1: Column metadata for optional MasterData columns
const COLUMN_METADATA: { [key: string]: { label: string; category: string } } = {
  // PO Line Item Fields (from PO line items)
  note: { label: 'Note', category: 'po_line_item' },
  received_quantity: { label: 'Received Quantity', category: 'po_line_item' },
  returned_quantity: { label: 'Returned Quantity', category: 'po_line_item' },
  units_left: { label: 'Units Left', category: 'po_line_item' },
  tax_type: { label: 'Tax Type', category: 'po_line_item' },
  tax_rate: { label: 'Tax %', category: 'po_line_item' },
  tax_amount: { label: 'Tax Amount', category: 'po_line_item' },
  landed_cost: { label: 'Landed Cost', category: 'po_line_item' },
  landed_cost_shipping_per_unit: { label: 'Shipping / Unit', category: 'po_line_item' },
  landed_cost_adjustment_per_unit: { label: 'Adjustment / Unit', category: 'po_line_item' },
  margin_amount: { label: 'Margin', category: 'po_line_item' },
  margin_percent: { label: 'Margin %', category: 'po_line_item' },
  landed_margin_amount: { label: 'Landed Margin', category: 'po_line_item' },
  landed_margin_percent: { label: 'Landed Margin %', category: 'po_line_item' },

  // Basic Information
  variant_id: { label: 'Variant ID', category: 'basic' },
  product_id: { label: 'Product ID', category: 'basic' },
  item_description: { label: 'Item Description', category: 'basic' },
  product_barcode: { label: 'Product Barcode', category: 'basic' },
  vendor_name: { label: 'Vendor Name', category: 'basic' },
  product_type: { label: 'Product Type', category: 'basic' },
  product_status: { label: 'Product Status', category: 'basic' },

  // Pricing
  unit_cost: { label: 'Unit Cost', category: 'pricing' },
  selling_price: { label: 'Selling Price', category: 'pricing' },
  retail_value: { label: 'Retail Value', category: 'pricing' },
  actual_inventory_cost: { label: 'Actual Inventory Cost', category: 'pricing' },

  // Quantities
  actual_inventory_quantity: { label: 'Actual Inventory Quantity', category: 'quantities' },
  location_inventory_quantity: { label: 'Stock at PO Location', category: 'quantities' },
  committed_quantity: { label: 'Committed Quantity', category: 'quantities' },
  incoming_quantity: { label: 'Incoming Quantity', category: 'quantities' },
  available_quantity: { label: 'Available Quantity', category: 'quantities' },

  // Product Attributes
  total_weight: { label: 'Total Weight', category: 'attributes' },
  weight_unit: { label: 'Weight Unit', category: 'attributes' },
  hs_code: { label: 'HS Code', category: 'attributes' },
  country_of_origin: { label: 'Country of Origin', category: 'attributes' },
  inventory_tracking: { label: 'Inventory Tracking', category: 'attributes' },
  continue_selling_out_stock: { label: 'Continue Selling Out of Stock', category: 'attributes' },

  // Metadata
  product_tags: { label: 'Product Tags', category: 'metadata' },
  product_collections: { label: 'Product Collections', category: 'metadata' },
  product_published_date: { label: 'Product Published Date', category: 'metadata' },
};

@Component({
  components: {
    ImportItemsDialog,
    CostSummaryDialog,
    PurchaseOrderAttachments,
    POStatusTimeline,
    POLifecycleGuide,
    POFinancialEditDialog,
    POPaymentSection,
    LandedCostBreakdown,
    AddProductsDialog,
    CopyLinkButton,
    AdjustQuantityDialog,
    ApplyPoTaxDialog,
    RelinkShopifyVariantDialog,
    TryNewAtlasBanner,
    ManageColumnsMenu,
    FinalisePoCostsDialog,
  },
  // US-050: capture the referrer route so the "Back to Purchase Orders"
  // button can preserve the list's filter/pagination/sort query string.
  // Must live inside @Component({...}) options (not as a class method),
  // because vue-class-component@7.x does not auto-recognize route hooks
  // on class methods without Component.registerHooks() at startup. This
  // project uses the options-block pattern (see CustomColumnValues.vue's
  // beforeRouteLeave).
  beforeRouteEnter(to: Route, from: Route, next: (vm?: any) => void) {
    next((vm: any) => {
      vm.listReturnPath = resolveBackToListPath(from.path, from.fullPath);
    });
  }
})
export default class PurchaseOrderDetail extends Vue {
  @Prop({ type: [String, Number], required: true }) id!: string | number;

  @Prop({ type: Object, required: true }) store!: StoreModel;

  // US-050: path to return to when the user clicks "Back to Purchase
  // Orders" in the header or error states. Populated by the
  // beforeRouteEnter hook in the @Component options block above.
  // Falls back to the bare list URL when the detail page was
  // deep-linked or reached from a route outside the PO list.
  private listReturnPath: string = PO_LIST_BASE_PATH;

  private goBackToList() {
    // replace (not push) so the detail-page entry is swapped for the
    // list URL rather than stacked — otherwise browser back from the
    // restored list would return to this detail page in a loop.
    this.$router.replace(this.listReturnPath);
  }

  get showTryNewPoBanner(): boolean {
    return getFeatureVersion('purchaseOrder') === 'classic';
  }

  private switchPoToAtlas(): void {
    setFeatureVersion('purchaseOrder', 'atlas');
    this.$router.push(purchaseOrderSwitchTarget('purchaseOrderDetail', this.$route.params.id));
  }

  private purchaseOrder: PurchaseOrderModel | null = null;
  private supplier: SupplierModel | null = null;
  private location: StoreLocationModel | null = null;
  private locations: StoreLocationModel[] = [];
  private items: IPurchaseOrderItem[] = [];
  // US-051 Tier 0 follow-up: bound page state for the items v-data-table.
  // `onProductsAdded()` resets this to 1 so newly prepended rows are
  // visible regardless of where the user was paging. Vuetify's internal
  // page state would otherwise be sticky and hide the new products
  // behind the previously-visible page. See MR !2650 CodeRabbit review
  // discussion b9c813a7 for the full context.
  private itemsTablePage = 1;
  private editedPurchaseOrder: Partial<IPurchaseOrder> = {};
  private isEditing = false;
  private loading = false;
  private purchaseOrderNotFound = false;
  private errorMessage = '';
  private hasError = false;
  private saving = false;
  private duplicating = false;
  private showDuplicateDialog = false;
  private deletingItemId: number | null = null;
  private dateMenu = false;
  private departureDateMenu = false;
  private editedDepartureDate = '';
  private internalNotesEditValue = '';
  private showFinancialEditDialog = false;
  // US-092 — merchant-wide, loaded when the receive dialog opens.
  private updateSupplierCost = true;
  // False when the preference read failed. The receive payload then omits the
  // flag so the SERVER resolves it from stored state — asserting a value we
  // could not read would re-enable the write-back for a merchant who opted out.
  private updateSupplierCostLoaded = true;
  private supplierCostMap: Map<number, number | null> = new Map();
  // Guards against a late response from a previous PO overwriting this one's
  // context, mirroring receiveColumnConfigOpVersion / currentSupplierSkuFetchToken.
  private supplierCostContextFetchToken = 0;
  private suppliers: SupplierModel[] = [];
  private loadingSuppliers = false;
  // US-066 Relink-to-Shopify-product dialog state
  private relinkDialogOpen = false;
  private relinkDialogItem: any = null;
  private relinkDialogReceivedItem: any = null;
  // Maps purchase_received_items.id -> true for rows that the merchant just
  // relinked but hasn't retried yet. Drives the green "Just relinked" pill on
  // the failed row and hides the Relink button so a second click can't
  // accidentally re-relink before Retry Sync runs.
  private recentlyRelinked: Record<number, true> = {};

  // US-094: PO lines the last Receive attempt could not point at a Shopify
  // inventory item. Non-empty only when the receive resolved NOTHING and was
  // therefore rejected before it committed, so these lines are the whole
  // reason the merchant is blocked — the receive dialog stays open and marks
  // them rather than closing on an error the merchant never sees.
  private receiveBlockedLines: IUnresolvableReceiveLine[] = [];

  // Bill To / Ship To editing
  private showAddressDialog = false;
  private editingAddressType: 'bill_to' | 'ship_to' = 'bill_to';
  private editedAddress: Record<string, string> = {};
  private savingAddress = false;
  private resolvedBillTo: Record<string, string> | null = null;
  private resolvedShipTo: Record<string, string> | null = null;
  private billToSource: 'database' | 'default' = 'default';
  private shipToSource: 'database' | 'default' = 'default';
  private showTransactionHistory = false;
  private showActivityHistory = false;
  private addressSuggestions: Record<string, string>[] = [];
  private loadingAddressSuggestions = false;
  private showResetAddressDialog = false;
  private resetAddressType: 'bill_to' | 'ship_to' = 'bill_to';
  private resettingAddress = false;
  private taxTypeList: TaxTypeList = new TaxTypeList();
  private bulkTaxTypeId: number | null = null;
  private showBulkTaxMenu = false;

  // Phase 1: PO Line Items Column Customization
  private columnConfig: { columns: {[columnId: string]: {enabled: boolean, order: number}} } | null = null;
  private masterData: {[variantId: string]: any} = {};
  private locationStock: LocationStockByVariant = {};
  private locationStockFetchToken = 0;
  private loadingConfig = false;
  private loadingMasterData = false;

  // US-069: Per-template column configuration. The in-app view is switchable;
  // `selectedTemplateId` drives the on-screen table (defaults to master, resets
  // to master on reload). Export/send each have their own selection initialized
  // from this when their dialog opens.
  private columnTemplates: ColumnTemplate[] = [];
  private selectedTemplateId: string = DEFAULT_TEMPLATE_ID;
  private loadingTemplates = false;
  private applyingColumnTemplate = false;
  private exportTemplateId: string = DEFAULT_TEMPLATE_ID;
  private sendTemplateId: string = DEFAULT_TEMPLATE_ID;

  // Phase 2: Custom Columns
  private customColumnDefinitions: {[columnId: string]: {label: string, enabled: boolean, order: number}} = {};
  private customColumnValues: {[variantId: string]: {[columnId: string]: string}} = {};
  private dirtyCustomColumnVariants: Set<string> = new Set();
  private loadingCustomColumns = false;

  // US-086: merchant-wide per-column visibility for the Receive Items dialog,
  // loaded from and saved to /purchase_orders/config/receive-columns. Replaces
  // the US-067 localStorage boolean toggle. Custom columns are hidden until the
  // merchant selects them in the Manage Columns picker.
  private visibleReceiveColumns: string[] = DEFAULT_VISIBLE_RECEIVE_COLUMNS;
  // Tracks the latest receive-columns operation (the dialog-open fetch, or
  // an onApplyReceiveColumns save) — same guard shape as
  // PurchaseOrderList.vue's columnConfigOpVersion.
  private receiveColumnConfigOpVersion = 0;
  private lastConfirmedReceiveColumns: string[] = DEFAULT_VISIBLE_RECEIVE_COLUMNS;
  private receiveColumnConfigSaveQueue: Promise<void> = Promise.resolve();

  // Phase 3: Metafield Columns
  // metafieldColumns is keyed by the backend's ownerResource-aware config key:
  //   - "<namespace>.<name>"          (product-scoped, legacy)
  //   - "variant:<namespace>.<name>"  (variant-scoped)
  private metafieldColumns: {[key: string]: {enabled: boolean, order: number, label: string, ownerResource?: MetafieldOwnerResource}} = {};
  // metafieldValues is now split per resource so product- and variant-scoped
  // entries with the same namespace.name don't collide.
  //   product:  productId  -> { "<namespace>.<name>": value }
  //   variant:  variantId  -> { "<namespace>.<name>": value }
  private metafieldValues: {product: {[productId: string]: {[key: string]: string}}, variant: {[variantId: string]: {[key: string]: string}}} = { product: {}, variant: {} };
  private loadingMetafieldValues = false;

  // Email dialog data
  private showEmailDialog = false;
  private emailSubject = '';
  private emailMessage = '';
  private emailRecipients: string[] = [];
  private ccRecipients: string[] = [];
  private attachmentFormat: 'pdf' | 'csv' = 'pdf';
  private includePricing = true;
  private groupByProduct = false;
  private includeSimplifiedPdf = true;
  private replyToEmails: string[] = [];
  private companyName = '';
  private savedEmails: string[] = [];
  // PO email template (single template per merchant) + per-send user variables
  private poEmailTemplate: POEmailTemplateType = { subject: '', body: '' };
  private userVariables: Record<string, string> = {};
  private savedCompanyNames: string[] = [];
  private sending = false;
  private isEmailFormValid = false;

  // Cancel PO dialog
  private showCancelDialog = false;
  private canceling = false;

  // Revert to Draft dialog
  private showRevertToDraftDialog = false;
  private revertingToDraft = false;

  // Re-open dialog (US-088)
  private showReopenDialog = false;
  private reopening = false;

  // PO Lifecycle Guide dialog
  private showLifecycleGuide = false;

  // Edit mode dialogs
  private showSaveChangesDialog = false;
  private showDiscardChangesDialog = false;

  // Export dialog data
  private showExportDialog = false;
  private exportFormat: 'csv' | 'xlsx' | 'pdf' = 'csv';
  private exporting = false;
  private exportIncludePricing = true;
  private exportGroupByProduct = false;

  // Import items dialog
  private showImportItemsDialog = false;

  // Add products dialog
  private showAddProductsDialog = false;

  // US-090: AddProductsDialog excludes by variant id rather than by PO line item, so
  // both pages can feed it whatever shape they hold their lines in.
  get existingItemVariantIds(): number[] {
    return (this.items || []).map((item: any) => Number(item.variantId));
  }

  // Mark as Completed dialog
  private showMarkAsCompletedDialog = false;
  private markingAsCompleted = false;

  // Audit History (status changes, email events)
  private auditHistory: IPurchaseOrderHistoryItem[] = [];
  private loadingAuditHistory = false;

  // Receive dialog data
  private showReceiveDialog = false;
  // US-093 — finalise costs after receiving.
  private showFinaliseCostsDialog = false;
  // US-068: barcode scan mode for the Receive Products dialog.
  // When enabled, pressing Enter in the search input triggers an exact
  // case-insensitive lookup against product_barcode (via MasterData), sku,
  // supplierSku, and currentSupplierSkuByVariantId. Visual feedback only —
  // audio is deferred (US-068 Out of Scope).
  private scanModeEnabled = false;
  private scanErrorMessage = '';
  private lastScannedItemId: number | null = null;
  // Handle for the row-flash auto-clear timer. Tracked so we can cancel
  // it on dialog close and before scheduling a new flash, preventing
  // timer accumulation across rapid successive scans.
  private scanFlashTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly SCAN_MODE_STORAGE_KEY = 'po-receive-scan-mode';
  private markAsCompleteAfterReceive = false;
  private receiving = false;
  // One idempotency key per receive intent: minted when the user confirms,
  // reused across error retries of the same intent (so the backend can
  // dedupe a re-submission after a lost response), reset only on success.
  private receiveIdempotencyKey = '';
  private receiveQuantities: { [key: number]: number | string } = {};
  // AS-11685 — live stock. liveStockLastKey is the request key of the last
  // finished read; a different current key renders as loading.
  private liveStockLevels: Record<number, LiveLevels> = {};
  private liveStockStoredStatus: LiveStockStatus = 'idle';
  private liveStockLastKey: string | null = null;
  private liveStockFetchedAt: number | null = null;
  private liveStockOpVersion = 0;
  private liveStockNow = Date.now();
  private liveStockClock: ReturnType<typeof setInterval> | null = null;
  private receiveNotes = '';
  private receiveds: PurchaseReceived[] = [];
  private loadingHistory = false;
  private deliveryNoteNumber = '';
  private showReceiveDetailDialog = false;
  private selectedReceive: IPurchaseReceived | null = null;
  private showReceiveReviewDialog = false;
  private updateShopifyInventory = true;
  private shopifyCostUpdateMethod = 'none';
  private receiveCosts: { [key: number]: number } = {};
  private receiveSearchQuery = '';
  // variantId -> current supplier SKU (from SupplierProduct), refreshed on each
  // Receive dialog open. Lets search match items against the CURRENT supplier
  // SKU even when the stored item.supplierSku snapshot is stale (e.g. the
  // merchant updated the supplier SKU after the PO was created).
  private currentSupplierSkuByVariantId: Record<number, string> = {};
  // Monotonic token to discard stale in-flight SKU-map responses after the
  // dialog is closed+reopened for the same supplier.
  private currentSupplierSkuFetchToken = 0;

  // Add mode switcher state
  private receiveMode: 'manual' | 'import' = 'manual';
  
  // Scroll state for settings indicator
  private showSettingsIndicator = false;

  // Import-related data properties
  private importFile: File | null = null;
  private importingReceive = false;
  private downloadingTemplate = false;
  private downloadingPoLabels = false;
  private downloadingReceiveLabels: { [key: string]: boolean } = {};

  // Add these properties
  private confirming = false;

  // Import summary data
  private importSummaryData: {
    totalRowsInCsv: number;
    successfullyProcessedRows: number;
    failedRows: number;
    errorDetails: any[];
    receiveRecordId: number | null;
    inventoryUpdateStatus: string;
    costUpdateStatus: string;
    costCalculationMethod: string | null;
    itemsWithCosts: number;
  } = {
    totalRowsInCsv: 0,
    successfullyProcessedRows: 0,
    failedRows: 0,
    errorDetails: [],
    receiveRecordId: null,
    inventoryUpdateStatus: 'skipped',
    costUpdateStatus: 'skipped',
    costCalculationMethod: null,
    itemsWithCosts: 0
  };
  private showImportSummaryDialog = false;
  private retryingSyncStatus = false;
  private retryingCostSyncStatus = false;
  private retryingInventorySync = false;
  // AS-11687: what the last receive-dialog Retry Sync skipped. `terminalRetry`
  // is set only by a 409 "terminal" answer (nothing was sent) and gates Retry anyway.
  private terminalRetry: TerminalRetry | null = null;
  private retrySkippedLines: SkippedRetryLine[] = [];
  private confirmingRetryAnyway = false;
  private retryingCostSync = false;
  private retryingHistoryInventory: number | null = null;
  private retryingHistoryCost: number | null = null;

  // Return dialog data
  private showReturnDialog = false;
  private showReturnReviewDialog = false;
  private returnQuantities: { [key: number]: number } = {};
  private returnQuantityErrors: { [key: number]: string } = {};
  private returnReason = '';
  private returnNotes = '';
  private submittingReturn = false;
  private returnSearchQuery = '';

  // Return history data
  private returnHistory: any[] = [];
  private loadingReturnHistory = false;
  private selectedReturn: any = null;
  private showReturnDetailDialog = false;
  private showDeleteReturnDialog = false;
  private returnToDelete: any = null;

  private showAdjustDialog = false;
  private showApplyTaxDialog = false;
  private adjustmentHistory: any[] = [];
  private loadingAdjustmentHistory = false;
  private selectedAdjustment: any = null;
  private showAdjustmentDetailDialog = false;
  private retryingReturnSync: number | null = null;
  private deletingReturn = false;

  // File error tracking
  private fileError: string = '';

  // Cost tracking data
  private showCostSummaryDialog = false;
  private costSummary: any = {};
  private costDetails: any[] = [];
  private shopifyUpdateResult: any = {};

  private shopifyCostUpdateOptions = [
    { text: 'Do not update', value: 'none' },
    { text: 'Latest cost', value: 'latest' },
    { text: 'Weighted average', value: 'average' }
  ];

  // Variant order quantity constraints
  private variantOrderConstraints: { [variantId: number]: { min: number; max: number } } = {};
  private loadingConstraints = false;
  private quantityValidationErrors: { [itemId: number]: string } = {};

  // Validation Rules
  private emailSubjectRules = [(v: string) => !!v || 'Subject is required'];

  private replyToRules = [
    (v: string[]) => !v || v.length === 0 || v.every((email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) || 'Invalid email format'
  ];

  private get emailRecipientsRules() {
    return toRecipientsRules(this.ccRecipients, (email) => this.validateEmail(email));
  }

  private get ccRecipientsRules() {
    return ccRecipientsRules(this.emailRecipients, (email) => this.validateEmail(email));
  }

  // The To + CC cap spans both fields, and Vuetify does not re-run a field's
  // validation when only its rules change: it compares them with deepEqual,
  // which treats any two functions as equal. Without this, a cap error on one
  // field outlives the fix made in the other and leaves Send disabled.
  @Watch('emailRecipients')
  @Watch('ccRecipients')
  private revalidateRecipientFields() {
    this.$nextTick(() => {
      ['emailRecipientsInput', 'ccRecipientsInput'].forEach((ref) => {
        const input = this.$refs[ref] as (Vue & { validate(): boolean }) | undefined;
        if (input) input.validate();
      });
    });
  }

  private get emailMessageRules() {
    return [(v: string) => !!v || 'Message is required'];
  }

  private get attachmentFormatRules() {
    return [(v: string) => !!v || 'A format must be selected'];
  }

  // Computed property for localStorage key (store-specific)
  private get localStorageKey(): string {
    return `po_email_preferences_${this.store.id}`;
  }

  private get savedEmailsKey(): string {
    return `po_saved_emails_${this.store.id}`;
  }

  private get savedCompanyNamesKey(): string {
    return `po_saved_company_names_${this.store.id}`;
  }

  // Computed property for formatted date in edit mode
  get formattedExpectedDeliveryDateForEdit(): string {
    return this.formatDate(this.editedPurchaseOrder.expectedDeliveryDate);
  }

  // Computed property for formatted departure date in edit mode
  get formattedDepartureDateForEdit(): string {
    return this.editedDepartureDate ? this.formatDate(this.editedDepartureDate) : '';
  }

  // Computed property for loading column data (MasterData, Custom, Metafield)
  get loadingColumnData(): boolean {
    return this.loadingMasterData || this.loadingCustomColumns || this.loadingMetafieldValues;
  }

  // Helper to format note value - returns dash for empty/invalid values
  formatNote(note: any): string {
    if (note === null || note === undefined || note === '' || note === 0 || note === '0') {
      return '—';
    }
    const noteStr = String(note).trim();
    return noteStr === '' || noteStr === '0' ? '—' : noteStr;
  }

  // Helper to check if note has a valid value
  hasValidNote(note: any): boolean {
    if (note === null || note === undefined || note === '' || note === 0 || note === '0') {
      return false;
    }
    const noteStr = String(note).trim();
    return noteStr !== '' && noteStr !== '0';
  }

  // Helper to check if a column is enabled in config
  // For PO status columns (received_quantity, returned_quantity, units_left), default to true for backward compatibility
  // For 'note' column, default to false (disabled by default)
  isColumnEnabled(columnId: string): boolean {
    const config = this.columnConfig?.columns?.[columnId];
    if (config === undefined) {
      // These columns should be disabled by default when not in saved config
      if (columnId === 'landed_cost_shipping_per_unit') return false;
      if (columnId === 'landed_cost_adjustment_per_unit') return false;
      if (columnId === 'margin_amount') return false;
      if (columnId === 'margin_percent') return false;
      if (columnId === 'landed_margin_amount') return false;
      if (columnId === 'landed_margin_percent') return false;
      // Other columns default to true for backward compatibility
      return true;
    }
    return config.enabled;
  }

  private get headers() {
    // Helper to get configured order for a column, with fallback
    const getOrder = (columnId: string, defaultOrder: number): number => {
      return this.columnConfig?.columns?.[columnId]?.order ?? defaultOrder;
    };

    const quantityHeader = this.purchaseOrder?.status === 'draft'
      ? { text: 'Quantity', value: 'quantityStatus', sortable: false, align: 'center', width: '120px', order: getOrder('req_quantity', 4) }
      : { text: 'Quantity Status', value: 'quantityStatus', sortable: false, align: 'center', width: '180px', order: getOrder('req_quantity', 4) };

    // Dynamic currency suffix for monetary columns
    const currSuffix = this.isForeignCurrencyPO ? ` (${this.purchaseOrder!.currencyCode})` : '';

    const baseHeaders: any[] = [
      ...(this.isColumnEnabled('req_image') ? [{ text: '', value: 'variant_image', sortable: false, align: 'center', width: '70px', order: getOrder('req_image', 1) }] : []),
      { text: 'Product Details', value: 'productDetails', sortable: false, align: 'start', width: '250px', order: getOrder('req_product_details', 2) },
      quantityHeader,
      { text: `Unit Price${currSuffix}`, value: 'unitPrice', sortable: false, align: 'end', width: '140px', order: getOrder('req_unit_price', 5) },
      { text: `Total${currSuffix}`, value: 'totalPrice', sortable: false, align: 'end', width: '130px', order: getOrder('req_total', 6) }
    ];

    // Add Note column if enabled (default to true for backward compatibility)
    if (this.isColumnEnabled('note')) {
      baseHeaders.push({ text: 'Note', value: 'note', sortable: false, align: 'start', width: '200px', order: getOrder('note', 3) });
    }

    // Tax columns (gated by column config)
    if (this.isColumnEnabled('tax_type')) {
      baseHeaders.push({ text: 'Tax Type', value: 'taxType', sortable: false, align: 'start', width: '140px', order: getOrder('tax_type', 7) });
    }
    if (this.isColumnEnabled('tax_rate')) {
      baseHeaders.push({ text: 'Tax %', value: 'taxRate', sortable: false, align: 'end', width: '100px', order: getOrder('tax_rate', 8) });
    }
    if (this.isColumnEnabled('tax_amount')) {
      baseHeaders.push({ text: `Tax Amount${currSuffix}`, value: 'taxAmount', sortable: false, align: 'end', width: '120px', order: getOrder('tax_amount', 9) });
    }
    if (this.isColumnEnabled('landed_cost')) {
      const landedCostLabel = this.isForeignCurrencyPO ? `Landed Cost (${this.storeCurrency})` : 'Landed Cost';
      baseHeaders.push({ text: landedCostLabel, value: 'landedCost', sortable: false, align: 'end', width: '120px', order: getOrder('landed_cost', 10) });
    }
    if (this.isColumnEnabled('landed_cost_shipping_per_unit')) {
      const shippingLabel = this.isForeignCurrencyPO ? `Shipping / Unit (${this.storeCurrency})` : 'Shipping / Unit';
      baseHeaders.push({ text: shippingLabel, value: 'landedCostShippingPerUnit', sortable: false, align: 'end', width: '120px', order: getOrder('landed_cost_shipping_per_unit', 11) });
    }
    if (this.isColumnEnabled('landed_cost_adjustment_per_unit')) {
      const adjLabel = this.isForeignCurrencyPO ? `Adjustment / Unit (${this.storeCurrency})` : 'Adjustment / Unit';
      baseHeaders.push({ text: adjLabel, value: 'landedCostAdjustmentPerUnit', sortable: false, align: 'end', width: '130px', order: getOrder('landed_cost_adjustment_per_unit', 12) });
    }
    if (this.isColumnEnabled('margin_amount')) {
      baseHeaders.push({ text: 'Margin', value: 'marginAmount', sortable: false, align: 'end', width: '120px', order: getOrder('margin_amount', 13) });
    }
    if (this.isColumnEnabled('margin_percent')) {
      baseHeaders.push({ text: 'Margin %', value: 'marginPercent', sortable: false, align: 'end', width: '100px', order: getOrder('margin_percent', 14) });
    }
    if (this.isColumnEnabled('landed_margin_amount')) {
      baseHeaders.push({ text: 'Landed Margin', value: 'landedMarginAmount', sortable: false, align: 'end', width: '120px', order: getOrder('landed_margin_amount', 15) });
    }
    if (this.isColumnEnabled('landed_margin_percent')) {
      baseHeaders.push({ text: 'Landed Margin %', value: 'landedMarginPercent', sortable: false, align: 'end', width: '120px', order: getOrder('landed_margin_percent', 16) });
    }

    const optionalHeaders: any[] = [];

    // Phase 1: Add optional columns from config (MasterData columns)
    // Note: PO item columns (received_quantity, returned_quantity, units_left) are displayed inside Quantity Status, not as separate columns
    if (this.columnConfig && this.columnConfig.columns) {
      Object.entries(this.columnConfig.columns).forEach(([columnId, config]) => {
        // Skip PO item columns - they are displayed inside the Quantity Status column, not as separate columns
        if (PO_ITEM_COLUMNS.includes(columnId)) return;
        // Export-only columns (product_sku) never render on-screen — guard explicitly
        // so they stay hidden even if COLUMN_METADATA later gains an entry for them
        // (mirrors the EXPORT_ONLY_COLUMNS exclusion in enabledOptionalColumns).
        if (this.EXPORT_ONLY_COLUMNS.includes(columnId)) return;
        if (config.enabled) {
          const metadata = COLUMN_METADATA[columnId];
          if (metadata) {
            optionalHeaders.push({
              text: columnId === LOCATION_STOCK_COLUMN ? locationStockHeaderText(this.locationStockLocationName) : metadata.label,
              value: columnId,
              sortable: false,
              align: NUMERIC_COLUMNS.includes(columnId) ? 'end' : 'start',
              order: config.order,
              type: 'masterdata'
            });
          }
        }
      });
    }

    // Phase 2: Add custom columns (order 40+)
    if (this.customColumnDefinitions) {
      Object.entries(this.customColumnDefinitions).forEach(([columnId, def]) => {
        if (def.enabled) {
          optionalHeaders.push({
            text: def.label,
            value: `custom_${columnId}`,
            sortable: false,
            align: 'start',
            order: def.order,
            type: 'custom',
            columnId: columnId
          });
        }
      });
    }

    // Phase 3: Add metafield columns
    if (this.metafieldColumns) {
      Object.entries(this.metafieldColumns).forEach(([key, config]) => {
        if (config.enabled) {
          optionalHeaders.push({
            text: config.label,
            value: `metafield_${key}`,
            sortable: false,
            align: 'start',
            order: config.order,
            type: 'metafield',
            metafieldKey: key
          });
        }
      });
    }

    // Combine and sort by order
    const allHeaders = [...baseHeaders, ...optionalHeaders];
    allHeaders.sort((a, b) => a.order - b.order);

    if (this.isEditing) {
      allHeaders.push({ text: '', value: 'actions', sortable: false, align: 'center', width: '50px', order: 999 });
    }

    return allHeaders;
  }

  // Export-only columns: rendered as their own column only in the exported file,
  // never on the on-screen table (product_sku is already visible on-screen inside
  // Product Details, so a dedicated on-screen column would be redundant). Shared by
  // enabledOptionalColumns (which excludes them) and the export-count getters
  // (which must still count them, since they DO appear in the export).
  private readonly EXPORT_ONLY_COLUMNS = ['product_sku'];

  // MasterData columns that render via explicit template slots/headers (their own
  // gated headers and color-coded slots), not the generic optional-column loop.
  private readonly EXPLICIT_SLOT_COLUMNS = ['note', 'margin_amount', 'margin_percent', 'landed_margin_amount', 'landed_margin_percent'];

  private readonly PRICE_RELATED_COLUMNS = new Set([
    'unit_cost', 'selling_price', 'retail_value', 'actual_inventory_cost',
    'landed_cost', 'landed_cost_shipping_per_unit', 'landed_cost_adjustment_per_unit',
    'margin_amount', 'margin_percent', 'landed_margin_amount', 'landed_margin_percent'
  ]);

  // The MasterData column config the export will ACTUALLY use: the template picked
  // in the export dialog (mirrors exportPurchaseOrder / enabledColumnIds), falling
  // back to the on-screen master config when no template resolves. The PDF
  // width-warning must count from this, not the on-screen columnConfig — the two
  // diverge when the dialog's exportTemplateId differs from the on-screen config.
  private get resolvedExportColumns(): { [id: string]: { enabled: boolean; order: number } } {
    const exportTemplate = resolveTemplate(this.columnTemplates, this.exportTemplateId);
    if (exportTemplate) return exportTemplate.columns as any;
    return (this.columnConfig && this.columnConfig.columns) || {};
  }

  // Number of enabled export-only columns in the resolved export config
  // (none are price-related, so they always count).
  private get enabledExportOnlyColumnCount(): number {
    return Object.entries(this.resolvedExportColumns)
      .filter(([columnId, config]) => config.enabled && this.EXPORT_ONLY_COLUMNS.includes(columnId))
      .length;
  }

  // Total column count for export (required + optional + export-only + price columns based on toggle)
  // Base required columns: Image, Product, SKU, Quantity = 4
  // +2 for Unit Price and Total if exportIncludePricing is true
  private get totalExportColumnCount(): number {
    const baseRequiredColumns = 4;
    // MasterData optional columns from the resolved export template (mirrors what
    // the export actually sends), excluding required, explicit-slot, and export-only
    // ids, and price-related ids the backend strips when pricing is off.
    const masterdataOptionalCount = Object.entries(this.resolvedExportColumns)
      .filter(([id, config]) => config.enabled && !id.startsWith('req_')
        && !this.EXPLICIT_SLOT_COLUMNS.includes(id) && !this.EXPORT_ONLY_COLUMNS.includes(id)
        && (this.exportIncludePricing || !this.PRICE_RELATED_COLUMNS.has(id)))
      .length;
    // Custom and metafield columns are not scoped by the export-template selector,
    // so count the on-screen enabled ones (unchanged behavior).
    const customAndMetafieldCount = this.enabledOptionalColumns
      .filter((col: any) => (col.type === 'custom' || col.type === 'metafield')
        && (this.exportIncludePricing || !this.PRICE_RELATED_COLUMNS.has(col.id)))
      .length;
    let count = baseRequiredColumns + masterdataOptionalCount
      + customAndMetafieldCount + this.enabledExportOnlyColumnCount;
    if (this.exportIncludePricing) count += 2; // Unit Price + Total
    return count;
  }

  // Check if PDF export will be limited
  private get pdfColumnLimitWarning(): boolean {
    return this.totalExportColumnCount > 12;
  }

  // Must match PDF_IMAGE_ITEM_LIMIT in purchase_order_export.js
  private readonly PDF_IMAGE_ITEM_LIMIT = 20;

  private get pdfImageAutoDisabled(): boolean {
    return (this.purchaseOrder?.items?.length || 0) > this.PDF_IMAGE_ITEM_LIMIT;
  }

  // Phase 1, 2 & 3: Get list of enabled optional columns (MasterData + Custom + Metafield)
  private get enabledOptionalColumns() {
    const columns: any[] = [];

    // Phase 1: MasterData columns (skip required columns - they're counted separately)
    if (this.columnConfig && this.columnConfig.columns) {
      // Columns with explicit template slots/headers (see EXPLICIT_SLOT_COLUMNS) and
      // export-only columns (see EXPORT_ONLY_COLUMNS) are excluded from the on-screen
      // optional list; the latter are still counted in totalExportColumnCount.
      Object.entries(this.columnConfig.columns)
        .filter(([columnId, config]) => config.enabled && !columnId.startsWith('req_')
          && !this.EXPLICIT_SLOT_COLUMNS.includes(columnId) && !this.EXPORT_ONLY_COLUMNS.includes(columnId))
        .forEach(([columnId, config]) => {
          columns.push({
            id: columnId,
            type: 'masterdata',
            order: config.order
          });
        });
    }

    // Phase 2: Custom columns
    if (this.customColumnDefinitions) {
      Object.entries(this.customColumnDefinitions)
        .filter(([_, def]) => def.enabled)
        .forEach(([columnId, def]) => {
          columns.push({
            id: `custom_${columnId}`,
            columnId: columnId,
            type: 'custom',
            order: def.order
          });
        });
    }

    // Phase 3: Metafield columns
    if (this.metafieldColumns) {
      Object.entries(this.metafieldColumns)
        .filter(([_, config]) => config.enabled)
        .forEach(([key, config]) => {
          columns.push({
            id: `metafield_${key}`,
            metafieldKey: key,
            type: 'metafield',
            label: config.label,
            order: config.order
          });
        });
    }

    return columns.sort((a, b) => a.order - b.order);
  }

  // True when any enabled custom column exists in the current column config.
  // Used to show the per-variant-scope warning banner while editing.
  private get hasEnabledCustomColumns(): boolean {
    return this.enabledOptionalColumns.some((c: any) => c.type === 'custom');
  }

  // Phase 1: Get MasterData value for a variant and column
  private getMasterDataValue(variantId: number | string, columnId: string, item?: any): string {
    // PO item columns (received_quantity, returned_quantity, units_left, note, landed_cost) come from the item, not MasterData
    if (PO_ITEM_COLUMNS.includes(columnId) && item) {
      // Special handling for calculated column: units_left = orderedQuantity - receivedQuantity
      if (columnId === 'units_left') {
        const ordered = Number(item.orderedQuantity || 0);
        const received = Number(item.receivedQuantity || 0);
        return String(ordered - received);
      }
      // Note column should return empty string for null/undefined, not '0'
      if (columnId === 'note') {
        const value = item.note;
        return value !== undefined && value !== null && value !== '' && value !== 0 && value !== '0'
          ? String(value)
          : '';
      }
      // Landed cost comes from item.extra.landed_cost
      if (columnId === 'landed_cost') {
        return item?.extra?.landed_cost != null ? this.formatCurrency(item.extra.landed_cost) : '';
      }
      if (columnId === 'landed_cost_shipping_per_unit') {
        const c = item?.extra?.landed_cost_components;
        if (!c || c.method !== 'proportional') return '';
        return c.shipping_per_unit != null ? this.formatCurrency(c.shipping_per_unit) : '';
      }
      if (columnId === 'landed_cost_adjustment_per_unit') {
        const c = item?.extra?.landed_cost_components;
        if (!c) return '';
        if (c.method === 'proportional') return c.adjustment_per_unit != null ? this.formatCurrency(c.adjustment_per_unit) : '';
        return '';
      }
      // Margin columns: computed from selling price (MasterData unit_price) and PO unitPrice / landed_cost
      // Use resolveSellingPrice() to handle comma-formatted strings from OLAP (e.g., "2,630.00")
      if (columnId === 'margin_amount' || columnId === 'margin_percent') {
        const variant = this.masterData[String(variantId)];
        const sellingPrice = this.resolveSellingPrice(variant);
        const cost = Number(item.unitPrice || 0);
        if (!sellingPrice) return '';
        if (columnId === 'margin_amount') return this.formatCurrency(sellingPrice - cost);
        const pct = ((sellingPrice - cost) / sellingPrice) * 100;
        return pct.toFixed(1) + '%';
      }
      if (columnId === 'landed_margin_amount' || columnId === 'landed_margin_percent') {
        const variant = this.masterData[String(variantId)];
        const sellingPrice = this.resolveSellingPrice(variant);
        const landedCost = item?.extra?.landed_cost;
        if (!sellingPrice || landedCost == null) return '';
        const cost = Number(landedCost);
        if (columnId === 'landed_margin_amount') return this.formatCurrency(sellingPrice - cost);
        const pct = ((sellingPrice - cost) / sellingPrice) * 100;
        return pct.toFixed(1) + '%';
      }
      const field = COLUMN_API_CODES[columnId];
      const value = item[field];
      // For numeric fields (received_quantity, returned_quantity), '0' is a valid value
      return value !== undefined && value !== null ? String(value) : '0';
    }

    // Convert to string for consistent lookup (masterData is indexed by string keys)
    const variantIdStr = String(variantId);
    const variant = this.masterData[variantIdStr];

    if (!variant) {
      return '';
    }

    // Look up by column ID directly (data is stored by column ID in fetchMasterData)
    // Fall back to API code lookup for backwards compatibility
    let value = variant[columnId];
    if (value === undefined) {
      const apiCode = COLUMN_API_CODES[columnId];
      if (apiCode) {
        value = variant[apiCode];
      }
    }

    // Return value or empty string (not undefined)
    return value !== undefined && value !== null ? String(value) : '';
  }

  // Check if a column should be right-aligned (numeric columns)
  private isNumericColumn(columnId: string): boolean {
    return NUMERIC_COLUMNS.includes(columnId);
  }

  // Get color class for margin percentage: green (>=30%), yellow (15-29%), red (<15%)
  private getMarginColorClass(variantId: number | string, cost: number | null | undefined, isLanded = false): string {
    const variant = this.masterData[String(variantId)];
    const sellingPrice = this.resolveSellingPrice(variant);
    if (!sellingPrice || cost == null) return '';
    const pct = ((sellingPrice - Number(cost)) / sellingPrice) * 100;
    if (pct >= 30) return 'success--text';
    if (pct >= 15) return 'warning--text';
    return 'error--text';
  }

  // Phase 2: Get custom column value for a variant
  private getCustomColumnValue(variantId: number | string, columnId: string): string {
    const variantIdStr = String(variantId);
    const values = this.customColumnValues[variantIdStr];
    if (!values) {
      return '';
    }

    const value = values[columnId];
    if (value === null || value === undefined || value === '') {
      return '';
    }

    return String(value);
  }

  // Set a custom column value (used in edit mode). Tracks the variant as dirty
  // for saveCustomColumnValues() to pick up on save.
  private setCustomColumnValue(variantId: number | string, columnId: string, value: string) {
    const variantIdStr = String(variantId);
    if (!this.customColumnValues[variantIdStr]) {
      this.$set(this.customColumnValues, variantIdStr, {});
    }
    const normalized = value == null ? '' : String(value);
    this.$set(this.customColumnValues[variantIdStr], columnId, normalized);
    this.dirtyCustomColumnVariants.add(variantIdStr);
  }

  // Phase 3: Get a metafield value for a column.
  // metafieldKey is the config-map key — "<ns>.<name>" for PRODUCT, "variant:<ns>.<name>" for PRODUCTVARIANT.
  // When the caller passes a variantId for a product-scoped column, the caller
  // SHOULD also pass fallbackProductId (typically item.productId from the row
  // context) — that avoids a linear scan of this.items from the render path
  // when masterData is missing the variant entry.
  private getMetafieldValue(
    productIdOrVariantId: number | string,
    metafieldKey: string,
    isVariantId = false,
    fallbackProductId: number | string | null = null,
  ): string {
    const { ownerResource, effectiveKey } = parseMetafieldConfigKey(metafieldKey);
    const isVariantScoped = ownerResource === 'PRODUCTVARIANT';

    let lookupId: string;
    let bucket: {[id: string]: {[key: string]: string}};

    if (isVariantScoped) {
      // Variant-scoped: index by variantId. If caller passed a productId, we
      // can't resolve a variant — return empty.
      if (!isVariantId) return '';
      lookupId = String(productIdOrVariantId);
      bucket = this.metafieldValues.variant;
    } else {
      // Product-scoped: index by productId. If caller passed a variantId,
      // resolve productId via masterData, with a fallback to the caller-
      // provided fallbackProductId. fetchMetafieldValues keys product-scoped
      // values by item.productId BEFORE consulting masterData, so without
      // this fallback the column renders blank for items whose variant is
      // missing from masterData even though the value was fetched.
      if (isVariantId) {
        const variantIdStr = String(productIdOrVariantId);
        const masterDataItem = this.masterData[variantIdStr];
        let resolvedProductId = masterDataItem?.product_id ? String(masterDataItem.product_id) : '';
        if (!resolvedProductId && fallbackProductId) {
          resolvedProductId = String(fallbackProductId);
        }
        if (!resolvedProductId) return '';
        lookupId = resolvedProductId;
      } else {
        lookupId = String(productIdOrVariantId);
      }
      bucket = this.metafieldValues.product;
    }

    const values = bucket[lookupId];
    if (!values) return '';

    const value = values[effectiveKey];
    if (value === null || value === undefined || value === '') {
      return '';
    }

    return String(value);
  }

  mounted() {
    // US-068: hydrate scan-mode preference per-browser. Silent on errors —
    // blocked storage just means the merchant has to re-enable scan mode.
    try {
      const raw = localStorage.getItem(this.SCAN_MODE_STORAGE_KEY);
      if (raw === 'true') this.scanModeEnabled = true;
    } catch { /* localStorage blocked / quota — ignore */ }
  }

  beforeDestroy() {
    // AS-11685: a read still in flight must not restart the clock on a destroyed component.
    ++this.liveStockOpVersion;
    this.stopLiveStockClock();
    // US-068: cancel any in-flight row-flash timer so it can't fire on a
    // destroyed component and pin the closure in memory.
    this.clearScanFlashTimer();
  }

  @Watch('id', { immediate: true })
  onIdChange() {
    // Reset transient label-download state so a spinner can't leak across POs
    this.downloadingPoLabels = false;
    this.downloadingReceiveLabels = {};
    // AS-11593: closing the Send PO dialog also clears its CC, so a CC list typed
    // for one PO can't go out with the next when the route is reused.
    this.showEmailDialog = false;
    // Reset duplicate dialog state when PO changes
    this.showDuplicateDialog = false;
    this.duplicating = false;
    // US-088: reset re-open dialog state so a confirmation opened on one
    // completed PO can't be applied to a different PO when the route is reused.
    this.showReopenDialog = false;
    this.reopening = false;
    this.isEditingRate = false;
    this.editedRate = null;
    this.isSavingRate = false;
    this.isRefreshingRate = false;
    // Reset inline metadata edit state
    this.isEditingNotes = false;
    this.isEditingInternalNotes = false;
    this.isEditingExpectedDelivery = false;
    this.isEditingDeparture = false;
    // Clear stale custom-column dirty set to prevent cross-PO writes
    this.dirtyCustomColumnVariants.clear();
    // Reset relink dialog + just-relinked markers so they cannot leak into a
    // different PO when the route is reused.
    this.receiveBlockedLines = [];
    this.relinkDialogOpen = false;
    this.relinkDialogItem = null;
    this.relinkDialogReceivedItem = null;
    this.recentlyRelinked = {};
    // US-068: reset scan-mode transient state so a stale row-flash, error
    // message, or in-flight flash timer from a previous PO can't leak into
    // the new one when the route is reused.
    this.clearScanFlashTimer();
    this.scanErrorMessage = '';
    this.lastScannedItemId = null;
    // Page back to 1 so navigating to a smaller PO doesn't land on an empty
    // page from the previous PO's pagination.
    this.itemsTablePage = 1;

    this.fetchData();
    this.checkImportFlag();
    this.loadEmailPreferences();
    this.loadSavedEmails();
    this.loadExportPreferences();
  }

  @Watch('$route', { immediate: true })
  onRouteChange() {
    this.checkImportFlag();
  }

  @Watch('showReceiveDialog')
  onReceiveDialogChange(val: boolean) {
    if (val) {
      // Reset quantities when dialog opens
      this.initializeReceiveData();
      // US-094: the blocked-line panel says "Your quantities have been kept",
      // which is only true within the session that hit the failure. Opening a
      // new session resets the quantities above, so a stale panel would be
      // telling the merchant their (now empty) entries were preserved.
      this.receiveBlockedLines = [];
      this.receiveNotes = '';
      this.receiveSearchQuery = '';
      this.scanErrorMessage = '';
      this.clearScanFlashTimer();
      this.lastScannedItemId = null;
      // Reset mode and import-related state
      this.receiveMode = 'manual';
      this.importFile = null;
      this.importingReceive = false;
      this.downloadingTemplate = false;
      this.markAsCompleteAfterReceive = false;  // Reset mark as complete checkbox
      const savedMethod = localStorage.getItem('po_receive_cost_method');
      if (savedMethod && this.shopifyCostUpdateOptions.map(o => o.value).includes(savedMethod)) {
        this.shopifyCostUpdateMethod = savedMethod;
      } else {
        this.shopifyCostUpdateMethod = 'none';
      }
      // US-086: fetch the merchant-wide receive-columns config each time the
      // dialog opens (mirrors PurchaseOrderList.vue's created()/onApplyColumns
      // op-versioning pattern).
      this.fetchReceiveColumnsForDialog();
      // US-092: merchant-wide toggle + the supplier costs this receive may
      // replace, both loaded on open so the dialog can show what will change.
      this.fetchSupplierCostContextForDialog();
      // Initialize scroll indicator state
      this.showSettingsIndicator = !this.$vuetify.breakpoint.lgAndUp;
      // Refresh supplier SKUs so search matches newly-updated mappings.
      this.fetchCurrentSupplierSkus();
    } else {
      // Reset scroll indicator when dialog closes
      this.showSettingsIndicator = false;
      this.currentSupplierSkuByVariantId = {};
      // Cross-PO state: without this the next PO's dialog briefly shows the
      // previous supplier's costs.
      this.supplierCostMap = new Map();
    }
  }

  // US-092. Both reads fail open: the preference helper resolves to ON, and an
  // empty cost map just hides the "Supplier now" line. Neither should block a
  // merchant from receiving stock.
  private async fetchSupplierCostContextForDialog() {
    const supplierId = this.purchaseOrder?.supplierId;
    const fetchToken = ++this.supplierCostContextFetchToken;

    // Independent reads — the supplier id comes from the already-loaded PO, not
    // from the preference, so awaiting them in series just doubled the latency
    // before the dialog can render its cost hints.
    const [preference, supplierCostMap] = await Promise.all([
      fetchUpdateSupplierCostPreference(),
      supplierId ? fetchSupplierCostMap(supplierId) : Promise.resolve(new Map()),
    ]);

    // Reopened on another PO while this was in flight — the late response would
    // show another supplier's "was" costs.
    if (this.supplierCostContextFetchToken !== fetchToken) { return; }

    this.updateSupplierCost = preference.value;
    this.updateSupplierCostLoaded = preference.loaded;
    this.supplierCostMap = supplierCostMap;
  }

  private async onUpdateSupplierCostChange(value: boolean) {
    // The merchant just asserted a value, so it is known regardless of whether
    // the earlier read succeeded.
    this.updateSupplierCostLoaded = true;
    try {
      await saveUpdateSupplierCostPreference(value);
    } catch {
      // Roll back so the switch never shows a preference the server rejected.
      this.updateSupplierCost = !value;
      // Single-object payload — App.vue's listener reads config.color and drops
      // a positional colour argument (see sendEmail / handleReopen).
      EventBus.$emit('show-snackbar', {
        message: 'Failed to save the supplier cost setting',
        color: 'error',
      });
    }
  }

  private async fetchReceiveColumnsForDialog() {
    const opId = ++this.receiveColumnConfigOpVersion;
    const visibleColumns = await fetchReceiveColumnsConfig();
    if (this.receiveColumnConfigOpVersion === opId) {
      this.visibleReceiveColumns = visibleColumns;
      this.lastConfirmedReceiveColumns = visibleColumns;
    }
  }

  public async onApplyReceiveColumns(next: string[]) {
    const opId = ++this.receiveColumnConfigOpVersion;
    this.visibleReceiveColumns = next;

    // Queue this save's actual request behind whatever is already pending,
    // so requests are sent (and therefore land at the server) in order.
    const thisSave = this.receiveColumnConfigSaveQueue
      .catch(() => { /* an earlier save's failure must not break the chain */ })
      .then(() => saveReceiveColumnsConfig(next));
    this.receiveColumnConfigSaveQueue = thisSave.catch(() => { /* see above */ });

    try {
      await thisSave;
      if (this.receiveColumnConfigOpVersion === opId) {
        this.lastConfirmedReceiveColumns = next;
      }
    } catch (error) {
      if (this.receiveColumnConfigOpVersion !== opId) {
        return;
      }
      this.visibleReceiveColumns = this.lastConfirmedReceiveColumns;
      EventBus.$emit('show-snackbar', {
        message: await extractApiErrorMessage(error, 'Failed to save column preferences'),
        color: 'error',
      });
    }
  }

  private toggleScanMode() {
    this.scanModeEnabled = !this.scanModeEnabled;
    try {
      localStorage.setItem(this.SCAN_MODE_STORAGE_KEY, String(this.scanModeEnabled));
    } catch { /* localStorage blocked / quota — ignore */ }
    this.scanErrorMessage = '';
    // US-068: if scan mode was just enabled and masterData hasn't been fetched
    // yet (e.g. the merchant has no optional columns enabled), lazy-fetch it so
    // barcode lookups have data to match against. Fire-and-forget — the first
    // scan or two may match only sku/supplierSku until this resolves, which is
    // acceptable; awaiting here would block the UI on toggle. Guard against an
    // in-flight fetch from fetchData() so we don't race on initial load.
    if (this.scanModeEnabled && !this.loadingMasterData && Object.keys(this.masterData).length === 0) {
      this.fetchMasterData();
    }
    // US-068: after toggling, return focus to the search input so the merchant
    // can scan/type immediately without an extra click.
    this.$nextTick(() => {
      const inputRef = this.$refs.receiveSearchInput as any;
      if (inputRef && typeof inputRef.focus === 'function') inputRef.focus();
    });
  }

  private onScanSubmit() {
    if (!this.scanModeEnabled) return;
    const raw = this.receiveSearchQuery.trim();
    if (!raw) return;
    const needle = raw.toLowerCase();

    const matches = (this.purchaseOrder?.items ?? []).filter(item => {
      const barcode = (this.masterData[String(item.variantId)]?.product_barcode || '').toString().toLowerCase();
      const sku = (item.sku || '').toLowerCase();
      const supplierSku = (item.supplierSku || '').toLowerCase();
      const currentSku = (this.currentSupplierSkuByVariantId[item.variantId] || '').toLowerCase();
      return barcode === needle || sku === needle || supplierSku === needle || currentSku === needle;
    });

    if (matches.length === 0) {
      // US-068: if MasterData is still loading and we have nothing cached yet,
      // a barcode-only scan would falsely report "no match" for an item that
      // actually exists — the data just hasn't arrived. Surface that state
      // instead so the merchant knows to retry rather than chasing a ghost.
      if (this.loadingMasterData && Object.keys(this.masterData).length === 0) {
        this.handleScanError('Loading barcode data — please scan again in a moment');
      } else {
        this.handleScanError(`No item on this PO matches "${raw}"`);
      }
    } else if (matches.length > 1) {
      this.handleScanError('Multiple items match — scan a more specific code');
    } else {
      this.handleScanHit(matches[0]);
    }
  }

  private handleScanHit(item: IPurchaseOrderItem) {
    if (!item.id) return;
    const alreadyReceived = item.receivedQuantity || 0;
    if (item.orderedQuantity <= alreadyReceived) {
      this.handleScanError(`"${item.productName}" is already fully received`);
      return;
    }
    const pending = Number(this.receiveQuantities[item.id]) || 0;
    // Vue 2 reactivity: receiveQuantities is a plain object, use $set
    this.$set(this.receiveQuantities, item.id, pending + 1);
    this.lastScannedItemId = item.id;
    const flashedId = item.id;
    // 50ms buffer over the 1.2s CSS animation so the class is removed
    // only AFTER the animation has finished, preventing visual stutter.
    this.clearScanFlashTimer();
    this.scanFlashTimer = setTimeout(() => {
      if (this.lastScannedItemId === flashedId) this.lastScannedItemId = null;
      this.scanFlashTimer = null;
    }, 1250);
    this.scanErrorMessage = '';
    this.receiveSearchQuery = '';
    this.$nextTick(() => this.scrollScannedRowIntoView(item.id!));
  }

  private handleScanError(message: string) {
    // Force the shake animation to re-trigger on consecutive errors by
    // briefly clearing the error before reapplying it — Vue's class
    // binding needs to see the truthy value transition or the CSS
    // animation won't restart.
    this.scanErrorMessage = '';
    this.$nextTick(() => {
      this.scanErrorMessage = message;
    });
    // receiveSearchQuery is intentionally preserved so the merchant can see what was scanned.
  }

  private scrollScannedRowIntoView(itemId: number) {
    try {
      const row = document.querySelector(`[data-scan-row-id="${itemId}"]`);
      if (row && typeof (row as HTMLElement).scrollIntoView === 'function') {
        (row as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch { /* DOM not ready / no-op in tests */ }
  }

  private scanRowClass(item: IPurchaseOrderItem): string {
    return this.lastScannedItemId === item.id ? 'po-scan-flash' : '';
  }

  private clearScanFlashTimer() {
    if (this.scanFlashTimer != null) {
      clearTimeout(this.scanFlashTimer);
      this.scanFlashTimer = null;
    }
  }

  private async fetchCurrentSupplierSkus(): Promise<void> {
    const supplierId = this.purchaseOrder?.supplierId;
    if (!supplierId) {
      this.currentSupplierSkuByVariantId = {};
      return;
    }
    // Capture a per-call token so a late response from a previous dialog
    // session can't overwrite data fetched by a newer session.
    const fetchToken = ++this.currentSupplierSkuFetchToken;
    try {
      // /sku-map is a tolerant-filter endpoint (supplierId + isDeleted=false)
      // so legacy rows with merchantId=null or status=INACTIVE still appear.
      const response: any = await get(`/supplier-products/supplier/${supplierId}/sku-map`);
      if (
        fetchToken !== this.currentSupplierSkuFetchToken ||
        !this.showReceiveDialog ||
        this.purchaseOrder?.supplierId !== supplierId
      ) {
        return;
      }
      const items: Array<{ variantId: number; supplierSku?: string | null }> = response?.data || [];
      const map: Record<number, string> = {};
      for (const it of items) {
        if (it.variantId != null && it.supplierSku) {
          map[Number(it.variantId)] = String(it.supplierSku);
        }
      }
      this.currentSupplierSkuByVariantId = map;
    } catch (err) {
      if (
        fetchToken !== this.currentSupplierSkuFetchToken ||
        !this.showReceiveDialog ||
        this.purchaseOrder?.supplierId !== supplierId
      ) {
        return;
      }
      // Non-fatal: search falls back to stored item.supplierSku snapshot.
      console.warn('Failed to fetch current supplier SKU map:', err);
      this.currentSupplierSkuByVariantId = {};
    }
  }

  // Add watcher to handle mode switching and preserve manual mode state
  @Watch('receiveMode')
  onReceiveModeChange(newMode: 'manual' | 'import', oldMode: 'manual' | 'import') {
    if (newMode === 'import') {
      // When switching to import mode, reset import-related state
      this.importFile = null;
      this.importingReceive = false;
      this.downloadingTemplate = false;
    }
    // Note: When switching to manual mode, we preserve receiveQuantities, deliveryNoteNumber, and receiveNotes
    // This allows users to switch modes without losing their manual entry progress
  }

  @Watch('isEditing')
  async onEditingChange(val: boolean) {
    if (val) {
      // Fetch suppliers when entering edit mode
      await this.fetchSuppliers();
      // Fetch variant order constraints for validation
      await this.fetchVariantOrderConstraints();
    }
  }


  private async fetchData() {
    this.loading = true;
    // Reset error states
    this.purchaseOrderNotFound = false;
    this.hasError = false;
    this.errorMessage = '';
    // US-069: fetchData reloads the master columnConfig, so reset the template
    // selection back to the default to keep the picker in sync with the table
    // (also matches the documented "resets on reload" behavior).
    this.selectedTemplateId = DEFAULT_TEMPLATE_ID;
    this.exportTemplateId = DEFAULT_TEMPLATE_ID;
    this.sendTemplateId = DEFAULT_TEMPLATE_ID;
    
    try {
      this.purchaseOrder = await PurchaseOrderModel.getById(Number(this.id));
      if(this.purchaseOrder.locationId){
        this.purchaseOrder.locationId = Number(this.purchaseOrder.locationId);
      }
      this.items = this.purchaseOrder?.items || [];

      // Initialize edited purchase order with current data
      this.editedPurchaseOrder = {
        ...(this.purchaseOrder as IPurchaseOrder), // Cast to ensure all properties are available for spread
        expectedDeliveryDate: this.toDateInputValue(this.purchaseOrder?.expectedDeliveryDate) || undefined,
        supplierId: this.purchaseOrder?.supplierId, // Ensure supplierId is included
        supplierName: this.purchaseOrder?.supplierName // Include supplier name
      };

      // Initialize departure date and internal notes for edit mode
      this.editedDepartureDate = this.toDateInputValue(this.purchaseOrder?.departureDate);
      this.internalNotesEditValue = this.purchaseOrder?.extra?.internalNotes || '';

      // Set loading to false early so PO details are shown
      this.loading = false;

      // Fetch locations and column config first (needed to determine if MasterData is required)
      await Promise.all([
        this.fetchLocations(),
        this.fetchColumnConfig(),
        this.fetchColumnTemplates(),  // US-069: available column templates for the picker
        this.fetchCustomColumnDefinitions(),  // Phase 2
        this.taxTypeList.fetch().catch(() => {})  // Tax types for tax column dropdowns
      ]);

      // Check if we need MasterData (for enabled columns OR metafield product_id lookup OR US-068 scan mode)
      const hasEnabledMasterDataColumns = this.columnConfig?.columns
        && Object.values(this.columnConfig.columns).some((config: any) => config.enabled);
      const hasEnabledMetafieldColumns = Object.values(this.metafieldColumns)
        .some((config: any) => config.enabled);

      // US-068: scan mode needs product_barcode from MasterData to match scanned codes.
      // Read the persisted flag directly: mounted() hydrates this.scanModeEnabled from
      // localStorage, but @Watch('id', { immediate: true }) fires fetchData() during
      // `created`, BEFORE mounted runs — so the in-memory flag isn't trustworthy here.
      let scanModePersisted = false;
      try { scanModePersisted = localStorage.getItem(this.SCAN_MODE_STORAGE_KEY) === 'true'; } catch { /* ignore */ }
      if (hasEnabledMasterDataColumns || hasEnabledMetafieldColumns || this.scanModeEnabled || scanModePersisted) {
        await Promise.all([this.fetchMasterData(), this.loadLocationStock()]);
      }

      // Fetch custom column values after items are loaded (Phase 2)
      await this.fetchCustomColumnValues();

      // Fetch metafield values if there are enabled metafield columns (Phase 3)
      if (hasEnabledMetafieldColumns) {
        await this.fetchMetafieldValues();
      }

      // Fetch supplier details if there's a supplier
      if (this.purchaseOrder?.supplierId) {
        this.supplier = await SupplierModel.getById(this.purchaseOrder.supplierId);
        
        // Fetch variant order constraints for validation (in background for draft POs)
        if (this.purchaseOrder.status === 'draft') {
          this.fetchVariantOrderConstraints(); // Don't await, let it run in background
        }
        
        // Pre-populate email recipients with supplier emails if available, without clobbering user edits
        if (this.supplier.contactEmail) {
          // Ensure supplier emails are always available in the dropdown for re-selection
          const supplierEmails = [this.supplier.contactEmail];
          if (this.supplier.additionalEmails && this.supplier.additionalEmails.length > 0) {
            supplierEmails.push(...this.supplier.additionalEmails);
          }
          this.savedEmails = [...new Set([...supplierEmails, ...this.savedEmails])].filter(Boolean);

          if (!this.emailRecipients || this.emailRecipients.length === 0) {
            this.emailRecipients = [...supplierEmails];
          }
        } else {
          this.emailRecipients = [];
        }

      }

      // Seed the bulk "apply tax to all" default. Resolved AFTER the supplier is
      // fetched above so resolveDefaultTaxTypeId() can prefer the supplier's
      // default; taxTypeList was already fetched in the Promise.all above.
      // Assign unconditionally (resolveDefaultTaxTypeId returns number | null) so
      // navigating to a PO with no default clears any value left over from a
      // previously-viewed PO — the component instance is reused across routes.
      this.bulkTaxTypeId = this.resolveDefaultTaxTypeId();

      // Pre-populate email subject, without overwriting user-modified value
      if (!this.emailSubject) {
        this.emailSubject = `Purchase Order #${this.purchaseOrder.poNumber}`;
      }

      // Pre-populate company name with store name
      if (!this.companyName) {
        this.companyName = this.store.information?.name || '';
      }
      
      // Fetch location details if there's a location
      if (this.purchaseOrder.locationId) {
        this.location = this.locations.find(location => location.id === this.purchaseOrder?.locationId) || null;
      }

      // Fetch resolved addresses in background
      this.fetchResolvedAddresses();

      // Fetch audit history in background
      this.fetchAuditHistory();

      // Load return history in the background
      this.loadReturnHistory();

      this.loadAdjustmentHistory();
    } catch (error: any) {
      console.error('Error fetching purchase order:', error);
      
      // Check if it's a 404 error (purchase order not found)
      if (error?.response?.status === 404 || error?.status === 404) {
        this.purchaseOrderNotFound = true;
      } else {
        this.hasError = true;
        this.errorMessage = error?.message || 'Failed to load purchase order details';
        EventBus.$emit('show-snackbar', 'Failed to load purchase order details. ' + error.message, 'error');
      }
      
      this.loading = false;
    }
  }

  private async fetchLocations() {
    try {
      const locationsList = new StoreLocationList();
      await locationsList.fetch();
      this.locations = locationsList.items;
    } catch (error: any) {
      console.error('Error fetching locations:', error.message);
    }
  }

  // Phase 1: Fetch column configuration
  private async fetchColumnConfig() {
    this.loadingConfig = true;
    try {
      const response: any = await get('/purchase_orders/config/line-item-columns');
      this.columnConfig = response.data;
      // Phase 3: Load metafield columns config
      this.metafieldColumns = response.data?.metafieldColumns || {};
    } catch (error: any) {
      console.error('Failed to fetch column config:', error);
      // Fallback to default (no optional columns)
      this.columnConfig = { columns: {} };
      this.metafieldColumns = {};
    } finally {
      this.loadingConfig = false;
    }
  }

  // US-069: Fetch the list of column templates (master/default + extras).
  private async fetchColumnTemplates() {
    this.loadingTemplates = true;
    try {
      const response: any = await get('/purchase_orders/config/column-templates');
      this.columnTemplates = shapeTemplateList(response?.data);
    } catch (error: any) {
      console.error('Failed to fetch column templates:', error);
      this.columnTemplates = [];
    } finally {
      this.loadingTemplates = false;
    }
  }

  // US-069: Apply the picked template to the on-screen line-items table.
  // Swaps columnConfig/metafieldColumns and re-fetches the data the new
  // template needs. Selection is ephemeral (resets to master on reload).
  private async onTemplateChange(): Promise<void> {
    const template = resolveTemplate(this.columnTemplates, this.selectedTemplateId);
    if (!template) return;
    // Guard against overlapping applies: a slow earlier fetch must not land
    // after a later selection and overwrite the table's data. The selector is
    // disabled while applying so switches are serialized.
    this.applyingColumnTemplate = true;
    try {
      // Keep selection in sync with what actually resolved (handles stale ids).
      this.selectedTemplateId = template.id;
      this.columnConfig = { columns: { ...template.columns } };
      // Cast: helper types ownerResource as string; the view uses the narrower
      // MetafieldOwnerResource union (same raw shape the API returns).
      this.metafieldColumns = { ...template.metafieldColumns } as any;

      const hasEnabledMasterDataColumns = Object.values(template.columns).some((c: any) => c.enabled);
      const hasEnabledMetafieldColumns = Object.values(template.metafieldColumns).some((c: any) => c.enabled);
      if (hasEnabledMasterDataColumns || hasEnabledMetafieldColumns) {
        await Promise.all([this.fetchMasterData(), this.loadLocationStock()]);
      }
      if (hasEnabledMetafieldColumns) {
        await this.fetchMetafieldValues();
      }
    } finally {
      this.applyingColumnTemplate = false;
    }
  }

  // AS-10834: while editing a draft, the column follows the unsaved delivery location.
  private get locationStockLocationId(): number | null {
    const id = this.isEditing && this.editedPurchaseOrder.locationId !== undefined
      ? this.editedPurchaseOrder.locationId
      : this.purchaseOrder?.locationId;
    return id || null;
  }

  private get locationStockLocationName(): string | null {
    const id = this.locationStockLocationId;
    if (!id) return null;
    return this.locations.find(loc => loc.id === id)?.name || null;
  }

  private getLocationStockValue(variantId: number | string): string {
    const qty = this.locationStock[String(variantId)];
    return qty === undefined ? '' : qty.toLocaleString();
  }

  private async loadLocationStock() {
    const enabled = this.columnConfig?.columns?.[LOCATION_STOCK_COLUMN]?.enabled === true;
    const locationId = this.locationStockLocationId;
    const variantIds = this.items.map((item: any) => item.variantId);
    if (!enabled || !locationId || variantIds.length === 0) {
      this.locationStock = {};
      return;
    }
    const token = ++this.locationStockFetchToken;
    try {
      const stock = await fetchLocationStock(variantIds, locationId);
      if (token !== this.locationStockFetchToken) return;
      this.locationStock = stock;
    } catch (error: any) {
      if (token !== this.locationStockFetchToken) return;
      console.error('Failed to fetch location stock:', error);
      this.locationStock = {};
    }
  }

  @Watch('locationStockLocationId')
  private onLocationStockLocationChange() {
    this.loadLocationStock();
  }

  // Phase 1: Fetch MasterData for variants in this PO
  private async fetchMasterData() {
    this.loadingMasterData = true;
    try {
      // Extract unique variant IDs from PO line items
      const variantIds = this.items
        .map((item: any) => String(item.variantId))
        .filter((id: string) => id && id !== 'null' && id !== 'undefined');

      if (variantIds.length === 0) {
        this.masterData = {};
        return;
      }

      // Build column_filter to fetch only these variants
      const columnFilter = [
        {
          code: 'variant_id',
          operator: 'in',
          values: variantIds,
          dataType: 'text'
        }
      ];

      // Build column list: required display columns + only ENABLED optional columns
      // Required columns for basic display (always needed)
      // variant_id is required for indexing the results
      // product_id is required for metafield lookups (Phase 3)
      // product_barcode is included unconditionally so US-068 scan mode can match
      // against variant.barcode regardless of whether the merchant has enabled the
      // Product Barcode optional column. One short string per variant.
      const requiredDisplayColumns = ['variant_id', 'product_id', 'variant', 'product_sku', 'variant_image', 'unit_price', 'product_barcode'];

      // Get only ENABLED columns from columnConfig (not PO_ITEM_COLUMNS as they come from PO items)
      const enabledOptionalColumns: string[] = [];
      if (this.columnConfig?.columns) {
        Object.entries(this.columnConfig.columns).forEach(([columnId, config]: [string, any]) => {
          // Skip PO item columns (received_quantity, returned_quantity, units_left) - they come from PO items, not MasterData
          if (PO_ITEM_COLUMNS.includes(columnId)) return;
          // Only add enabled columns that have an API code mapping
          if (config.enabled && COLUMN_API_CODES[columnId]) {
            enabledOptionalColumns.push(COLUMN_API_CODES[columnId]);
          }
        });
      }

      // Combine and deduplicate (unit_price is already in requiredDisplayColumns, so margin columns work automatically)
      const allVariantColumns = [...new Set([...requiredDisplayColumns, ...enabledOptionalColumns])];

      // Fetch variants with filter and all columns
      const params = {
        column_filter: JSON.stringify(columnFilter),
        columns: allVariantColumns.join(','),
        limit: variantIds.length // Ensure we get all variants
      };

      const response: any = await get('/master-data/variants', params);

      // Index by variant_id for O(1) lookup
      this.masterData = {};

      // Master Data API returns OLAP format: { message, data: { columns, rows, ... } }
      // The columns array has metadata with the actual column codes
      const columns = response?.data?.columns || [];
      const rows = response?.data?.rows || [];

      if (Array.isArray(rows) && rows.length > 0 && Array.isArray(columns)) {
        rows.forEach((row: any) => {
          // Parse the OLAP row format - each row has items array
          const items = row.items || [];

          // Build a flat object from items array
          // Use the columns metadata to get the correct column code for each item
          const flatVariant: any = {};

          items.forEach((item: any, index: number) => {
            const value = item.value || '';
            const originalValue = item.original_value !== undefined && item.original_value !== null
              ? item.original_value : value;

            // Get the column metadata by index to know the correct code
            const columnMeta = columns[index];
            if (!columnMeta) return;

            const columnCode = columnMeta.code;

            // Store by column code (the unique identifier)
            flatVariant[columnCode] = value;
            // Store raw numeric value for calculations (avoids comma-formatted strings)
            flatVariant[`_raw_${columnCode}`] = originalValue;

            // Also store by internal column ID using reverse mapping
            // This allows getMasterDataValue to look up by column ID directly
            const columnId = COLUMN_ID_BY_API_CODE[columnCode];
            if (columnId) {
              flatVariant[columnId] = value;
              flatVariant[`_raw_${columnId}`] = originalValue;
            }

            // Legacy aliases for columns with different API codes
            if (columnCode === 'variant_unit_cost') {
              flatVariant.unit_cost = value;
            } else if (columnCode === 'inventoty_tracking') {
              // Typo in API response
              flatVariant.inventory_tracking = value;
            }
          });

          // Index by variant_id as string for O(1) lookup
          const variantId = String(flatVariant.variant_id || '');
          if (variantId) {
            this.masterData[variantId] = flatVariant;
          }
        });
      }

    } catch (error: any) {
      console.error('Failed to fetch MasterData:', error);
      this.masterData = {};
      // Don't show error to user - just log it
    } finally {
      this.loadingMasterData = false;
    }
  }

  // Phase 2: Fetch custom column definitions
  private async fetchCustomColumnDefinitions() {
    try {
      const response: any = await get('/purchase_orders/config/custom-columns');
      this.customColumnDefinitions = response.data?.columns || {};
    } catch (error: any) {
      console.error('Failed to fetch custom column definitions:', error);
      this.customColumnDefinitions = {};
    }
  }

  // Phase 2: Fetch custom column values for variants in this PO
  private async fetchCustomColumnValues() {
    this.loadingCustomColumns = true;
    try {
      // Extract unique variant IDs from PO line items
      const variantIds = this.items
        .map((item: any) => String(item.variantId))
        .filter((id: string) => id && id !== 'null' && id !== 'undefined');

      if (variantIds.length === 0) {
        this.customColumnValues = {};
        return;
      }

      const response: any = await get('/purchase_orders/config/custom-column-values', {
        object_ids: variantIds.join(','),
        object_type: 'variant'
      });

      // Build values map from response
      this.customColumnValues = response.data || {};
    } catch (error: any) {
      console.error('Failed to fetch custom column values:', error);
      this.customColumnValues = {};
    } finally {
      this.loadingCustomColumns = false;
    }
  }

  // Save edited custom column values for dirty variants.
  // Values are stored per-variant in ProductCustomColumns, so edits affect
  // that variant across all POs (not just this one).
  private async saveCustomColumnValues(): Promise<void> {
    if (this.dirtyCustomColumnVariants.size === 0) return;

    const variantIds = Array.from(this.dirtyCustomColumnVariants)
      .filter(v => v && v !== '0' && v !== 'null' && v !== 'undefined');
    if (variantIds.length === 0) {
      this.dirtyCustomColumnVariants.clear();
      return;
    }

    const BATCH = 100;
    const defs = this.customColumnDefinitions || {};

    for (let i = 0; i < variantIds.length; i += BATCH) {
      const slice = variantIds.slice(i, i + BATCH);
      const values: {[variantId: string]: {[columnId: string]: string}} = {};
      slice.forEach(vid => {
        const raw = this.customColumnValues[vid] || {};
        const filtered: {[columnId: string]: string} = {};
        Object.keys(raw).forEach(colId => {
          if (defs[colId]) filtered[colId] = raw[colId];
        });
        values[vid] = filtered;
      });
      await put('/purchase_orders/config/custom-column-values', {
        object_type: 'variant',
        values,
      });
    }
    this.dirtyCustomColumnVariants.clear();
  }

  // Phase 3: Fetch metafield values for both product- and variant-scoped columns.
  // The backend metafield-values endpoint is per-resource, so we issue up to two
  // requests in parallel and merge into the split metafieldValues shape.
  private async fetchMetafieldValues() {
    // Split enabled keys by ownerResource using the shared config-key parser
    // ("variant:..." -> PRODUCTVARIANT, everything else -> PRODUCT).
    const productKeys: string[] = [];
    const variantKeys: string[] = [];
    Object.entries(this.metafieldColumns).forEach(([key, config]) => {
      if (!config?.enabled) return;
      const { ownerResource, effectiveKey } = parseMetafieldConfigKey(key);
      if (ownerResource === 'PRODUCTVARIANT') {
        variantKeys.push(effectiveKey);
      } else {
        productKeys.push(effectiveKey);
      }
    });

    // Skip entirely if nothing is enabled
    if (productKeys.length === 0 && variantKeys.length === 0) {
      this.metafieldValues = { product: {}, variant: {} };
      return;
    }

    // Resolve owner IDs for each resource
    const productIds = [...new Set(
      this.items
        .map((item: any) => {
          if (item.productId) {
            return String(item.productId);
          }
          const variantIdStr = String(item.variantId);
          const masterDataItem = this.masterData[variantIdStr];
          return masterDataItem?.product_id ? String(masterDataItem.product_id) : null;
        })
        .filter(Boolean)
    )] as string[];

    const variantIds = [...new Set(
      this.items
        .map((item: any) => (item.variantId != null ? String(item.variantId) : null))
        .filter(Boolean)
    )] as string[];

    this.loadingMetafieldValues = true;

    const fetchPart = async (
      ownerResource: 'PRODUCT' | 'PRODUCTVARIANT',
      ownerIds: string[],
      keys: string[]
    ) => {
      if (keys.length === 0 || ownerIds.length === 0) return {};
      try {
        const response: any = await get('/purchase_orders/config/metafield-values', {
          ownerResource,
          owner_ids: ownerIds.join(','),
          keys: keys.join(','),
        });
        return response.data || {};
      } catch (error: any) {
        console.error(`Failed to fetch ${ownerResource} metafield values:`, error);
        return {};
      }
    };

    try {
      const [productValues, variantValues] = await Promise.all([
        fetchPart('PRODUCT', productIds, productKeys),
        fetchPart('PRODUCTVARIANT', variantIds, variantKeys),
      ]);
      this.metafieldValues = { product: productValues, variant: variantValues };
    } finally {
      this.loadingMetafieldValues = false;
    }
  }

  private async fetchAuditHistory() {
    if (!this.purchaseOrder) return;

    this.loadingAuditHistory = true;
    try {
      this.auditHistory = await this.purchaseOrder.getHistory();
    } catch (error: any) {
      console.error('Error fetching audit history:', error.message);
      // Don't show error to user, just log it
    } finally {
      this.loadingAuditHistory = false;
    }
  }

  getEventColor(type: string): string {
    switch (type) {
      case 'status_change':
        return 'blue';
      case 'email_sent':
        return 'purple';
      case 'receive':
        return 'green';
      case 'return':
        return 'orange';
      case 'cost_correction':
        return 'teal';
      case 'tax_change':
        return 'deep-purple';
      default:
        return 'grey';
    }
  }

  getEventIcon(type: string): string {
    switch (type) {
      case 'status_change':
        return 'mdi-list-status';
      case 'email_sent':
        return 'mdi-email-send';
      case 'receive':
        return 'mdi-package-down';
      case 'return':
        return 'mdi-package-up';
      case 'cost_correction':
        return 'mdi-cash-check';
      case 'tax_change':
        return 'mdi-percent';
      default:
        return 'mdi-information';
    }
  }

  getEventTitle(event: IPurchaseOrderHistoryItem): string {
    switch (event.type) {
      case 'status_change':
        return 'Status Changed';
      case 'email_sent':
        return 'Email Sent';
      case 'receive':
        return 'Items Received';
      case 'return':
        return 'Items Returned';
      case 'cost_correction':
        return 'Costs Finalised';
      case 'tax_change':
        return (event.data as any)?.cleared ? 'Tax Removed' : 'Tax Applied';
      default:
        return 'Event';
    }
  }

  getEventDescription(event: IPurchaseOrderHistoryItem): string {
    const data = event.data as any;
    switch (event.type) {
      case 'status_change':
        const prevStatus = this.formatStatus(data?.previousStatus);
        const newStatus = this.formatStatus(data?.newStatus);
        return `${prevStatus} → ${newStatus}`;
      case 'email_sent':
        const recipients = data?.recipients?.join(', ') || 'Unknown';
        const emailStatus = data?.status === 'sent' ? 'Sent to' : 'Failed to send to';
        const cc = data?.ccRecipients?.length ? ` (CC: ${data.ccRecipients.join(', ')})` : '';
        return `${emailStatus} ${recipients}${cc}`;
      case 'receive':
        const receiveQty = data?.totalQuantity || 0;
        const receiveItems = data?.itemCount || 0;
        return `${receiveQty} units across ${receiveItems} items`;
      case 'return':
        const returnQty = data?.totalQuantity || 0;
        const returnItems = data?.itemCount || 0;
        return `${returnQty} units across ${returnItems} items`;
      case 'cost_correction': {
        const lineCount = Array.isArray(data?.lines) ? data.lines.length : 0;
        const noun = lineCount === 1 ? 'line' : 'lines';
        // PO-currency totals, exactly like previousGrandTotal/newGrandTotal in
        // the tax_change case below — formatCurrency would default to the STORE
        // currency and mislabel a foreign-currency PO.
        const before = this.formatCurrencyFor(data?.totalAmountBefore);
        const after = this.formatCurrencyFor(data?.totalAmountAfter);
        const invoice = data?.invoiceNumber ? ` · invoice ${data.invoiceNumber}` : '';
        // What actually reached Shopify, and why it may differ from the method
        // the merchant picked (AS-10622).
        const sync = describeCostSync(data?.costSync);
        const syncPart = sync ? ` · ${sync}` : '';
        return `${lineCount} ${noun} repriced · total ${before} → ${after}${invoice}${syncPart}`;
      }
      case 'tax_change': {
        const lineCount = data?.lineCount || 0;
        const before = this.formatCurrencyFor(data?.previousGrandTotal);
        const after = this.formatCurrencyFor(data?.newGrandTotal);
        const what = data?.cleared
          ? 'Tax removed'
          : `${data?.taxTypeName || 'Tax'} applied`;
        const reason = data?.reason ? ` · ${data.reason}` : '';
        return `${what} on ${lineCount} line(s) · total ${before} → ${after}${reason}`;
      }
      default:
        return '';
    }
  }

  formatDateTime(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  private async fetchSuppliers() {
    this.loadingSuppliers = true;
    try {
      const supplierList = new SupplierList();
      await supplierList.fetch();
      this.suppliers = supplierList.items;
    } catch (error: any) {
      console.error('Error fetching suppliers:', error.message);
      EventBus.$emit('show-snackbar', 'Failed to load suppliers', 'error');
    } finally {
      this.loadingSuppliers = false;
    }
  }

  private async fetchVariantOrderConstraints() {
    if (!this.purchaseOrder || !this.supplier) return;
    
    // Only fetch constraints for draft purchase orders
    if (this.purchaseOrder.status !== 'draft') return;
    
    this.loadingConstraints = true;
    try {
      // Get unique variant IDs from editable items (includes newly added items)
      const variantIds = [...new Set(this.items.map(item => item.variantId).filter(id => id > 0))];
      
      if (variantIds.length === 0) return;
      
      // Create filter for specific variants
      const filterColumns = new FilterColumnList();
      const variantFilter = new FilterColumnModel({
        name: 'Variant Id',
        code: 'variant_id',
        operator: 'eq',
        dataType: 'number',
        value: variantIds
      });
      filterColumns.add(variantFilter);
      
      // Fetch data using supplier's fetchProductsWithOptions
      const report = await this.supplier.fetchProductsWithOptions(
        'inventory_replenishment',
        'eq',
        {
          initialFilterColumns: filterColumns,
          pageOptions: {
            page: 1,
            itemsPerPage: 1000  // Fetch all variants in one go
          },
          columns: [
            { code: 'variant_id', dataType: 'number', name: 'Variant ID' },
            { code: '[variant_min_order_quantity]', dataType: 'number', name: 'Min Order Qty' },
            { code: '[variant_max_order_quantity]', dataType: 'number', name: 'Max Order Qty' }
          ],
          suggestFutureReorderQuantity: true
        }
      );
      
      // Clear existing constraints
      this.variantOrderConstraints = {};
      
      // Parse report data using getReportRowsData() method
      const reportData = report.getReportRowsData();
      
      // Store constraints in map
      if (reportData && reportData.length > 0) {
        reportData.forEach((row: any) => {
          const variantId = row['variant_id'];
          const minQty = parseFloat(row['[variant_min_order_quantity]']) || 0;
          const maxQty = parseFloat(row['[variant_max_order_quantity]']) || 999999;
          
          if (variantId) {
            this.variantOrderConstraints[variantId] = {
              min: minQty,
              max: maxQty === 0 ? 999999 : maxQty  // Treat 0 max as no limit
            };
          }
        });
      }
      
      // Validate all items after loading constraints
      this.validateAllItems();
    } catch (error: any) {
      console.error('Error fetching variant order constraints:', error);
      // Don't show error to user, constraints are optional
    } finally {
      this.loadingConstraints = false;
    }
  }

  private async onSupplierChange(supplierId: number | null) {
    if (supplierId) {
      // Fetch the new supplier details
      try {
        const newSupplier = await SupplierModel.getById(supplierId);
        this.supplier = newSupplier;
        
        // Update the supplier name in the edited purchase order
        this.editedPurchaseOrder.supplierName = newSupplier.name;
        
        // Fetch variant constraints for the new supplier
        await this.fetchVariantOrderConstraints();
      } catch (error: any) {
        console.error('Error fetching supplier details:', error.message);
        EventBus.$emit('show-snackbar', 'Failed to load supplier details', 'error');
      }
    } else {
      // Clear supplier if null is selected
      this.supplier = null;
      this.editedPurchaseOrder.supplierName = undefined;
      this.variantOrderConstraints = {};
    }
  }

  private addItem() {
    const newItem: IPurchaseOrderItem = {
        productName: '',
        sku: '',
        orderedQuantity: 1,
        receivedQuantity: 0,
        unitPrice: 0,
        totalPrice: 0,
        variantId: 0
    };
    // Seed the default tax so the blank row matches the rest of the PO; the
    // tax amount stays 0 until a unit price is entered (updateItemTotal recomputes).
    const defaultTaxTypeId = this.resolveDefaultTaxTypeId();
    if (defaultTaxTypeId) { this.onTaxTypeChange(newItem, defaultTaxTypeId); }
    this.items.push(newItem);
  }

  private onProductsAdded(newItems: IPurchaseOrderItem[]) {
    if (newItems.length === 0) return;

    // Auto-fill the supplier/merchant default tax on new lines that don't
    // already carry one. AddProductsDialog emits items without tax (it doesn't
    // load the tax-type list), so without this the Tax Type / Tax % columns
    // stay blank for newly-added products even though existing lines and the
    // PO's tax setup have a default. Items that arrive with tax are left as-is.
    const defaultTaxTypeId = this.resolveDefaultTaxTypeId();
    if (defaultTaxTypeId) {
      for (const item of newItems) {
        const hasTax = item.extra?.financial?.tax_type_id != null
          || item.extra?.financial?.tax_rate != null;
        if (!hasTax) { this.onTaxTypeChange(item, defaultTaxTypeId); }
      }
    }

    // Prepend new items to the top so users can easily edit qty, price, etc.
    this.items = [...newItems, ...this.items];

    // Reset the items table to page 1 so the newly-prepended rows are
    // actually visible — otherwise Vuetify's page state is sticky and
    // users sitting on page 2+ would see nothing change after adding.
    // This pairs with `:page.sync="itemsTablePage"` on the v-data-table.
    this.itemsTablePage = 1;

    // Enter edit mode if not already editing
    if (!this.isEditing) {
      this.isEditing = true;
    }

    // Refresh constraints so min/max warnings apply to newly added variants
    this.fetchVariantOrderConstraints();

    // New rows would otherwise show blank MasterData/location-stock cells until reload.
    const hasEnabledMasterDataColumns = this.columnConfig?.columns
      && Object.values(this.columnConfig.columns).some((config: any) => config.enabled);
    if (hasEnabledMasterDataColumns) {
      this.fetchMasterData();
      this.loadLocationStock();
    }

    EventBus.$emit('show-snackbar', `${newItems.length} product(s) added to purchase order`, 'success');
  }

  private async removeItem(item: IPurchaseOrderItem) {
    if (!this.purchaseOrder || !item.id) return;

    this.deletingItemId = item.id;
    try {
      // Call the API to remove the item
      await this.purchaseOrder.removeItem(item.id);
      
      // Remove from local items array
      const index = this.items.findIndex(i => i.id === item.id);
      if (index !== -1) {
        this.items.splice(index, 1);
      }

      // Show success message
      EventBus.$emit('show-snackbar', 'Item removed successfully', 'success');
      
      // Update total amount
      if (this.purchaseOrder) {
        this.purchaseOrder.calculateTotalAmount();
      }
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to remove item: ${error.message}`, 'error');
      console.error('Error removing item:', error);
    } finally {
      this.deletingItemId = null;
    }
  }

  private updateItemTotal(item: IPurchaseOrderItem) {
    item.totalPrice = item.orderedQuantity * item.unitPrice;
    // Recalculate tax amount if a tax rate is set
    const taxRate = item.extra?.financial?.tax_rate || 0;
    if (taxRate > 0 && item.extra?.financial) {
      item.extra.financial.tax_amount = Math.round((item.orderedQuantity * item.unitPrice * taxRate) * 100) / 100;
    }
    // Validate quantity when it changes
    if (item.id) {
      this.validateItemQuantity(item);
    }
  }

  private validateItemQuantity(item: IPurchaseOrderItem) {
    if (!item.id) return;
    
    // Only validate when PO status is draft
    if (this.purchaseOrder?.status !== 'draft') {
      Vue.delete(this.quantityValidationErrors, item.id);
      return;
    }
    
    const constraint = this.variantOrderConstraints[item.variantId];
    if (!constraint) {
      // No constraints for this variant - use Vue.delete for reactivity
      Vue.delete(this.quantityValidationErrors, item.id);
      return;
    }
    
    const qty = item.orderedQuantity;
    
    if (qty < constraint.min) {
      Vue.set(this.quantityValidationErrors, item.id, `Minimum order quantity is ${constraint.min}`);
    } else if (qty > constraint.max && constraint.max < 999999) {
      Vue.set(this.quantityValidationErrors, item.id, `Maximum order quantity is ${constraint.max}`);
    } else {
      // Remove error - use Vue.delete for reactivity
      Vue.delete(this.quantityValidationErrors, item.id);
    }
  }

  private validateAllItems() {
    if (!this.items) return;
    
    // Clear all existing errors
    this.quantityValidationErrors = {};
    
    // Only validate items when PO status is draft
    if (this.purchaseOrder?.status !== 'draft') return;
    
    // Validate each item
    this.items.forEach(item => {
      if (item.id && item.variantId) {
        this.validateItemQuantity(item);
      }
    });
  }

  private getQuantityError(item: IPurchaseOrderItem): string {
    // Only show validation errors when PO status is draft
    if (this.purchaseOrder?.status !== 'draft') return '';
    return item.id ? (this.quantityValidationErrors[item.id] || '') : '';
  }

  private getQuantityHint(item: IPurchaseOrderItem): string {
    const constraint = this.variantOrderConstraints[item.variantId];
    if (!constraint) return '';
    
    if (constraint.max >= 999999) {
      return constraint.min > 0 ? `Min: ${constraint.min}` : '';
    }
    
    return `Min: ${constraint.min}, Max: ${constraint.max}`;
  }

  private get hasQuantityErrors(): boolean {
    // Only show validation errors when PO status is draft
    if (this.purchaseOrder?.status !== 'draft') return false;
    return Object.keys(this.quantityValidationErrors).length > 0;
  }

  private get quantityErrorItems(): IPurchaseOrderItem[] {
    // Only show validation errors when PO status is draft
    if (this.purchaseOrder?.status !== 'draft') return [];
    return this.items.filter(item => 
      item.id && this.quantityValidationErrors[item.id]
    );
  }

  private get getSubtotal(): number {
    if (!this.items) return 0;
    return this.items.reduce((total, item) => {
      const itemTotal = Number(item.unitPrice || 0) * Number(item.orderedQuantity || 0);
      return total + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);
  }

  private get getTotalAmount(): number {
    if (!this.purchaseOrder) return this.getSubtotal;
    const subtotal = this.getSubtotal;
    const itemTaxTotal = this.getItemTaxTotal;
    const shippingCost = this.purchaseOrder.shippingCost;
    const shippingTaxAmount = this.purchaseOrder.shippingTaxAmount;
    const discountCalculated = this.purchaseOrder.discountCalculated;
    const adjustmentAmount = this.purchaseOrder.adjustmentAmount;
    return Math.round((subtotal + itemTaxTotal + shippingCost + shippingTaxAmount - discountCalculated + adjustmentAmount) * 100) / 100;
  }

  private get getTotalUnits(): number {
    if (!this.items) return 0;
    return this.items.reduce((total, item) => {
      const quantity = Number(item.orderedQuantity || 0);
      return total + (isNaN(quantity) ? 0 : quantity);
    }, 0);
  }

  private get getItemTaxTotal(): number {
    if (!this.items) return 0;
    return this.items.reduce((sum, item) => {
      return sum + (Number(item.extra?.financial?.tax_amount) || 0);
    }, 0);
  }

  private get getTotalTax(): number {
    const itemTax = this.getItemTaxTotal;
    const shippingTax = this.purchaseOrder?.shippingTaxAmount || 0;
    return itemTax + shippingTax;
  }

  private get landedCostRatio(): string {
    const serverRatio = this.purchaseOrder?.extra?.financial?.landed_cost_ratio;
    if (serverRatio != null) return Number(serverRatio).toFixed(2) + 'x';
    // Fallback for unsaved POs
    const subtotal = this.getSubtotal;
    if (subtotal <= 0) return '0.00x';
    const shipping = this.purchaseOrder?.shippingCost || 0;
    const adjustment = this.purchaseOrder?.adjustmentAmount || 0;
    const discount = this.purchaseOrder?.discountCalculated || 0;
    const discountedSubtotal = subtotal - discount;
    if (discountedSubtotal <= 0) return '0.00x';
    return ((discountedSubtotal + adjustment + shipping) / discountedSubtotal).toFixed(2) + 'x';
  }

  private get avgLandedCostPerUnit(): number {
    const serverAvg = this.purchaseOrder?.extra?.financial?.avg_landed_cost_per_unit;
    if (serverAvg != null) return Number(serverAvg);
    // Fallback for unsaved POs
    const units = this.getTotalUnits;
    if (units <= 0) return 0;
    const subtotal = this.getSubtotal;
    const shipping = this.purchaseOrder?.shippingCost || 0;
    const adjustment = this.purchaseOrder?.adjustmentAmount || 0;
    const discount = this.purchaseOrder?.discountCalculated || 0;
    return (subtotal - discount + adjustment + shipping) / units;
  }

  // Parse a numeric value that may be a comma-formatted string from OLAP (e.g., "2,630.00")
  private parseNumericValue(value: any): number {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    const cleaned = String(value).replace(/,/g, '');
    const num = Number(cleaned);
    return isNaN(num) ? 0 : num;
  }

  // Resolve selling price from masterData for a variant, handling comma-formatted OLAP strings
  private resolveSellingPrice(variant: any): number {
    if (!variant) return 0;
    const raw = variant._raw_selling_price ?? variant._raw_unit_price;
    if (raw != null) {
      const num = this.parseNumericValue(raw);
      if (num > 0) return num;
    }
    return this.parseNumericValue(variant.selling_price ?? variant.unit_price ?? 0);
  }

  // Profitability summary: helper to get raw selling price from masterData
  private getItemSellingPrice(item: any): number {
    const variant = this.masterData[String(item.variantId)];
    return this.resolveSellingPrice(variant);
  }

  // Count of items that have selling price data in masterData
  private get itemsWithSellingPrice(): number {
    return this.items.filter(item => this.getItemSellingPrice(item) > 0).length;
  }

  // Total revenue from selling prices (only items with selling price data)
  private get totalRevenue(): number {
    return this.items.reduce((sum, item) => {
      const price = this.getItemSellingPrice(item);
      if (!price) return sum;
      return sum + price * Number(item.orderedQuantity || 0);
    }, 0);
  }

  // Total margin = total revenue - total cost (for items with selling price)
  private get totalMargin(): number {
    return this.items.reduce((sum, item) => {
      const price = this.getItemSellingPrice(item);
      if (!price) return sum;
      const qty = Number(item.orderedQuantity || 0);
      return sum + (price * qty) - (Number(item.unitPrice || 0) * qty);
    }, 0);
  }

  // Average margin % = (totalMargin / totalRevenue) × 100
  private get avgMarginPercent(): number {
    if (this.totalRevenue <= 0) return 0;
    return (this.totalMargin / this.totalRevenue) * 100;
  }

  // Landed margin aggregation (only items with BOTH selling price AND landed_cost)
  private get totalLandedMargin(): { margin: number; percent: number; count: number } {
    let revenue = 0;
    let cost = 0;
    let count = 0;
    this.items.forEach((item: any) => {
      const price = this.getItemSellingPrice(item);
      const lc = item.extra?.landed_cost;
      if (!price || lc == null) return;
      const qty = Number(item.orderedQuantity || 0);
      revenue += price * qty;
      cost += Number(lc) * qty;
      count++;
    });
    const margin = revenue - cost;
    const percent = revenue > 0 ? (margin / revenue) * 100 : 0;
    return { margin, percent, count };
  }

  // Color class for margin percentage values (summary-level)
  private getMarginPercentColorClass(percent: number): string {
    if (percent >= 30) return 'success--text';
    if (percent >= 15) return 'warning--text';
    return 'error--text';
  }

  private onTaxTypeChange(item: IPurchaseOrderItem, taxTypeId: number | null) {
    if (!item.extra) Vue.set(item, 'extra', {});
    if (!item.extra!.financial) Vue.set(item.extra!, 'financial', {});

    if (!taxTypeId) {
      // Cleared selection
      Vue.set(item.extra!.financial!, 'tax_type_id', undefined);
      Vue.set(item.extra!.financial!, 'tax_type_name', undefined);
      Vue.set(item.extra!.financial!, 'tax_rate', 0);
      Vue.set(item.extra!.financial!, 'tax_amount', 0);
      return;
    }

    const taxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.id === taxTypeId);
    if (!taxType) return;

    Vue.set(item.extra!.financial!, 'tax_type_id', taxType.id);
    Vue.set(item.extra!.financial!, 'tax_type_name', taxType.name);
    Vue.set(item.extra!.financial!, 'tax_rate', taxType.rate);
    const taxAmount = Math.round((item.orderedQuantity * item.unitPrice * taxType.rate) * 100) / 100;
    Vue.set(item.extra!.financial!, 'tax_amount', taxAmount);
  }

  private onTaxRateInput(item: IPurchaseOrderItem, value: string) {
    if (!item.extra) Vue.set(item, 'extra', {});
    if (!item.extra!.financial) Vue.set(item.extra!, 'financial', {});

    const pct = Number(value) || 0;
    const rate = pct / 100;
    Vue.set(item.extra!.financial!, 'tax_rate', rate);
    Vue.set(item.extra!.financial!, 'tax_type_id', undefined);
    Vue.set(item.extra!.financial!, 'tax_type_name', rate > 0 ? 'Custom' : undefined);
    const taxAmount = Math.round((item.orderedQuantity * item.unitPrice * rate) * 100) / 100;
    Vue.set(item.extra!.financial!, 'tax_amount', taxAmount);
  }

  // Resolve the default tax type id: the supplier's configured default first,
  // then the merchant-wide default. Single source of truth for tax defaulting
  // (used to seed bulkTaxTypeId on load and to auto-fill newly-added lines).
  private resolveDefaultTaxTypeId(): number | null {
    const supplierDefaultId = this.supplier?.options?.financial?.default_tax_type_id;
    const supplierDefault = supplierDefaultId
      ? this.taxTypeList.items.find((t: TaxTypeModel) => t.id == supplierDefaultId)
      : null;
    const merchantDefault = this.taxTypeList.items.find((t: TaxTypeModel) => t.isDefault);
    const defaultType = supplierDefault || merchantDefault;
    return defaultType ? defaultType.id : null;
  }

  private getLineTotalWithTax(item: IPurchaseOrderItem): number {
    const lineTotal = (Number(item.orderedQuantity) || 0) * (Number(item.unitPrice) || 0);
    const taxAmount = Number(item.extra?.financial?.tax_amount) || 0;
    return lineTotal + taxAmount;
  }

  private get hasTaxColumnsEnabled(): boolean {
    return this.isColumnEnabled('tax_type') || this.isColumnEnabled('tax_rate') || this.isColumnEnabled('tax_amount');
  }

  private applyTaxToAllItems() {
    if (!this.bulkTaxTypeId) return;
    const taxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.id === this.bulkTaxTypeId);
    if (!taxType) return;

    for (const item of this.items) {
      this.onTaxTypeChange(item, taxType.id);
    }
    this.showBulkTaxMenu = false;
  }

  private clearTaxFromAllItems() {
    for (const item of this.items) {
      this.onTaxTypeChange(item, null);
    }
    this.showBulkTaxMenu = false;
  }

  // Return computed properties
  get canReturnItems(): boolean {
    // Can return items from partial_received, received, or completed POs
    // AND there must be items with receivable quantity > returned quantity
    if (!this.purchaseOrder?.items) return false;
    const statusAllowsReturn =
      this.purchaseOrder.status === 'partial_received' ||
      this.purchaseOrder.status === 'received' ||
      this.purchaseOrder.status === 'completed';
    const hasReturnableItems = this.purchaseOrder.items.some(item =>
      (item.receivedQuantity || 0) > (item.returnedQuantity || 0)
    );
    return statusAllowsReturn && hasReturnableItems;
  }

  get canAdjustQuantity(): boolean {
    if (!this.purchaseOrder) return false;
    const s = this.purchaseOrder.status as string;
    return s === 'confirmed' || s === 'partial_received' || s === 'received';
  }

  /** US-093 — the PO's LOCKED rate, never a spot rate (D4). */
  get poExchangeRate(): number | null {
    const rate = Number((this.purchaseOrder as any)?.extra?.financial?.exchange_rate);
    return Number.isFinite(rate) && rate > 0 ? rate : null;
  }

  /**
   * US-093 / AC16. Reload the PO (totals and line costs both moved) and the
   * audit history, which now carries the cost_correction entry.
   */
  private async onCostsFinalised(): Promise<void> {
    this.showFinaliseCostsDialog = false;
    await this.fetchData();
    await this.fetchAuditHistory();
    EventBus.$emit('show-snackbar', 'Purchase order costs finalised', 'success');
  }

  /**
   * US-093 / AC1, AC2. Finalising costs is offered on `partial_received` and
   * `received` only. A `completed` PO shows Re-open instead (D3) — routing
   * through it means this action never writes to a terminal PO.
   */
  get canFinaliseCosts(): boolean {
    if (!this.purchaseOrder) return false;
    const s = this.purchaseOrder.status as string;
    return s === 'partial_received' || s === 'received';
  }

  /**
   * AS-10746: tax may be applied or changed through `received`. Unlike the
   * draft-only edit gate this is safe past confirmation — landed cost excludes
   * tax, and the unit cost pushed to Shopify on receive is `extra.landed_cost`.
   */
  get canApplyLineTax(): boolean {
    if (!this.purchaseOrder) return false;
    // No tax types configured means there is nothing to apply; the merchant
    // sets them up in Financial Settings first.
    if (!this.taxTypeList.items || this.taxTypeList.items.length === 0) return false;
    return canApplyTax(this.purchaseOrder.status as string);
  }


  /**
   * The supplier's configured default tax type, used to preselect the dialog.
   * Arrives from JSON supplier options, so it may be a string.
   */
  get supplierDefaultTaxTypeId(): number | string | null {
    return (this.supplier as any)?.options?.financial?.default_tax_type_id ?? null;
  }

  private async onTaxApplied(): Promise<void> {
    this.showApplyTaxDialog = false;
    EventBus.$emit('show-snackbar', 'Tax updated successfully', 'success');
    // fetchData() reloads the PO and fans out the history fetches, which is
    // what this needs: the response carries a new grand total AND a new
    // payment status, and the audit history has a new tax_change row.
    await this.fetchData();
  }

  private onTaxApplyError(message: string): void {
    EventBus.$emit('show-snackbar', message || 'Failed to apply tax', 'error');
  }

  get returnableItems(): IPurchaseOrderItem[] {
    if (!this.purchaseOrder?.items) return [];
    return this.purchaseOrder.items.filter(item =>
      (item.receivedQuantity || 0) > (item.returnedQuantity || 0)
    );
  }

  get returnTableHeaders() {
    return [
      { text: 'Product', value: 'product', sortable: false },
      { text: 'Received', value: 'receivedQuantity', align: 'center' },
      { text: 'Returned', value: 'returnedQuantity', align: 'center' },
      { text: 'Available', value: 'availableToReturn', align: 'center' },
      { text: 'Return Qty', value: 'returnQuantity', align: 'center', sortable: false }
    ];
  }

  get totalReturningItems(): number {
    return this.returnableItems.filter(item =>
      this.returnQuantities[item.id!] && this.returnQuantities[item.id!] > 0
    ).length;
  }

  get totalReturningQuantity(): number {
    return this.returnableItems.reduce((sum, item) => {
      const qty = this.returnQuantities[item.id!] || 0;
      return sum + Number(qty);
    }, 0);
  }

  get totalReturningValue(): number {
    return this.returnableItems.reduce((sum, item) => {
      const qty = this.returnQuantities[item.id!] || 0;
      const cost = item.unitPrice || 0;
      return sum + (Number(qty) * cost);
    }, 0);
  }

  get canSubmitReturn(): boolean {
    const hasItems = this.totalReturningItems > 0 && this.totalReturningQuantity > 0;
    const hasErrors = Object.keys(this.returnQuantityErrors).length > 0;
    return hasItems && !hasErrors;
  }

  get sortedReturnHistory(): any[] {
    return [...this.returnHistory].sort((a, b) => {
      const dateA = new Date(a.returnDate).getTime();
      const dateB = new Date(b.returnDate).getTime();
      return dateB - dateA;
    });
  }

  private async savePurchaseOrder() {
    if (!this.purchaseOrder) return;

    this.saving = true;
    try {
      // For non-draft POs, only update extra field (departure date)
      if (this.purchaseOrder.status !== 'draft') {
        // Update departure date and internal notes (stored in extra field)
        this.purchaseOrder.departureDate = this.editedDepartureDate || undefined;
        if (!this.purchaseOrder.extra) this.purchaseOrder.extra = {};
        this.purchaseOrder.extra.internalNotes = this.internalNotesEditValue?.trim() || undefined;
        await put(`/purchase_orders/${this.purchaseOrder.id}`, {
          extra: this.purchaseOrder.extra
        });
      } else {
        // For draft POs, update all fields
        Object.assign(this.purchaseOrder, this.editedPurchaseOrder);
        this.purchaseOrder.items = this.items;

        // Update departure date and internal notes AFTER Object.assign to avoid being overwritten
        this.purchaseOrder.departureDate = this.editedDepartureDate || undefined;
        if (!this.purchaseOrder.extra) this.purchaseOrder.extra = {};
        this.purchaseOrder.extra.internalNotes = this.internalNotesEditValue?.trim() || undefined;

        // Calculate totals
        this.purchaseOrder.calculateTotalAmount();

        // Save to API
        await this.purchaseOrder.save();

        // Re-sync local items from model (server may have updated extra fields)
        this.items = this.purchaseOrder.items;
      }

      // Save custom column values (per-variant, independent of PO status)
      try {
        await this.saveCustomColumnValues();
      } catch (customErr: any) {
        console.error('Error saving custom column values:', customErr);
        EventBus.$emit('show-snackbar', 'PO saved, but failed to save custom column values', 'warning');
        // PO itself was saved — exit edit mode, leave dirty set for retry.
      }

      this.isEditing = false;
      EventBus.$emit('show-snackbar', 'Purchase order updated successfully', 'success');
      
      // Update location if needed
      if (this.purchaseOrder.locationId) {
        this.location = this.locations.find(loc => loc.id === this.purchaseOrder?.locationId) || null;
      } else {
        this.location = null;
      }
      
      // Update supplier if changed
      if (this.editedPurchaseOrder.supplierId !== undefined && this.purchaseOrder.supplierId !== this.editedPurchaseOrder.supplierId) {
        if (this.editedPurchaseOrder.supplierId) {
          this.supplier = await SupplierModel.getById(this.editedPurchaseOrder.supplierId);
        } else {
          this.supplier = null;
        }
      }
    } catch (error) {
      EventBus.$emit('show-snackbar', 'Failed to update purchase order', 'error');
      console.error('Error updating purchase order:', error);
    } finally {
      this.saving = false;
    }
  }

  private async fetchResolvedAddresses() {
    if (!this.purchaseOrder?.id) return;
    try {
      const response: any = await get(`/purchase_orders/${this.purchaseOrder.id}/addresses`);
      this.resolvedBillTo = response.billTo || null;
      this.billToSource = response.billToSource || 'default';
      this.resolvedShipTo = response.shipTo || null;
      this.shipToSource = response.shipToSource || 'default';
    } catch (error: any) {
      console.warn('Failed to fetch resolved addresses:', error.message);
    }
  }

  private openAddressDialog(type: 'bill_to' | 'ship_to') {
    if (!this.purchaseOrder) return;
    this.editingAddressType = type;
    const resolved = type === 'bill_to' ? this.resolvedBillTo : this.resolvedShipTo;
    this.editedAddress = resolved ? { ...resolved } : { name: '', address1: '', address2: '', city: '', province: '', zip: '', country: '', phone: '' };
    this.showAddressDialog = true;
    this.fetchAddressSuggestions(type);
  }

  private async fetchAddressSuggestions(type: 'bill_to' | 'ship_to') {
    this.addressSuggestions = [];
    this.loadingAddressSuggestions = true;
    try {
      const response: any = await get(`/purchase_orders/addresses/suggestions?type=${type}`);
      this.addressSuggestions = Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.warn('Failed to fetch address suggestions:', error.message);
    } finally {
      this.loadingAddressSuggestions = false;
    }
  }

  private onAddressSuggestionSelected(addr: Record<string, string> | null) {
    if (!addr) return;
    this.editedAddress = {
      name: addr.name || '',
      address1: addr.address1 || '',
      address2: addr.address2 || '',
      city: addr.city || '',
      province: addr.province || '',
      zip: addr.zip || '',
      country: addr.country || '',
      phone: addr.phone || '',
    };
  }

  private async saveAddress() {
    if (!this.purchaseOrder) return;
    this.savingAddress = true;
    try {
      const extra = {
        ...this.purchaseOrder.extra,
        [this.editingAddressType]: { ...this.editedAddress }
      };
      await put(`/purchase_orders/${this.purchaseOrder.id}`, { extra });
      this.purchaseOrder.extra = extra;
      this.showAddressDialog = false;
      await this.fetchResolvedAddresses();
      EventBus.$emit('show-snackbar', `${this.editingAddressType === 'bill_to' ? 'Bill To' : 'Ship To'} updated successfully`, 'success');
    } catch (error) {
      EventBus.$emit('show-snackbar', 'Failed to save address', 'error');
      console.error('Error saving address:', error);
    } finally {
      this.savingAddress = false;
    }
  }

  private clearAddress(type: 'bill_to' | 'ship_to') {
    this.resetAddressType = type;
    this.showResetAddressDialog = true;
  }

  private async confirmClearAddress() {
    if (!this.purchaseOrder) return;
    this.resettingAddress = true;
    try {
      const extra = {
        ...this.purchaseOrder.extra,
        [this.resetAddressType]: null
      };
      await put(`/purchase_orders/${this.purchaseOrder.id}`, { extra });
      this.purchaseOrder.extra = extra;
      this.showResetAddressDialog = false;
      await this.fetchResolvedAddresses();
      const label = this.resetAddressType === 'bill_to' ? 'Bill To' : 'Ship To';
      EventBus.$emit('show-snackbar', `${label} cleared — will use default`, 'success');
    } catch (error) {
      EventBus.$emit('show-snackbar', 'Failed to clear address', 'error');
      console.error('Error clearing address:', error);
    } finally {
      this.resettingAddress = false;
    }
  }

  private onFinancialSaved(data: { totalAmount: number; extra: any; items?: any[] }) {
    if (!this.purchaseOrder) return;
    this.purchaseOrder.totalAmount = data.totalAmount;
    this.purchaseOrder.extra = data.extra;
    if (data.items) {
      this.items = data.items;
      this.purchaseOrder.items = data.items;
    }
  }

  private cancelEdit() {
    this.isEditing = false;
    this.dirtyCustomColumnVariants.clear();
    this.fetchData(); // Reset to original data (also re-fetches custom column values)
  }

  private async confirmSaveChanges() {
    await this.savePurchaseOrder();
    this.showSaveChangesDialog = false;
  }

  private confirmDiscardChanges() {
    this.showDiscardChangesDialog = false;
    this.cancelEdit();
  }

  private async loadImageAsBase64(url: string): Promise<string | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/jpeg');
          resolve(dataURL);
        } else {
          resolve(null);
        }
      };
      img.onerror = function() {
        resolve(null);
      };
      img.src = url;
    });
  }

  private validateEmailForm() {
    if (!this.$refs.emailForm || !this.purchaseOrder) return;

    const form = this.$refs.emailForm as Vue & { validate(): boolean };
    return form.validate();
  }

  private async sendEmail() {
    if (!this.$refs.emailForm || !this.purchaseOrder) return;

    // Validate form
    const form = this.$refs.emailForm as Vue & { validate(): boolean };
    if (!form.validate()) return;

    this.sending = true;
    try {
      // Save preferences before sending
      this.saveEmailPreferences();
      this.saveSavedEmails();

      await this.purchaseOrder.sendToSupplier({
        emailSubject: this.emailSubject,
        recipients: this.emailRecipients,
        ccRecipients: uniqueCcRecipients(this.emailRecipients, this.ccRecipients),
        customMessage: this.emailMessage,
        attachmentFormat: this.attachmentFormat,
        includePricing: this.includePricing,
        groupByProduct: this.groupByProduct,
        includeSimplifiedPdf: this.includeSimplifiedPdf,
        replyTo: this.replyToEmails.length > 0 ? this.replyToEmails : undefined,
        companyName: this.companyName || undefined,
        templateId: this.sendTemplateId || undefined  // US-069: column template for the attachment
      });
      
      this.showEmailDialog = false;
      EventBus.$emit('show-snackbar', 'Purchase order sent successfully!', 'success');
      
      // Refresh data to get updated status
      await this.fetchData();
    } catch (error) {
      if (error instanceof PoEmailRateLimitError) {
        // Per-merchant rate limit (AS-9905) — leave the dialog open so the
        // user can adjust recipients or retry, and surface a specific message
        // about which window tripped. show-snackbar listener in App.vue
        // expects a single `{ message, color }` payload — a positional
        // second arg is silently dropped and the toast falls back to the
        // default color.
        EventBus.$emit('show-snackbar', {
          message: poEmailRateLimitMessage(error),
          color: 'warning',
        });
      } else {
        EventBus.$emit('show-snackbar', {
          message: 'Failed to send purchase order',
          color: 'error',
        });
      }
      console.error('Error sending purchase order:', error);
    } finally {
      this.sending = false;
    }
  }

    private async exportPurchaseOrder(): Promise<void> {
    if (!this.purchaseOrder) return;

    this.exporting = true;

    try {
      const token = getIdentityToken();

      // Prepare headers conditionally
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // US-069: export uses the template chosen in the dialog (default = master).
      // We send templateId (backend resolves columns) and also optionalColumns
      // derived from that template so the default-template path stays correct.
      const exportTemplate = resolveTemplate(this.columnTemplates, this.exportTemplateId);
      const enabledColumns: string[] = exportTemplate
        ? enabledColumnIds(exportTemplate)
        : (this.columnConfig && this.columnConfig.columns
            ? Object.entries(this.columnConfig.columns).filter(([, c]) => c.enabled).map(([id]) => id)
            : []);
      // Send the resolved template id so templateId and optionalColumns always
      // describe the same template (a stale id resolves to the default).
      const resolvedTemplateId = exportTemplate ? exportTemplate.id : this.exportTemplateId;

      // Make authenticated GET request to the new export endpoint
      const response = await ax.get(`/purchase_orders/${this.purchaseOrder.id}/export`, {
        params: {
          format: this.exportFormat,
          includePricing: this.exportIncludePricing,
          groupByProduct: this.exportGroupByProduct,
          templateId: resolvedTemplateId || undefined,
          optionalColumns: enabledColumns.length > 0 ? enabledColumns.join(',') : undefined
        },
        responseType: 'blob',
        headers
      });

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `PO-${this.purchaseOrder.poNumber}.${this.exportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);

      // Close dialog
      this.showExportDialog = false;

      // Show success message
      EventBus.$emit('show-snackbar', 'Export completed successfully.', 'success');
    } catch (error: any) {
      console.error('Export error:', error);
      
      // Handle error message extraction from blob response
      let errorMessage = 'Export failed. Please try again.';
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // If parsing fails, use default message
          console.error('Failed to parse error response:', parseError);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      EventBus.$emit('show-snackbar', errorMessage, 'error');
    } finally {
      this.exporting = false;
    }
  }


  private removeRecipient(index: number) {
    this.emailRecipients.splice(index, 1);
  }

  private removeCcRecipient(index: number) {
    this.ccRecipients.splice(index, 1);
  }

  private removeReplyToEmail(index: number) {
    this.replyToEmails.splice(index, 1);
  }

  private validateEmail(email: string): boolean {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  get canEditPurchaseOrder(): boolean {
    if (!this.purchaseOrder) return false;

    // Only allow editing if status is draft
    return this.purchaseOrder.status === 'draft';
  }

  get canEditAddresses(): boolean {
    if (!this.purchaseOrder) return false;
    return ['draft', 'pending_approval'].includes(this.purchaseOrder.status);
  }

  get canManagePurchaseOrders(): boolean {
    const validateValues = [
      {
        code: 'purchase_order',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  getStatusColor(status: string | undefined): string {
    if (!status) return 'grey';

    switch (status) {
      case 'draft':
        return 'grey';
      case 'confirmed':
        return 'purple';
      case 'partial_received':
        return 'orange';
      case 'received':
        return 'green';
      case 'completed':
        return 'teal';
      case 'canceled':
        return 'red';
      default:
        return 'grey';
    }
  }

  getStatusIcon(status: string | undefined): string {
    if (!status) return 'mdi-help-circle-outline';

    switch (status) {
      case 'draft':
        return 'mdi-file-document-edit-outline';
      case 'confirmed':
        return 'mdi-check-circle-outline';
      case 'partial_received':
        return 'mdi-package-variant';
      case 'received':
        return 'mdi-package-check';
      case 'completed':
        return 'mdi-check-circle';
      case 'canceled':
        return 'mdi-cancel';
      default:
        return 'mdi-help-circle-outline';
    }
  }

  formatStatus(status: string | undefined): string {
    if (!status) return '';

    switch (status) {
      case 'draft':
        return 'Draft';
      case 'confirmed':
        return 'Confirmed';
      case 'partial_received':
        return 'Partially Received';
      case 'received':
        return 'Received';
      case 'completed':
        return 'Completed';
      case 'canceled':
        return 'Canceled';
      default:
        return status;
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = date.toLocaleString('default', { month: 'long' });
    const day = date.getDate();
    const year = date.getFullYear();
    
    // Add ordinal suffix to day
    const suffix = ['th', 'st', 'nd', 'rd'][(day % 10 > 3 ? 0 : day % 10)] || 'th';
    
    return `${month} ${day}${suffix}, ${year}`;
  }

  formatCurrency(value: number | undefined): string {
    return CurrencyHelper.formatCurrency(value);
  }

  formatCurrencyFor(value: number | undefined | null): string {
    return CurrencyHelper.formatCurrencyWithCode(value as any, this.poCurrencyCode);
  }

  formatCurrencyStore(value: number | undefined | null): string {
    return CurrencyHelper.formatCurrencyWithCode(value as any, this.storeCurrency);
  }

  getCurrencySymbol(code: string): string {
    return CurrencyHelper.getCurrencySymbol(code);
  }

  // US-092. The input is the supplier's clean unit price, so it is denominated in
  // the PO's currency. The store-currency figure the merchant cares about — the
  // landed cost Shopify receives — is shown as a derived hint underneath.
  get receiveCostPrefix(): string {
    return CurrencyHelper.getCurrencySymbol(this.poCurrencyCode);
  }

  // Supplier -> store multiplier for this PO, locked when the PO was confirmed.
  // Without a usable rate the derived figure would be a supplier-currency
  // amount labelled as store currency, so the hint is hidden instead.
  get canShowReceiveLandedCost(): boolean {
    return hasUsableSupplierToStoreRate(
      this.poCurrencyCode,
      this.storeCurrency,
      this.purchaseOrder?.exchangeRate,
    );
  }

  get receiveSupplierToStoreRate(): number {
    return supplierToStoreRate(
      this.poCurrencyCode,
      this.storeCurrency,
      this.purchaseOrder?.exchangeRate,
    );
  }

  // What Shopify will actually be sent for this line, given what has been typed.
  private getReceiveLandedCost(item: any): number {
    return landedCostForEnteredCost(
      this.receiveCosts[item.id],
      this.receiveSupplierToStoreRate,
      item?.extra?.landed_cost_components,
    );
  }

  // Current SupplierProduct.cost, so the merchant sees the value about to be
  // replaced before confirming. Null when the variant has no link yet.
  private showsSupplierCostReplacement(item: any): boolean {
    if (!this.updateSupplierCost) { return false; }
    return shouldShowSupplierCostReplacement(
      this.receiveCosts[item.id],
      this.getCurrentSupplierCost(item),
    );
  }

  private getCurrentSupplierCost(item: any): number | null {
    if (item?.variantId == null) { return null; }
    const cost = this.supplierCostMap.get(Number(item.variantId));
    return cost == null ? null : Number(cost);
  }

  getLineTotalBase(item: any): number | null {
    const unitPriceBase = item.extra?.unit_price_base;
    if (unitPriceBase == null) return null;
    const taxBase = item.extra?.tax_amount_base || 0;
    return (unitPriceBase * (item.orderedQuantity || 0)) + taxBase;
  }

  get storeCurrency(): string {
    return CurrencyHelper.getStoreCurrency();
  }

  get isForeignCurrencyPO(): boolean {
    return this.purchaseOrder?.isForeignCurrency ?? false;
  }

  get poCurrencyCode(): string {
    return this.purchaseOrder?.currencyCode || this.storeCurrency;
  }

  get receiveInventoryCategorisedError(): {
    color: string;
    label: string;
    customerMessage: string;
    supportDetail: string | null;
    attemptCount: number;
    locationName: string;
    failedItems: FailedItemRow[];
  } | null {
    return this.buildCategorisedError(
      this.selectedReceive?.shopifyInventorySyncMetadata,
      'inventory'
    );
  }

  get receiveCostCategorisedError(): {
    color: string;
    label: string;
    customerMessage: string;
    supportDetail: string | null;
    attemptCount: number;
    locationName: string;
    failedItems: FailedItemRow[];
  } | null {
    return this.buildCategorisedError(
      this.selectedReceive?.shopifyCostSyncMetadata,
      'cost'
    );
  }

  get errorLogSections(): Array<{ prefix: string; categorisedError: ReturnType<PurchaseOrderDetail['buildCategorisedError']> }> {
    const inv = this.receiveInventoryCategorisedError;
    const cost = this.receiveCostCategorisedError;
    const out: Array<{ prefix: string; categorisedError: any }> = [];
    if (inv) out.push({ prefix: 'Inventory', categorisedError: inv });
    if (cost) out.push({ prefix: 'Cost', categorisedError: cost });
    return out;
  }

  private buildCategorisedError(metadata: any, syncType: 'inventory' | 'cost') {
    if (!metadata) return null;
    if (metadata.status !== 'failed' && metadata.status !== 'partial') return null;

    // Prefer the classified plain-English message. Never fall back to
    // metadata.error — for parent records that string is a JSON blob of the
    // failure array, which we don't want users to see.
    const customerMessage = metadata.customerMessage || syncFailureFallbackMessage(metadata.status);

    const receiveItems = this.selectedReceive?.items || [];
    const poItems = this.purchaseOrder?.items || [];
    const locationId = this.selectedReceive?.locationId;

    return {
      color: getCategoryChipColor(metadata.errorCategory),
      label: formatCategoryLabel(metadata.errorCategory),
      customerMessage,
      supportDetail: metadata.supportDetail || null,
      attemptCount: metadata.attemptCount || 0,
      locationName: resolveLocationName(locationId, this.locations),
      failedItems: buildFailedItemRows(receiveItems, syncType, poItems as any),
    };
  }

  // Expose helpers to the template
  private getCategoryChipColor = getCategoryChipColor;
  private formatCategoryLabel = formatCategoryLabel;
  private canRelinkSyncFailure = canRelinkSyncFailure;
  private syncFailureFallbackMessage = syncFailureFallbackMessage;

  /** AS-11687: name a line the last Retry Sync skipped, like the failed-items list does. */
  private retrySkippedLineLabel(line: SkippedRetryLine): string {
    const item = (this.selectedReceive?.items || []).find((i: any) => Number(i?.id) === line.receivedItemId);
    if (!item) return line.sku ? `SKU ${line.sku}` : `Line ${line.receivedItemId}`;
    return resolveItemLabel(item as any, (this.purchaseOrder?.items || []) as any);
  }

  /** Template-facing wrapper that binds the component's locations list. */
  private resolveLocationNameFor(locationId: any): string {
    return resolveLocationName(locationId, this.locations);
  }

  /** Returns-panel failed items (mirrors receiveInventoryCategorisedError.failedItems). */
  get returnInventoryFailedItems(): FailedItemRow[] {
    const selected: any = this.selectedReturn;
    if (!selected) return [];
    const items = selected.items || [];
    const poItems = this.purchaseOrder?.items || [];
    return buildFailedItemRows(items, 'inventory', poItems as any);
  }

  get returnLocationName(): string {
    return resolveLocationName(this.selectedReturn?.locationId, this.locations);
  }

  isRefreshingRate = false;
  isEditingRate = false;
  editedRate: number | null = null;
  isSavingRate = false;

  isEditingNotes = false;
  editingNotesValue = '';
  savingNotes = false;
  isEditingInternalNotes = false;
  editingInternalNotesValue = '';
  savingInternalNotes = false;
  isEditingExpectedDelivery = false;
  editingExpectedDeliveryValue = '';
  savingExpectedDelivery = false;
  showExpectedDeliveryPicker = false;
  isEditingDeparture = false;
  editingDepartureValue = '';
  savingDeparture = false;
  showDeparturePicker = false;

  get isExchangeRateTerminal(): boolean {
    if (!this.purchaseOrder?.currencyCode) return true;
    return this.purchaseOrder.isTerminalStatus();
  }

  get canEditExchangeRate(): boolean {
    return !this.isExchangeRateTerminal && !this.purchaseOrder?.exchangeRateLocked;
  }

  get canInlineEditMetadata(): boolean {
    if (!this.purchaseOrder) return false;
    if (this.isEditing) return false;
    return this.purchaseOrder.canEditMetadata();
  }

  private ensureFinancial(): IPurchaseOrderFinancial {
    if (!this.purchaseOrder!.extra) this.purchaseOrder!.extra = {};
    if (!this.purchaseOrder!.extra.financial) this.purchaseOrder!.extra.financial = {};
    return this.purchaseOrder!.extra.financial;
  }

  // Targeted PUT for extra-only updates — save() sends the full PO which gets rejected for non-draft statuses
  private async saveExtraToServer(): Promise<void> {
    const response: any = await put(`/purchase_orders/${this.purchaseOrder!.id}`, {
      extra: this.purchaseOrder!.extra
    });
    if (response?.data) {
      this.purchaseOrder!.mapData(response.data);
      this.items = this.purchaseOrder!.items || [];
    }
  }

  private snapshotFinancial(): any {
    return JSON.parse(JSON.stringify(this.purchaseOrder?.extra?.financial || {}));
  }

  private restoreFinancial(snapshot: any) {
    if (this.purchaseOrder?.extra) {
      this.purchaseOrder.extra.financial = snapshot;
    }
  }

  async onExchangeRateChange(payload: { exchange_rate: number; exchange_rate_manual: boolean }) {
    if (!this.purchaseOrder) return;
    const snapshot = this.snapshotFinancial();
    const f = this.ensureFinancial();
    f.exchange_rate = payload.exchange_rate;
    f.exchange_rate_manual = payload.exchange_rate_manual;
    f.exchange_rate_source = null;
    f.exchange_rate_fetched_at = null;
    try {
      await this.saveExtraToServer();
    } catch (err) {
      this.restoreFinancial(snapshot);
      console.error('Error saving exchange rate:', err);
      throw err;
    }
  }

  startEditingRate() {
    this.editedRate = Number(this.purchaseOrder?.exchangeRate) || null;
    this.isEditingRate = true;
  }

  cancelEditingRate() {
    this.isEditingRate = false;
    this.editedRate = null;
  }

  async saveEditedRate() {
    const num = Number(this.editedRate);
    if (!num || num <= 0 || !isFinite(num)) {
      EventBus.$emit('show-snackbar', { message: 'Please enter a valid exchange rate greater than zero', color: 'warning' });
      return;
    }
    this.isSavingRate = true;
    try {
      await this.onExchangeRateChange({ exchange_rate: num, exchange_rate_manual: true });
      this.isEditingRate = false;
      this.editedRate = null;
    } catch (err) {
      console.error('Error saving exchange rate:', err);
      EventBus.$emit('show-snackbar', { message: 'Failed to update exchange rate', color: 'error' });
    } finally {
      this.isSavingRate = false;
    }
  }

  private toDateInputValue(raw: string | undefined | null): string {
    return raw ? new Date(raw).toISOString().split('T')[0] : '';
  }

  startEditingNotes() {
    this.editingNotesValue = this.purchaseOrder?.notes || '';
    this.isEditingNotes = true;
  }

  cancelEditingNotes() {
    this.isEditingNotes = false;
  }

  async saveInlineNotes() {
    this.savingNotes = true;
    const previousValue = this.purchaseOrder!.notes;
    try {
      const response: any = await put(`/purchase_orders/${this.purchaseOrder!.id}`, {
        notes: this.editingNotesValue
      });
      if (response?.data) {
        this.purchaseOrder!.notes = response.data.notes;
        this.purchaseOrder!.updatedAt = response.data.updatedAt;
      }
      this.isEditingNotes = false;
    } catch (e: any) {
      this.purchaseOrder!.notes = previousValue;
      EventBus.$emit('show-snackbar', {
        message: e?.response?.data?.message || 'Failed to save notes',
        color: 'error'
      });
    } finally {
      this.savingNotes = false;
    }
  }

  startEditingInternalNotes() {
    this.editingInternalNotesValue = this.purchaseOrder?.extra?.internalNotes || '';
    this.isEditingInternalNotes = true;
  }

  cancelEditingInternalNotes() {
    this.isEditingInternalNotes = false;
    this.editingInternalNotesValue = '';
  }

  async saveInlineInternalNotes() {
    this.savingInternalNotes = true;
    try {
      const extra = {
        ...(this.purchaseOrder!.extra || {}),
        internalNotes: this.editingInternalNotesValue?.trim() || undefined,
      };
      const response: any = await put(`/purchase_orders/${this.purchaseOrder!.id}`, { extra });
      this.purchaseOrder!.extra = extra;
      if (response?.data?.updatedAt) {
        this.purchaseOrder!.updatedAt = response.data.updatedAt;
      }
      // Sync the draft edit buffer so a subsequent page-level save doesn't overwrite with stale data
      this.internalNotesEditValue = this.purchaseOrder?.extra?.internalNotes || '';
      this.isEditingInternalNotes = false;
    } catch (e: any) {
      EventBus.$emit('show-snackbar', {
        message: e?.response?.data?.message || 'Failed to save internal notes',
        color: 'error'
      });
    } finally {
      this.savingInternalNotes = false;
    }
  }

  startEditingExpectedDelivery() {
    this.editingExpectedDeliveryValue = this.toDateInputValue(this.purchaseOrder?.expectedDeliveryDate);
    this.isEditingExpectedDelivery = true;
  }

  cancelEditingExpectedDelivery() {
    this.isEditingExpectedDelivery = false;
    this.showExpectedDeliveryPicker = false;
  }

  async saveInlineExpectedDelivery() {
    this.savingExpectedDelivery = true;
    const previousValue = this.purchaseOrder!.expectedDeliveryDate;
    try {
      const response: any = await put(`/purchase_orders/${this.purchaseOrder!.id}`, {
        expectedDeliveryDate: this.editingExpectedDeliveryValue || null
      });
      if (response?.data) {
        this.purchaseOrder!.expectedDeliveryDate = response.data.expectedDeliveryDate;
        this.purchaseOrder!.updatedAt = response.data.updatedAt;
      }
      this.isEditingExpectedDelivery = false;
      this.showExpectedDeliveryPicker = false;
    } catch (e: any) {
      this.purchaseOrder!.expectedDeliveryDate = previousValue;
      EventBus.$emit('show-snackbar', {
        message: e?.response?.data?.message || 'Failed to save expected delivery date',
        color: 'error'
      });
    } finally {
      this.savingExpectedDelivery = false;
    }
  }

  startEditingDeparture() {
    this.editingDepartureValue = this.toDateInputValue(this.purchaseOrder?.departureDate);
    this.isEditingDeparture = true;
  }

  cancelEditingDeparture() {
    this.isEditingDeparture = false;
    this.showDeparturePicker = false;
  }

  async saveInlineDeparture() {
    this.savingDeparture = true;
    const previousValue = this.purchaseOrder!.departureDate;
    try {
      this.purchaseOrder!.departureDate = this.editingDepartureValue || undefined;
      await this.saveExtraToServer();
      this.isEditingDeparture = false;
      this.showDeparturePicker = false;
    } catch (e: any) {
      this.purchaseOrder!.departureDate = previousValue;
      EventBus.$emit('show-snackbar', {
        message: e?.response?.data?.message || 'Failed to save departure date',
        color: 'error'
      });
    } finally {
      this.savingDeparture = false;
    }
  }

  async onRefreshExchangeRate() {
    if (!this.purchaseOrder?.currencyCode) return;
    this.isRefreshingRate = true;
    const snapshot = this.snapshotFinancial();
    try {
      const response: any = await get(`/exchange-rates?from=${this.purchaseOrder.currencyCode}&to=${this.storeCurrency}&refresh=true`);
      if (response?.rate) {
        const f = this.ensureFinancial();
        f.exchange_rate = response.rate;
        f.exchange_rate_source = response.from ? `${response.from}→${response.to}` : null;
        f.exchange_rate_fetched_at = response.timestamp || new Date().toISOString();
        f.exchange_rate_manual = false;
        await this.saveExtraToServer();
      }
    } catch (err) {
      this.restoreFinancial(snapshot);
      console.error('Error refreshing exchange rate:', err);
    } finally {
      this.isRefreshingRate = false;
    }
  }

  async onUnlockExchangeRate() {
    if (!this.purchaseOrder) return;
    const snapshot = this.snapshotFinancial();
    const f = this.ensureFinancial();
    f.exchange_rate_locked = false;
    f.exchange_rate_locked_at = null;
    try {
      await this.saveExtraToServer();
    } catch (err) {
      this.restoreFinancial(snapshot);
      console.error('Error unlocking exchange rate:', err);
    }
  }

  // NEW: Helper methods for cost status display in import summary
  getCostUpdateStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'updated': 'success',
      'partial': 'warning', 
      'failed': 'error',
      'skipped': 'grey'
    };
    return colorMap[status] || 'grey';
  }

  getCostUpdateStatusText(status: string): string {
    const textMap: Record<string, string> = {
      'updated': 'Updated',
      'partial': 'Partially Updated',
      'failed': 'Failed', 
      'skipped': 'Skipped'
    };
    return textMap[status] || 'Unknown';
  }

  getCostMethodText(method: string): string {
    const methodMap: Record<string, string> = {
      'latest': 'Latest Cost',
      'average': 'Weighted Average'
    };
    return methodMap[method] || method;
  }

  get canReceiveItems(): boolean {
    // Can only receive items before fully received (use Return Items for received POs)
    return (
      this.purchaseOrder?.status === 'confirmed' ||
      this.purchaseOrder?.status === 'partial_received'
    );
  }

  get canMarkAsCompleted(): boolean {
    // AS-11374: the header Complete button / More item. Offered on a
    // partial_received PO with lines still outstanding, which is the write-off
    // case (supplier short-shipped). AS-10661 had narrowed this to "every item
    // received", which left such POs stuck. The confirmation dialog warns with
    // the outstanding count. The status rule is the model's, so the view and
    // the model cannot disagree.
    return canOfferCompletion(
      !!this.purchaseOrder?.canMarkAsCompleted(),
      this.purchaseOrder?.items,
    );
  }

  get nothingLeftToReceive(): boolean {
    // The Receive modal's "Nothing left to receive?" callout keeps the narrow
    // AS-10661 gate: its copy is only true once every line is in.
    return isNothingLeftToReceive(
      !!this.purchaseOrder?.canMarkAsCompleted(),
      this.purchaseOrder?.items,
    );
  }

  get outstandingItemsCount(): number {
    return countOutstandingLines(this.purchaseOrder?.items);
  }

  get completeWriteOffWarning(): string {
    return writeOffWarning(this.outstandingItemsCount);
  }

  // AS-11374: Receive & Complete writes off whatever this receive leaves short.
  // Manual entry only: in CSV import the quantities are not known until import.
  get outstandingAfterReceiveCount(): number {
    if (this.receiveMode === 'import') return 0;
    return countOutstandingAfterReceive(this.purchaseOrder?.items, this.receiveQuantities);
  }

  get receiveCompleteWriteOffWarning(): string {
    return writeOffWarning(this.outstandingAfterReceiveCount);
  }

  get canExportLabels(): boolean {
    // Barcode labels cover ordered items on a confirmed PO (pre-print before
    // goods arrive) and received items once receiving has started; the export
    // stays available through completed (completed POs always have received
    // items). The backend picks ordered-vs-received quantity from PO status.
    return (
      this.purchaseOrder?.status === 'confirmed' ||
      this.purchaseOrder?.status === 'partial_received' ||
      this.purchaseOrder?.status === 'received' ||
      this.purchaseOrder?.status === 'completed'
    );
  }

  get barcodeLabelItemsNoun(): string {
    // Confirmed POs export the ordered quantity (nothing received yet); every
    // other eligible status exports received items.
    return this.purchaseOrder?.status === 'confirmed' ? 'ordered' : 'received';
  }

  get canCancelPO(): boolean {
    // Can only cancel before receiving starts
    return (
      this.purchaseOrder?.status === 'draft' ||
      this.purchaseOrder?.status === 'confirmed'
    );
  }

  get canRevertToDraft(): boolean {
    // Can only revert to draft from confirmed status (before receiving starts)
    return this.purchaseOrder?.status === 'confirmed';
  }

  // US-088: 'completed' is a locked status — nothing on it can be edited or
  // adjusted. Re-opening moves the PO back to 'received' (the one transition
  // the backend allows out of 'completed'), where the ordinary non-terminal
  // rules apply again. The rule itself lives on the model, next to the other
  // capability checks and the terminal-status guard it has to agree with.
  get canReopen(): boolean {
    return this.purchaseOrder?.canReopen() ?? false;
  }

  get hasStatusActions(): boolean {
    // Always true since Duplicate PO is always available
    return true;
  }

  /** AS-10987 — true while the variant-image reload is in flight. */
  private reloadingImages = false;

  /**
   * Expose the shared usable-image rule to the template (AS-10987).
   *
   * The PO detail page is the screen the bug was reported on (PO 139475) and the
   * screen this action now lives on, so its three image cells need the guard as
   * much as PurchaseOrderDisplay did. Without it a merchant clicks Reload images
   * and the '-' rows stay broken, which reads as the button not working.
   */
  private isUsableImageUrl = isUsableImageUrl;

  get hasSecondaryActions(): boolean {
    // Check if there are secondary actions (shown in "More" menu).
    //
    // AS-10987: delegated to a pure helper so it can be tested for real —
    // vue-shim stubs .vue imports, so a getter here is only reachable as source
    // text. The helper also carries the overflow-only actions, which is what
    // makes the menu render on a PO with fewer than four actions (a completed PO
    // has three, a cancelled one has one) where `moreMenuButtons` returns [] and
    // this getter used to be false, so the v-menu was never created at all.
    return computeHasSecondaryActions({
      canAddProducts: this.canAddProducts,
      canImportItems: this.canImportItems,
      moreMenuButtonCount: this.moreMenuButtons.length,
    });
  }

  /**
   * Actions that live in the overflow menu regardless of the collapse
   * heuristic. Deliberately SEPARATE from `moreMenuButtons`: that getter and
   * `shouldCollapseButtons` / `inlineButtons` are left exactly as they were, so
   * existing collapse behaviour cannot regress (AS-10987 AC 6).
   */
  get overflowOnlyButtons(): readonly string[] {
    return OVERFLOW_ONLY_ACTIONS;
  }

  // Count of status-dependent action buttons (including Duplicate which is always available)
  get actionButtonCount(): number {
    let count = 1; // Duplicate PO is always available
    if (this.canConfirmPurchaseOrder) count++;
    if (this.canEditPurchaseOrder && !this.isEditing) count++;
    if (this.canReceiveItems) count++;
    if (this.canReturnItems) count++;
    if (this.canAdjustQuantity) count++;
    if (this.canApplyLineTax) count++;
    if (this.canMarkAsCompleted) count++;
    if (this.canRevertToDraft) count++;
    if (this.canReopen) count++;
    if (this.canCancelPO) count++;
    return count;
  }

  // Should we collapse buttons into More menu? (4+ buttons = show 2 + More)
  get shouldCollapseButtons(): boolean {
    return this.actionButtonCount >= 4;
  }

  // Buttons to show inline (all if <= 3, or top 2 priority if >= 4)
  get inlineButtons(): string[] {
    const allButtons: string[] = [];
    if (this.canConfirmPurchaseOrder) allButtons.push('confirm');
    if (this.canEditPurchaseOrder && !this.isEditing) allButtons.push('edit');
    if (this.canReceiveItems) allButtons.push('receive');
    if (this.canReturnItems) allButtons.push('return');
    if (this.canAdjustQuantity) allButtons.push('adjust');
    if (this.canFinaliseCosts) allButtons.push('finalise-costs');
    if (this.canApplyLineTax) allButtons.push('applyTax');
    if (this.canMarkAsCompleted) allButtons.push('complete');
    if (this.canRevertToDraft) allButtons.push('revert');
    if (this.canReopen) allButtons.push('reopen');
    if (this.canCancelPO) allButtons.push('cancel');
    allButtons.push('duplicate'); // Always available

    if (!this.shouldCollapseButtons) {
      return allButtons; // Show all buttons inline
    }

    // When collapsed, show top 2 priority buttons
    const priority: string[] = [];
    // First priority: main action for current status
    if (this.canConfirmPurchaseOrder) priority.push('confirm');
    else if (this.canReceiveItems) priority.push('receive');
    else if (this.canMarkAsCompleted) priority.push('complete');
    // Second priority
    if (this.canReturnItems && priority.length < 2) priority.push('return');
    if (this.canEditPurchaseOrder && !this.isEditing && priority.length < 2) priority.push('edit');
    if (this.canRevertToDraft && priority.length < 2) priority.push('revert');
    // Unreachable today (a completed PO has at most 3 actions, so this list
    // never collapses) — kept so every action is registered the same way and
    // Re-open can't go missing if a completed PO ever gains a 4th action.
    if (this.canReopen && priority.length < 2) priority.push('reopen');
    if (priority.length < 2) priority.push('duplicate');
    return priority;
  }

  // Buttons to show in More menu (only when collapsed)
  get moreMenuButtons(): string[] {
    if (!this.shouldCollapseButtons) return [];
    const allButtons: string[] = [];
    if (this.canConfirmPurchaseOrder) allButtons.push('confirm');
    if (this.canEditPurchaseOrder && !this.isEditing) allButtons.push('edit');
    if (this.canReceiveItems) allButtons.push('receive');
    if (this.canReturnItems) allButtons.push('return');
    if (this.canAdjustQuantity) allButtons.push('adjust');
    if (this.canFinaliseCosts) allButtons.push('finalise-costs');
    if (this.canApplyLineTax) allButtons.push('applyTax');
    if (this.canMarkAsCompleted) allButtons.push('complete');
    if (this.canRevertToDraft) allButtons.push('revert');
    if (this.canReopen) allButtons.push('reopen');
    if (this.canCancelPO) allButtons.push('cancel');
    allButtons.push('duplicate');
    return allButtons.filter(btn => !this.inlineButtons.includes(btn));
  }

  /**
   * AS-10987 — re-resolve the line items' variant images.
   *
   * `variant_image` is a snapshot frozen at PO-creation time (AS-10982), so when
   * it was written empty there is no other way to repair it.
   *
   * Always `?force=true`. Without it the bot only re-resolves lines whose stored
   * URL is syntactically unusable (`isUsableImageUrl`), so a URL that has gone
   * 404 on Shopify's CDN — the merchant replaced or deleted the file — is never
   * a candidate, and the data warehouse keeps handing back the same dead URL
   * anyway. force makes the bot ask Shopify first (`preferShopify`). Clicking
   * this is a deliberate repair, so the extra Shopify calls are the right trade.
   */
  private async reloadVariantImages(): Promise<void> {
    if (!this.purchaseOrder?.id) return;
    // Re-entry guard. The item is also disabled while this is set, but a click
    // landing before the re-render would still get through, and the backend has
    // no idempotency key — a second call re-runs the whole OLAP + Shopify
    // fan-out.
    if (this.reloadingImages) return;

    this.reloadingImages = true;
    // Compare against the ROUTE id, not `this.purchaseOrder.id`. On a route
    // change `purchaseOrder` still holds the previous PO until the @Watch('id')
    // fetch resolves, so a `purchaseOrder.id` check passes during that window and
    // this would race the watcher's own fetchData() — reporting the old PO's
    // counts over the new one. `fetchData` itself keys off Number(this.id).
    const requestPoId = Number(this.id);
    // Vuetify closes the v-menu on item click, so the disabled state and the
    // "Reloading images…" label are off-screen for the whole request — and
    // AS-10983 measured this call at up to ~11.7s on a large PO. Without an
    // immediate message the merchant clicks and sees nothing at all.
    EventBus.$emit('show-snackbar', { message: 'Reloading product images…', color: 'info' });
    try {
      const response: any = await post(`/purchase_orders/${requestPoId}/reload-images?force=true`);
      // The user may have navigated to another PO while this was outstanding.
      // Reporting these counts over a different PO is worse than saying nothing.
      if (Number(this.id) !== requestPoId) return;

      const result = response?.data as ReloadImagesResult | undefined;
      // fetchData swallows its own failures and emits its own error snackbar, so
      // a success message here would overwrite it and claim the images were
      // updated while the table still shows pre-reload data. Only report success
      // once the refetch has actually put the new images on screen.
      const refreshed = await this.reloadPurchaseOrderAfterImageUpdate();
      if (!refreshed) return;

      // Object form, NOT ('msg', 'color'): App.vue's listener takes a single
      // `config`, so the positional style silently drops the colour and every
      // such snackbar renders deep_sku_blue.
      EventBus.$emit('show-snackbar', {
        message: buildReloadImagesMessage(result),
        color: 'success',
      });
    } catch (error: any) {
      if (Number(this.id) !== requestPoId) return;
      // parseApiErrorMessage, not error.response?.data?.message: the axios
      // instance's transformResponse leaves the body a raw STRING, so the usual
      // lookup is undefined and the server's real message is lost.
      EventBus.$emit('show-snackbar', {
        message: parseApiErrorMessage(error, 'Failed to reload product images'),
        color: 'error',
      });
    } finally {
      this.reloadingImages = false;
    }
  }

  /**
   * Refetch the PO after an image reload, reporting whether it actually worked.
   *
   * `fetchData()` resolves normally even when it failed — it catches internally
   * and emits its own error snackbar — so the caller cannot tell success from
   * failure without reading the flags it sets.
   *
   * The id comparison alone is NOT that signal, which is what this used to do.
   * `fetchData` never clears `this.purchaseOrder` before calling `getById`
   * (see its reset block, which clears only the error flags), so after a failed
   * refetch of the SAME PO the stale object is still loaded and its id still
   * matches `this.id` — the check returns true and the success snackbar
   * overwrites the error one while the table shows pre-reload data.
   * The flags are the real signal; the id check stays only to catch a PO switch
   * mid-flight. (CodeRabbit, MR !2841.)
   */
  private async reloadPurchaseOrderAfterImageUpdate(): Promise<boolean> {
    await this.fetchData();
    return !this.hasError
      && !this.purchaseOrderNotFound
      && Number(this.purchaseOrder?.id) === Number(this.id);
  }

  get hasItemsToReturn(): boolean {
    return Object.values(this.returnQuantities).some(qty => qty > 0);
  }

  get isAllItemsReceived(): boolean {
    if (!this.purchaseOrder?.items) return false;
    return this.purchaseOrder.items.every(item =>
      (item.receivedQuantity || 0) >= item.orderedQuantity
    );
  }

  // Filtered activity history (status changes, email events, tax changes)
  // Receive and return events are shown in Transaction History section.
  // AS-10746: 'tax_change' must be listed here or the audit row the tax-only
  // endpoint writes is silently dropped and a changed grand total leaves no
  // visible trace on a PO the supplier already holds.
  get filteredAuditHistory(): IPurchaseOrderHistoryItem[] {
    return this.auditHistory.filter(event =>
      event.type === 'status_change'
      || event.type === 'email_sent'
      // US-093. Cost finalisations belong here, not in Transaction History:
      // they write no receive/return row for that table to render.
      || event.type === 'cost_correction'
      // AS-10746. Same reasoning for a tax change.
      || event.type === 'tax_change'
    );
  }

  /** US-093 — the newest cost finalisation on this PO, or null. */
  get latestCostCorrectionAt(): Date | null {
    const dates = this.auditHistory
      .filter(event => event.type === 'cost_correction' && event.date)
      .map(event => new Date(event.date as string).getTime())
      .filter(time => Number.isFinite(time));

    return dates.length ? new Date(Math.max(...dates)) : null;
  }

  // Computed properties for attachments
  get purchaseOrderId(): number | undefined {
    if (this.purchaseOrder?.id) {
      return parseInt(this.purchaseOrder?.id.toString());
    }
    return undefined;
  }

  get attachments(): any[] {
    return this.purchaseOrder?.getAttachments() || [];
  }

  get canEdit(): boolean {
    return this.purchaseOrder?.canEdit() || false;
  }

  get canEditAttachments(): boolean {
    // Invoice attachments can be added/removed for all PO statuses
    return this.purchaseOrder ? true : false;
  }

  private async handleReceiveItems() {
    if (!this.purchaseOrder) return;

    // Always show review dialog for user confirmation
    this.showReceiveReviewDialog = true;
  }

  /**
   * Handler for the in-modal "Mark PO as Complete" button. Opens the
   * confirmation dialog ON TOP of the still-open Receive Products
   * modal instead of closing it first (AS-10661: closing it here used
   * to drop any quantities the merchant had already typed — canceling
   * the confirmation left them on the plain detail page, and
   * reopening "Receive Items" resets receiveQuantities via the
   * showReceiveDialog watcher). The receive modal is only closed once
   * completion is actually confirmed, in handleMarkAsCompleted().
   */
  private openMarkAsCompletedFromReceive() {
    this.showMarkAsCompletedDialog = true;
  }

  private async processReceiveItems() {
    if (!this.purchaseOrder) return;
    
    this.receiving = true;
    
    try {
      const updates = Object.entries(this.receiveQuantities)
        .filter(([_, qty]) => Number(qty) > 0)
        .map(([id, qty]) => ({
          id: Number(id),
          receivedQuantity: Number(qty)
        }));

      // Prepare cost data - always send cost data when costs are entered
      let costData: IPurchaseOrderCostData | undefined = undefined;
      const costItems = Object.entries(this.receiveCosts)
        .filter(([id, cost]) => Number(cost) > 0 && updates.some(u => u.id === Number(id)))
        .map(([id, cost]) => {
          const numId = Number(id);
          const quantity = Number(this.receiveQuantities[numId]) || 0;
          const unitCost = Number(cost);
          return {
            id: numId,
            totalCost: quantity * unitCost,
            unitCost: unitCost
          };
        });

      if (costItems.length > 0) {
        costData = {
          enabled: true,
          method: this.shopifyCostUpdateMethod as 'none' | 'latest' | 'average',
          updateShopifyCosts: this.shopifyCostUpdateMethod !== 'none',
          items: costItems
        };
      }
      
      const response = await this.purchaseOrder.receiveItems(
        updates,
        this.receiveNotes,
        this.deliveryNoteNumber,
        this.updateShopifyInventory,
        costData,
        this.markAsCompleteAfterReceive,  // Pass mark as complete flag
        this.receiveIdempotencyKey,
        this.updateSupplierCostLoaded ? this.updateSupplierCost : undefined
      );

      const wasMarkedComplete = this.markAsCompleteAfterReceive;

      this.persistCostUpdateMethod();

      // US-094: the receive went through, so nothing is blocked any more.
      this.receiveBlockedLines = [];
      this.showReceiveDialog = false;
      this.initializeReceiveData();
      this.receiveNotes = '';
      this.deliveryNoteNumber = '';
      this.markAsCompleteAfterReceive = false;  // Reset the checkbox
      // This receive intent is complete — the next receive must use a new key
      this.receiveIdempotencyKey = '';

      if (response?.duplicate) {
        await this.handleDuplicateReceive();
        return;
      }

      // Check inventory sync status
      const inventoryStatus = response?.inventoryUpdateStatus || 'unknown';
      const overReceivingCount = this.overReceivingItems.length;

      let successMessage = wasMarkedComplete
        ? 'Items received and purchase order marked as complete'
        : overReceivingCount > 0
          ? `Items received successfully (${overReceivingCount} item${overReceivingCount > 1 ? 's' : ''} over-received)`
          : 'Items received successfully';

      // Add status-specific messaging
      if (inventoryStatus === 'failed') {
        successMessage += ' (Note: Shopify inventory sync failed - can be retried)';
      } else if (inventoryStatus === 'partial') {
        successMessage += ' (Note: Some Shopify inventory updates failed)';
      } else if (inventoryStatus === 'already_synced') {
        successMessage += ' (Inventory already synced)';
      }

      const messageColor = inventoryStatus === 'failed' || inventoryStatus === 'partial'
        ? 'warning'
        : 'success';

      // US-094: App.vue's listener takes ONE config object and reads .color off
      // it. Passing the colour positionally left every receive toast — success
      // and failure alike — on the default blue, so a merchant could not tell
      // the two apart.
      EventBus.$emit('show-snackbar', { message: successMessage, color: messageColor });

      // Reload the purchase order data
      await this.fetchData();

    } catch (error: any) {
      console.error('Error receiving items:', error);

      const UNCONFIRMED_RECEIPT_MESSAGE =
        'We could not confirm this receipt. The page has been refreshed — please check the Receipts list before trying again.';

      const failure = extractReceiveError(error, UNCONFIRMED_RECEIPT_MESSAGE);

      // Every line points at a variant Shopify no longer has. Nothing was
      // committed, the quantities the merchant typed are still correct, and the
      // fix is to relink those lines — so keep the dialog open, mark them, and
      // skip the refresh that would otherwise make this look like a no-op.
      if (isRelinkableReceiveError(failure)) {
        this.receiveBlockedLines = failure.lines;
        EventBus.$emit('show-snackbar', { message: failure.message, color: 'error' });
        return;
      }

      // Anything else keeps the wording it has always had. It is deliberately
      // vague: the request may have succeeded server-side and merely lost its
      // response, so the honest instruction is to go and look at the Receipts
      // list — not to repeat whatever the server happened to say.
      this.receiveBlockedLines = [];
      EventBus.$emit('show-snackbar', { message: UNCONFIRMED_RECEIPT_MESSAGE, color: 'error' });

      // The request may have succeeded server-side even though the client saw
      // an error (lost response). Refresh so the user sees the real state —
      // and if they retry anyway, the idempotency key prevents a duplicate.
      await this.refreshAfterReceiveError('receive');
    } finally {
      this.receiving = false;
    }
  }

  // Every toggleable entry for the receive-dialog Manage Columns picker: the
  // fixed columns plus one entry per enabled custom column definition
  // (regardless of whether it's currently visible — the picker needs the
  // full option list, not just what's currently shown).
  get toggleableReceiveColumnLabels(): { id: string; label: string }[] {
    const customEntries = Object.entries(this.customColumnDefinitions || {})
      .filter(([, def]) => def.enabled)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([columnId, def]) => ({ id: `custom_${columnId}`, label: def.label }));
    // AS-11685: the live stock columns render only in the desktop table, so
    // the compact picker doesn't offer them (a saved choice is kept either way).
    const fixed = this.$vuetify.breakpoint.lgAndUp
      ? TOGGLEABLE_COLUMN_LABELS
      : TOGGLEABLE_COLUMN_LABELS.filter((c) => !(LIVE_STOCK_COLUMN_IDS as readonly string[]).includes(c.id));
    return [...fixed, ...customEntries];
  }

  // Enabled custom column definitions actively shown inside the Receive Items
  // dialog right now, sorted by the merchant's configured display order.
  // Gated per-column on visibleReceiveColumns so display and search share
  // one source of truth: a column only appears here when it's both enabled
  // AND the merchant toggled it on in the picker.
  get enabledCustomColumnHeadersForReceive(): Array<{ columnId: string; label: string; order: number }> {
    return Object.entries(this.customColumnDefinitions || {})
      .filter(([columnId, def]) => def.enabled && this.visibleReceiveColumns.includes(`custom_${columnId}`))
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([columnId, def]) => ({ columnId, label: def.label, order: def.order }));
  }

  // Label for the receive-dialog search input. Switches to the longer copy
  // only when custom columns are actually being searched right now (at least
  // one column enabled AND visible), so merchants with none selected don't
  // see UI implying the wider match still applies.
  get receiveSearchInputLabel(): string {
    return this.enabledCustomColumnHeadersForReceive.length > 0
      ? 'Search items by name, SKU, supplier SKU, or custom column'
      : 'Search items by name, SKU, or supplier SKU';
  }

  // AS-11685 — live stock columns. Desktop table only: the compact header set
  // has no room for them, and a read nobody can see is wasted Shopify quota.
  get visibleLiveStockColumns(): LiveStockColumnId[] {
    if (!this.$vuetify.breakpoint.lgAndUp) return [];
    return pickVisibleLiveStockColumns(this.visibleReceiveColumns);
  }

  get liveStockKey(): string | null {
    if (!this.showReceiveDialog) return null;
    return liveStockRequestKey(
      this.purchaseOrder?.locationId ?? null,
      (this.purchaseOrder?.items || []).map((item) => item.inventoryItemId),
      this.visibleLiveStockColumns,
    );
  }

  get liveStockStatus(): LiveStockStatus {
    return effectiveLiveStockStatus(this.liveStockKey, this.liveStockLastKey, this.liveStockStoredStatus);
  }

  get liveStockUpdatedLabel(): string | null {
    return this.liveStockStatus === 'error' ? null : updatedAgoLabel(this.liveStockFetchedAt, this.liveStockNow);
  }

  get liveStockProjectedReceive(): Map<number, number> {
    // CSV import ignores the typed quantities, so they project nothing.
    if (this.receiveMode !== 'manual') return new Map();
    return projectedReceiveByItem(
      (this.purchaseOrder?.items || []).map((item) => ({
        inventoryItemId: item.inventoryItemId,
        receiveNow: item.id != null ? this.receiveQuantities[item.id] : 0,
      })),
      this.updateShopifyInventory,
    );
  }

  // One cell view per visible live column per line, built once per render
  // (each slot reads it twice: text + title).
  get liveStockCells(): Record<string, Partial<Record<LiveStockColumnId, LiveStockCellView>>> {
    const cells: Record<string, Partial<Record<LiveStockColumnId, LiveStockCellView>>> = {};
    if (this.visibleLiveStockColumns.length === 0) return cells;
    const locationId = this.purchaseOrder?.locationId ?? null;
    for (const item of this.purchaseOrder?.items || []) {
      if (item.id == null) continue;
      const input = {
        locationId,
        inventoryItemId: item.inventoryItemId,
        receiveNow: projectedReceiveFor(this.liveStockProjectedReceive, item.inventoryItemId),
        status: this.liveStockStatus,
        levels: this.liveStockLevels,
      };
      const row: Partial<Record<LiveStockColumnId, LiveStockCellView>> = {};
      for (const column of this.visibleLiveStockColumns) row[column] = liveStockCell(column, input);
      cells[String(item.id)] = row;
    }
    return cells;
  }

  public liveStockCellFor(column: LiveStockColumnId, item: IPurchaseOrderItem): LiveStockCellView {
    return this.liveStockCells[String(item.id)]?.[column] ?? { text: '' };
  }

  @Watch('liveStockKey')
  onLiveStockKeyChange(key: string | null) {
    if (key == null) {
      // Dialog closed / nothing to read: drop any in-flight answer and the old
      // numbers, so a reopen shows "…" rather than pre-receive stock labelled
      // "Updated just now".
      ++this.liveStockOpVersion;
      this.stopLiveStockClock();
      this.liveStockLevels = {};
      this.liveStockFetchedAt = null;
      this.liveStockLastKey = null;
      this.liveStockStoredStatus = 'idle';
      return;
    }
    this.loadLiveStock();
  }

  public refreshLiveStock() {
    if (this.liveStockKey == null || this.receiving) return;
    this.loadLiveStock();
  }

  private async loadLiveStock() {
    const key = this.liveStockKey;
    const locationId = this.purchaseOrder?.locationId;
    if (key == null || locationId == null) return;
    const opId = ++this.liveStockOpVersion;
    // A refresh of the same key shows loading too (the key check alone wouldn't).
    this.liveStockLastKey = null;
    try {
      const levels = await fetchInventoryLevels(
        Number(locationId),
        (this.purchaseOrder?.items || []).map((item) => item.inventoryItemId),
        liveStockNames(this.visibleLiveStockColumns),
      );
      if (opId !== this.liveStockOpVersion) return;
      this.liveStockLevels = levels;
      this.liveStockStoredStatus = 'loaded';
      this.liveStockFetchedAt = Date.now();
      this.liveStockNow = this.liveStockFetchedAt;
      this.startLiveStockClock();
    } catch (error) {
      if (opId !== this.liveStockOpVersion) return;
      console.warn('[AS-11685] live stock read failed', error);
      this.liveStockStoredStatus = 'error';
    } finally {
      if (opId === this.liveStockOpVersion) this.liveStockLastKey = key;
    }
  }

  private startLiveStockClock() {
    if (this.liveStockClock) return;
    this.liveStockClock = setInterval(() => { this.liveStockNow = Date.now(); }, 30000);
  }

  private stopLiveStockClock() {
    if (this.liveStockClock) clearInterval(this.liveStockClock);
    this.liveStockClock = null;
  }

  // Computed property for dynamic table headers
  get receiveTableHeaders(): any[] {
    const isForeign = this.isForeignCurrencyPO;
    const storeCurr = this.storeCurrency;
    const poCurr = this.poCurrencyCode;

    // Responsive headers based on screen size
    if (this.$vuetify.breakpoint.lgAndUp) {
      const headers: any[] = [
        { text: 'Image', value: 'variant_image', sortable: false, align: 'start', width: '80px' },
        { text: 'Product', value: 'productName', width: isForeign ? '28%' : '35%' },
        { text: 'Status', value: 'quantityStatus', align: 'left', width: '150px', sortable: false },
      ];
      // Custom columns sit right after Status so warehouse-zone / colour / bin
      // location info is co-located with the visual quantity progress merchants
      // scan first. When the toggle is off, the list is empty and this is a
      // no-op.
      this.enabledCustomColumnHeadersForReceive.forEach(def => {
        headers.push({
          text: def.label,
          value: `custom_${def.columnId}`,
          sortable: false,
          align: 'start',
          width: '120px',
          columnId: def.columnId,
          type: 'custom',
        });
      });
      // AS-11685: live stock sits between the custom columns and Receive Qty.
      this.visibleLiveStockColumns.forEach((id) => {
        const text = TOGGLEABLE_COLUMN_LABELS.find((c) => c.id === id)?.label ?? id;
        headers.push({
          text, value: id, align: 'right', sortable: false, width: id === 'liveOnHandAfterReceive' ? '120px' : '100px',
        });
      });
      headers.push({ text: 'Receive Qty', value: 'receiveQuantity', align: 'right', width: '110px' });
      if (isForeign) {
        headers.push({ text: `Unit Cost (${storeCurr})`, value: 'unitCostBase', align: 'right', width: '110px', sortable: false });
      }
      headers.push(
        // US-092: the editable column is the SUPPLIER's clean unit cost in the
        // PO's currency on every PO; the landed cost Shopify receives is a
        // derived caption under the input, not this column.
        { text: `Unit Cost (${poCurr})`, value: 'unitCost', align: 'right', width: '110px', sortable: false },
        { text: 'Total Cost', value: 'totalCost', align: 'right', width: '110px', sortable: false }
      );
      return filterReceiveHeaders(headers, this.visibleReceiveColumns);
    } else {
      return filterReceiveHeaders([
        { text: 'Product', value: 'productName', width: '40%' },
        { text: 'Status', value: 'quantityStatus', align: 'left', width: '25%', sortable: false },
        { text: 'Qty', value: 'receiveQuantity', align: 'right', width: '15%' },
        { text: 'Unit Cost', value: 'unitCost', align: 'right', width: '20%', sortable: false }
      ], this.visibleReceiveColumns);
    }
  }

  // F1.2: Validation computed properties preserved exactly as-is
  // These handle manual entry validation and remain completely unchanged
  get hasValidQuantities(): boolean {
    if (!this.purchaseOrder?.items) return false;
    
    // Check if any quantities are entered and valid
    const validEntries = Object.entries(this.receiveQuantities).some(([id, qty]) => {
      if (qty !== undefined && qty !== null && qty !== '') {
        const item = this.purchaseOrder!.items.find(i => Number(i.id) === Number(id));
        if (!item) return false;
        const numQty = Number(qty);
        return !isNaN(numQty) && numQty > 0; // Allow over-receiving
      }
      return true; // Skip empty or undefined quantities
    });

    // Return true if we have valid entries and no invalid quantities
    return validEntries && this.invalidQuantityItems.length === 0;
  }

  get invalidQuantityItems(): IPurchaseOrderItem[] {
    if (!this.purchaseOrder?.items) return [];
    
    return this.purchaseOrder.items.filter(item => {
      if (!item.id) return false;
      const qty = this.receiveQuantities[item.id];
      
      // Skip empty or zero quantities
      if (qty === undefined || qty === null || qty === '' || Number(qty) === 0) return false;
      
      const numQty = Number(qty);
      
      // Only check for invalid number or negative quantity (allow over-receiving)
      return isNaN(numQty) || numQty < 0;
    });
  }

  private isOverReceiving(item: IPurchaseOrderItem): boolean {
    if (!item.id) return false;
    const qty = this.receiveQuantities[item.id];
    
    if (qty === undefined || qty === null || qty === '' || Number(qty) === 0) return false;
    
    const numQty = Number(qty);
    const remaining = item.orderedQuantity - (item.receivedQuantity || 0);
    
    // Over-receiving if: receiving more than remaining OR receiving more than total ordered quantity
    return !isNaN(numQty) && numQty > 0 && (numQty > remaining || numQty > item.orderedQuantity);
  }

  get overReceivingItems(): IPurchaseOrderItem[] {
    if (!this.purchaseOrder?.items) return [];

    return this.purchaseOrder.items.filter(item => this.isOverReceiving(item));
  }

  get hasAnyCostsEntered(): boolean {
    return Object.values(this.receiveCosts).some(cost =>
      cost !== undefined && cost !== null && Number(cost) > 0
    );
  }

  get sortedReceiveHistory(): IPurchaseReceived[] {
    if (!this.purchaseOrder?.receiveds) return [];

    // Sort by ID descending (latest first)
    return [...this.purchaseOrder.receiveds].sort((a, b) => {
      const idA = Number(a.id) || 0;
      const idB = Number(b.id) || 0;
      return idB - idA;
    });
  }

  // Combined history of receives, returns, and adjustments
  get combinedTransactionHistory(): any[] {
    // Normalize IDs to strings — Sequelize BIGINTs can surface as either
    // numeric strings or numbers across collections, and raw Set.has(id) misses
    // matches when the sides differ.
    const returnIds = new Set(this.returnHistory.map(r => String(r.id)));
    // Adjustments share the purchase_receiveds table too — exclude their IDs
    // from receiveds just like returns.
    const adjustmentIds = new Set(this.adjustmentHistory.map(a => String(a.id)));

    // Filter receiveds to exclude any IDs that appear in returnHistory or adjustmentHistory
    const receives = (this.purchaseOrder?.receiveds || [])
      .filter(r => {
        const id = String(r.id);
        return !returnIds.has(id) && !adjustmentIds.has(id);
      })
      .map(r => ({
        ...r,
        type: 'receive',
        date: r.receivedDate
      }));

    const returns = this.returnHistory.map(r => ({
      ...r,
      type: 'return',
      date: r.returnDate,
      receivedDate: r.returnDate
    }));

    const adjustments = this.adjustmentHistory.map(a => ({
      ...a,
      type: 'adjustment',
      date: a.adjustedDate,
      receivedDate: a.adjustedDate
    }));

    // Combine and sort by date descending (latest first)
    return [...receives, ...returns, ...adjustments].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA;
    });
  }

  // Reconstructs previous/new qty at the time of the selected adjustment
  // by subtracting all subsequent-adjustment deltas from the line's
  // current orderedQuantity.
  get enrichedAdjustmentLines(): any[] {
    if (!this.selectedAdjustment || !Array.isArray(this.selectedAdjustment.items)) return [];
    // Key by String(id) — payload ids may be numbers while Sequelize BIGINT
    // ids surface as strings; mismatched types break Map/Record lookups.
    const poItemsById = new Map((this.purchaseOrder?.items || []).map((i: any) => [String(i.id), i]));

    const selectedDate = new Date(this.selectedAdjustment.adjustedDate || 0).getTime();
    const laterDeltaByLine: Record<string, number> = {};
    for (const adj of (this.adjustmentHistory || [])) {
      const adjDate = new Date(adj.adjustedDate || 0).getTime();
      if (adjDate > selectedDate) {
        for (const line of (adj.items || [])) {
          const lid = String(line.purchaseOrderItemId);
          laterDeltaByLine[lid] = (laterDeltaByLine[lid] || 0) + (Number(line.delta) || 0);
        }
      }
    }

    return this.selectedAdjustment.items.map((line: any) => {
      const itemId = String(line.purchaseOrderItemId);
      const poItem: any = poItemsById.get(itemId) || {};
      const currentQty: number = Number(poItem.orderedQuantity) || 0;
      const laterDelta: number = laterDeltaByLine[itemId] || 0;
      const newQtyAtTime: number = currentQty - laterDelta;
      const delta: number = Number(line.delta) || 0;
      const previousQty: number = newQtyAtTime - delta;
      const unitCost: number = Number(line.unitCost) || 0;
      return {
        id: line.id,
        productName: poItem.productName || poItem.name || '(unnamed)',
        sku: poItem.sku || '',
        variant: poItem.variantTitle || poItem.variant || undefined,
        previousQty,
        delta,
        newQty: newQtyAtTime,
        unitCost,
        valueChange: delta * unitCost,
      };
    });
  }

  get filteredReceiveItems(): IPurchaseOrderItem[] {
    if (!this.purchaseOrder?.items) return [];

    // US-068: in scan mode the input is a scratch buffer for the scanner —
    // wedge devices burst-fire characters and we'd re-render the table on
    // every keystroke for no useful feedback (the merchant only cares about
    // the row flash after Enter). Skip filtering entirely; onScanSubmit
    // handles matching on Enter.
    if (this.scanModeEnabled) {
      return this.purchaseOrder.items;
    }

    const searchQuery = this.receiveSearchQuery.toLowerCase().trim();

    if (!searchQuery) {
      return this.purchaseOrder.items;
    }

    // Derive from the toggle-gated computed so display and search share one
    // source of truth: when the merchant has turned the toggle off, search
    // also stops matching custom columns (otherwise rows would appear with
    // no visible reason).
    const enabledCustomColumnIds: string[] = this.enabledCustomColumnHeadersForReceive
      .map(def => def.columnId);

    return this.purchaseOrder.items.filter(item => {
      const productName = (item.productName || '').toLowerCase();
      const variant = (item.variant || '').toLowerCase();
      const sku = (item.sku || '').toLowerCase();
      const storedSupplierSku = (item.supplierSku || '').toLowerCase();
      // Also match against the CURRENT supplier SKU so merchants who updated
      // the supplier SKU after creating the PO can still find the item.
      const currentSupplierSku = (
        this.currentSupplierSkuByVariantId[item.variantId] || ''
      ).toLowerCase();

      if (productName.includes(searchQuery) ||
          variant.includes(searchQuery) ||
          sku.includes(searchQuery) ||
          storedSupplierSku.includes(searchQuery) ||
          currentSupplierSku.includes(searchQuery)) {
        return true;
      }

      for (const columnId of enabledCustomColumnIds) {
        const value = this.getCustomColumnValue(item.variantId, columnId).toLowerCase();
        if (value && value.includes(searchQuery)) {
          return true;
        }
      }
      return false;
    });
  }

  get totalReceivingItems(): number {
    return Object.entries(this.receiveQuantities)
      .filter(([_, qty]) => Number(qty) > 0)
      .length;
  }

  get totalReceivingQuantity(): number {
    return Object.values(this.receiveQuantities)
      .reduce((total: number, qty: number | string) => total + Number(qty || 0), 0);
  }

  get totalReceivingCost(): number {
    return Object.values(this.totalCostsByItem).reduce((sum, cost) => sum + cost, 0);
  }

  get itemsBeingReceived(): any[] {
    if (!this.purchaseOrder?.items) return [];
    
    return Object.entries(this.receiveQuantities)
      .filter(([_, qty]) => Number(qty) > 0)
      .map(([id, qty]) => {
        const item = this.purchaseOrder!.items.find(i => i.id === Number(id));
        if (!item) return null;
        
        const receivingQty = Number(qty);
        const totalReceived = (item.receivedQuantity || 0) + receivingQty;
        
        return {
          id: item.id,
          productName: item.productName,
          receivingQuantity: receivingQty,
          orderedQuantity: item.orderedQuantity,
          receivedQuantity: item.receivedQuantity || 0,
          isOverReceiving: totalReceived > item.orderedQuantity
        };
      })
      .filter(item => item !== null);
  }

  getProductName(purchaseOrderItemId: number): string {
    const item = this.purchaseOrder?.items.find(i => i.id === purchaseOrderItemId);
    return item ? item.productName : '-';
  }

  private calculateTotalQuantity(items: any[] = []): number {
    return items.reduce((total, item) => {
      // Adjustment child rows store a signed delta; sum the absolute values
      // so "total units" reflects the magnitude of the change, not the net
      // (which could cancel out to 0 on mixed-direction adjustments).
      if (item && item.delta !== undefined && item.delta !== null) {
        const d = Number(item.delta);
        return total + (Number.isFinite(d) ? Math.abs(d) : 0);
      }
      const quantity = Number(item.receivedQuantity || item.returnedQuantity || 0);
      return total + (isNaN(quantity) ? 0 : quantity);
    }, 0);
  }

  private getInventoryStatusColor(status?: string): string {
    switch (status) {
      case 'updated':
      case 'synced':
        return 'success';
      case 'already_synced':
        return 'info';
      case 'partial':
        return 'warning';
      case 'failed':
        return 'error';
      case 'pending':
        return 'orange';
      case 'skipped':
        return 'grey';
      default:
        return 'grey';
    }
  }

  private getInventoryStatusIcon(status?: string): string {
    switch (status) {
      case 'updated':
      case 'synced':
        return 'mdi-check-circle';
      case 'already_synced':
        return 'mdi-information';
      case 'partial':
        return 'mdi-alert';
      case 'failed':
        return 'mdi-alert-circle';
      case 'skipped':
        return 'mdi-minus-circle';
      case 'pending':
        return 'mdi-clock-outline';
      default:
        return 'mdi-help-circle';
    }
  }

  private getInventoryStatusText(status?: string): string {
    switch (status) {
      case 'updated':
      case 'synced':
        return 'Updated';
      case 'already_synced':
        return 'Already Synced';
      case 'partial':
        return 'Partially Updated';
      case 'failed':
        return 'Failed';
      case 'skipped':
        return 'Skipped';
      case 'pending':
        return 'Pending';
      default:
        return 'Legacy';  // For old records without status
    }
  }

  private showReceiveDetail(receive: IPurchaseReceived) {
    this.selectedReceive = receive;
    this.resetRetryOutcome();
    this.showReceiveDetailDialog = true;
  }

  private resetRetryOutcome() {
    this.terminalRetry = null;
    this.retrySkippedLines = [];
    this.confirmingRetryAnyway = false;
  }

  private getReceiveCostUpdateStatusColor(receive: IPurchaseReceived | null): string {
    const status = this.deriveShopifyCostStatus(receive);
    if (!status) return 'grey';

    const statusColorMap: Record<string, string> = {
      'synced': 'success',
      'updated': 'success',
      'skipped': 'grey',
      'failed': 'error',
      'pending': 'orange',
      'partial': 'warning',
      'not_applicable': 'grey'
    };

    return statusColorMap[status] || 'grey';
  }

  private getReceiveCostUpdateStatusIcon(receive: IPurchaseReceived | null): string {
    const status = this.deriveShopifyCostStatus(receive);
    if (!status) return 'mdi-minus-circle';

    const statusIconMap: Record<string, string> = {
      'synced': 'mdi-check-circle',
      'updated': 'mdi-check-circle',
      'skipped': 'mdi-skip-next-circle',
      'failed': 'mdi-alert-circle',
      'pending': 'mdi-clock-outline',
      'partial': 'mdi-alert',
      'not_applicable': 'mdi-minus-circle'
    };

    return statusIconMap[status] || 'mdi-minus-circle';
  }

  private getReceiveCostUpdateStatusText(receive: IPurchaseReceived | null): string {
    const status = this.deriveShopifyCostStatus(receive);
    if (!status) return 'Not Updated';

    // Get the cost calculation method
    const method = this.getCostCalculationMethod(receive);

    const statusTextMap: Record<string, string> = {
      'synced': method ? `Updated (${method})` : 'Updated',
      'updated': method ? `Updated (${method})` : 'Updated',
      'skipped': method ? `Skipped (${method})` : 'Skipped',
      'failed': method ? `Failed (${method})` : 'Failed',
      'pending': method ? `Pending (${method})` : 'Pending',
      'partial': method ? `Partially Updated (${method})` : 'Partially Updated',
      'not_applicable': 'Not Applicable'
    };

    return statusTextMap[status] || 'Unknown';
  }

  private getCostCalculationMethod(receive: IPurchaseReceived | null): string | null {
    if (!receive) return null;
    
    // Check if the receive has unitCostCalculationMethod from model
    if ((receive as any).unitCostCalculationMethod) {
      const method = (receive as any).unitCostCalculationMethod;
      return method === 'latest' ? 'Latest Cost' : method === 'average' ? 'Weighted Avg' : method;
    }
    
    return null;
  }

  private deriveShopifyCostStatus(receive: IPurchaseReceived | null): string | null {
    if (!receive) return null;

    const receiveAny = receive as any;

    // First, check if backend provided the status from JSONB metadata
    if (receiveAny.shopifyCostSyncMetadata?.status) {
      return receiveAny.shopifyCostSyncMetadata.status;
    }

    // Fallback: check if the receive has shopifyCostStatus already derived from backend
    if (receiveAny.shopifyCostStatus) {
      return receiveAny.shopifyCostStatus;
    }

    // Check if cost update was enabled
    if (receiveAny.isUpdateUnitCostShopify === false || receiveAny.isUpdateUnitCostShopify === null) {
      return 'not_applicable'; // No cost update was attempted
    }

    // Check item-level metadata to determine overall status
    if (receive.items && receive.items.length > 0) {
      const itemStatuses = receive.items.map((item: any) =>
        item.shopifyCostSyncMetadata?.status || 'pending'
      );

      // Count status types
      const syncedCount = itemStatuses.filter(s => s === 'synced').length;
      const failedCount = itemStatuses.filter(s => s === 'failed').length;
      const pendingCount = itemStatuses.filter(s => s === 'pending').length;
      const notApplicableCount = itemStatuses.filter(s => s === 'not_applicable').length;

      // Determine overall status based on item statuses
      if (failedCount > 0 && syncedCount === 0) {
        return 'failed'; // All attempts failed
      } else if (failedCount > 0 && syncedCount > 0) {
        return 'partial'; // Mixed success and failure
      } else if (syncedCount > 0) {
        return 'synced'; // At least some succeeded
      } else if (pendingCount > 0) {
        return 'pending'; // Still pending
      } else if (notApplicableCount === itemStatuses.length) {
        return 'not_applicable'; // All not applicable
      }
    }

    // Fallback to pending if cost updates enabled
    if (receiveAny.isUpdateUnitCostShopify === true) {
      return 'pending';
    }

    return 'not_applicable';
  }

  private getReceiveItemsCount(receive: IPurchaseReceived | null): number {
    return receive?.items?.length || 0;
  }

  private getReceiveTotalQuantity(receive: IPurchaseReceived | null): number {
    return receive?.items?.reduce((total, item) => total + (parseInt(item.receivedQuantity.toString()) || 0), 0) || 0;
  }

  private getReceiveTotalAmount(receive: IPurchaseReceived | null): number {
    if (!receive || !receive.items || !this.purchaseOrder) return 0;

    return receive.items.reduce((total, receiveItem) => {
      return total + (receiveItem.totalCost || 0);
    }, 0);
  }

  /**
   * Get color for item-level sync status
   */
  private getItemSyncStatusColor(status: string | undefined): string {
    if (!status) return 'grey';

    switch (status) {
      case 'synced':
        return 'success';
      case 'pending':
        return 'orange';
      case 'failed':
        return 'error';
      case 'skipped':
      case 'not_applicable':
        return 'grey';
      default:
        return 'grey';
    }
  }

  /**
   * Get icon for item-level sync status
   */
  private getItemSyncStatusIcon(status: string | undefined): string {
    if (!status) return 'mdi-help-circle';

    switch (status) {
      case 'synced':
        return 'mdi-check-circle';
      case 'pending':
        return 'mdi-clock-outline';
      case 'failed':
        return 'mdi-alert-circle';
      case 'skipped':
      case 'not_applicable':
        return 'mdi-minus-circle';
      default:
        return 'mdi-help-circle';
    }
  }

  private getShopifyVariantDetailUrl(productId: number, variantId: number): string {
    if (!this.store || !this.store.shopName || !productId || !variantId) return '#';
    
    const shopName = this.store.shopName.replace('.myshopify.com', '');
    return `https://admin.shopify.com/store/${shopName}/products/${productId}/variants/${variantId}`;
  }

  private get receiveDetailHeaders() {
    const isForeign = this.isForeignCurrencyPO;
    const storeCurr = this.storeCurrency;
    const headers: any[] = [
      { text: 'Product', value: 'product', sortable: false },
      { text: 'Received Qty', value: 'receivedQuantity', align: 'right', sortable: false },
      { text: isForeign ? `Unit Cost (${storeCurr})` : 'Unit Cost', value: 'unitCost', align: 'right', sortable: false },
    ];
    if (isForeign) {
      headers.push({ text: `Landed Cost (${storeCurr})`, value: 'landedCost', align: 'right', sortable: false });
    }
    headers.push(
      { text: 'Total Cost', value: 'totalCost', align: 'right', sortable: false },
      { text: 'Inventory Sync', value: 'inventorySync', align: 'center', sortable: false },
      { text: 'Cost Sync', value: 'costSync', align: 'center', sortable: false },
      { text: 'Actions', value: 'actions', sortable: false, width: '150px' }
    );
    return headers;
  }

  private getReceiveDetailItems(receive: IPurchaseReceived | null) {
    if (!receive || !receive.items || !this.purchaseOrder) return [];

    return receive.items.map(receiveItem => {
      const orderItem = this.purchaseOrder!.items.find(item => item.id === receiveItem.purchaseOrderItemId);
      if (!orderItem) return null;

      return {
        id: receiveItem.id,
        purchaseOrderItemId: receiveItem.purchaseOrderItemId,
        variantId: orderItem.variantId,
        inventoryItemId: receiveItem.inventoryItemId,
        product: orderItem.productName,
        variant: orderItem.variant || 'Default',
        sku: orderItem.sku || 'N/A',
        supplierSku: orderItem.supplierSku || orderItem.sku || 'N/A',
        receivedQuantity: receiveItem.receivedQuantity,
        unitCost: receiveItem.unitCost,
        landedCost: orderItem.extra?.landed_cost ?? null,
        totalCost: receiveItem.totalCost,
        variantImage: orderItem.variantImage,
        inventorySyncStatus:
          receiveItem.shopifyInventorySyncMetadata?.status
          || (receiveItem as any).shopifyInventorySyncStatus,
        inventoryErrorCategory: receiveItem.shopifyInventorySyncMetadata?.errorCategory,
        costSyncStatus:
          receiveItem.shopifyCostSyncMetadata?.status
          || (receiveItem as any).shopifyCostStatus,
        costErrorCategory: receiveItem.shopifyCostSyncMetadata?.errorCategory,
      };
    }).filter(item => item !== null);
  }

  private getShopifyInventoryHistoryUrl(inventoryItemId: number, locationId: number): string {
    if (!this.store || !this.store.shopName) return '#';
    
    const shopName = this.store.shopName.replace('.myshopify.com', '');
    return `https://admin.shopify.com/store/${shopName}/products/inventory/${inventoryItemId}/inventory_history?location_id=${locationId}`;
  }

  private async handleMarkAsCompleted() {
    if (!this.purchaseOrder) return;

    this.markingAsCompleted = true;
    try {
      await this.purchaseOrder.updateStatus('completed');

      this.showMarkAsCompletedDialog = false;
      // Also close the Receive Products modal if it was open underneath
      // this confirmation (in-modal "Mark PO as Complete" entry point) —
      // the PO is now terminal, so there's nothing left to receive.
      this.showReceiveDialog = false;
      EventBus.$emit('show-snackbar', 'Purchase order marked as completed successfully', 'success');

      // Reload the purchase order data
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to mark purchase order as completed: ${error.message}`, 'error');
      console.error('Error marking purchase order as completed:', error);
    } finally {
      this.markingAsCompleted = false;
    }
  }

  private async handleCancelPO() {
    if (!this.purchaseOrder) return;

    this.canceling = true;
    try {
      await this.purchaseOrder.updateStatus('canceled');

      this.showCancelDialog = false;
      EventBus.$emit('show-snackbar', 'Purchase order canceled successfully', 'success');

      // Reload the purchase order data
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to cancel purchase order: ${error.message}`, 'error');
      console.error('Error canceling purchase order:', error);
    } finally {
      this.canceling = false;
    }
  }

  private async handleRevertToDraft() {
    if (!this.purchaseOrder) return;

    this.revertingToDraft = true;
    try {
      await this.purchaseOrder.updateStatus('draft');

      this.showRevertToDraftDialog = false;
      EventBus.$emit('show-snackbar', 'Purchase order reverted to draft successfully', 'success');

      // Reload the purchase order data
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to revert purchase order: ${error.message}`, 'error');
      console.error('Error reverting purchase order to draft:', error);
    } finally {
      this.revertingToDraft = false;
    }
  }

  // Leaves the dialog open on failure so the error lands next to the action
  // that caused it. See canReopen for the status rule.
  private async handleReopen() {
    if (!this.purchaseOrder) return;

    this.reopening = true;
    try {
      await this.purchaseOrder.updateStatus('received');

      this.showReopenDialog = false;
      // Single config object — App.vue's show-snackbar listener reads
      // config.color, so a positional colour argument is silently dropped
      // and the toast falls back to the default blue.
      EventBus.$emit('show-snackbar', {
        message: 'Purchase order re-opened successfully',
        color: 'success',
      });

      // Reload the purchase order data
      await this.fetchData();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', {
        message: `Failed to re-open purchase order: ${error.message}`,
        color: 'error',
      });
      console.error('Error re-opening purchase order:', error);
    } finally {
      this.reopening = false;
    }
  }

  // Add computed property
  private get canConfirmPurchaseOrder(): boolean {
    return this.purchaseOrder?.status === 'draft';
  }

  // Add confirm method
  private async confirmPurchaseOrder() {
    if (!this.purchaseOrder) return;

    this.confirming = true;
    try {
      await this.purchaseOrder.updateStatus('confirmed');
      EventBus.$emit('show-snackbar', 'Purchase order confirmed successfully', 'success');
      await this.fetchData(); // Refresh data to get updated status
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to confirm purchase order: ${error.message}`, 'error');
      console.error('Error confirming purchase order:', error);
    } finally {
      this.confirming = false;
    }
  }

  private async confirmDuplicatePurchaseOrder() {
    if (this.duplicating) return;
    if (this.isEditing) return;
    if (!this.purchaseOrder?.id) return;

    this.duplicating = true;
    try {
      const newPO = await PurchaseOrderModel.duplicate(this.purchaseOrder.id);
      this.showDuplicateDialog = false;
      EventBus.$emit('show-snackbar', `Purchase order duplicated as ${newPO.poNumber}`, 'success');
      await this.$router.push(`/purchase-orders/${newPO.id}`);
    } catch (error: any) {
      EventBus.$emit('show-snackbar', `Failed to duplicate purchase order: ${error.message}`, 'error');
      console.error('Error duplicating purchase order:', error);
    } finally {
      this.duplicating = false;
    }
  }

  // Shared teardown for a deduplicated receive: the backend already recorded
  // this receipt (this submission was a duplicate of one whose response we
  // lost), so inform the user and refresh rather than show a fresh-success
  // summary. Used by both the manual and CSV import paths.
  private async handleDuplicateReceive(): Promise<void> {
    EventBus.$emit(
      'show-snackbar',
      'This receipt was already recorded — no duplicate was created.',
      'info'
    );
    await this.fetchData();
  }

  // Refresh the PO after a failed receive so the user sees the true server
  // state (the request may have succeeded server-side despite the client
  // error). Never let the refresh itself throw. Shared by both receive paths.
  private async refreshAfterReceiveError(context: string): Promise<void> {
    try {
      await this.fetchData();
    } catch (refreshError) {
      console.error(`Error refreshing PO after failed ${context}:`, refreshError);
    }
  }

  private confirmAndReceive() {
    // Re-entry guard: a receive is already in flight — a second click must
    // not fire a second request.
    if (this.receiving || this.importingReceive) return;

    // One idempotency key per receive intent: minted on first confirm,
    // reused when the user retries after an error (the backend dedupes
    // re-submissions of the same key), reset only on success.
    if (!this.receiveIdempotencyKey) {
      this.receiveIdempotencyKey = generateIdempotencyKey();
    }

    this.showReceiveReviewDialog = false;

    // Process either manual receive or CSV import based on current mode
    if (this.receiveMode === 'import' && this.importFile) {
      this.processImportReceive();
    } else {
      this.processReceiveItems();
    }
  }

  private cancelReceiveReview() {
    this.showReceiveReviewDialog = false;
  }

  // Cost tracking methods
  // Computed property to cache total costs for all items
  get totalCostsByItem(): { [key: number]: number } {
    const costs: { [key: number]: number } = {};
    
    Object.keys(this.receiveQuantities).forEach(itemIdStr => {
      const itemId = Number(itemIdStr);
      const quantity = Number(this.receiveQuantities[itemId]) || 0;
      const unitCost = Number(this.receiveCosts[itemId]) || 0;
      costs[itemId] = quantity * unitCost;
    });
    
    return costs;
  }

  private getTotalCost(itemId: number): number {
    return this.totalCostsByItem[itemId] || 0;
  }

  // Quick action methods
  private fillAllOutstanding(): void {
    if (!this.purchaseOrder) return;
    
    // Build new quantities object with all items pre-initialized
    const newQuantities: { [key: number]: number | string } = {};
    
    // First, initialize all items to ensure reactivity
    this.purchaseOrder.items.forEach(item => {
      if (item.id) {
        // Keep existing value if not in filtered view
        newQuantities[item.id] = this.receiveQuantities[item.id] || '';
      }
    });
    
    // Then fill only the filtered items with outstanding quantities
    this.filteredReceiveItems.forEach(item => {
      if (item.id) {
        const outstanding = item.orderedQuantity - (item.receivedQuantity || 0);
        // Set value for items with outstanding quantities, empty string for others
        newQuantities[item.id] = outstanding > 0 ? outstanding : '';
      }
    });
    
    // Replace the entire object to trigger Vue reactivity
    this.receiveQuantities = { ...newQuantities };
  }

  private clearAllQuantities(): void {
    if (!this.purchaseOrder) return;
    
    // Build new quantities object preserving non-filtered items
    const newQuantities: { [key: number]: number | string } = {};
    const newCosts: { [key: number]: number } = {};
    
    // Initialize all items preserving values for non-filtered items
    this.purchaseOrder.items.forEach(item => {
      if (item.id) {
        // Keep existing value if not in filtered view
        const isFiltered = this.filteredReceiveItems.some(f => f.id === item.id);
        newQuantities[item.id] = isFiltered ? '' : (this.receiveQuantities[item.id] || '');
        newCosts[item.id] = isFiltered ? 0 : (this.receiveCosts[item.id] || 0);
      }
    });
    
    // Replace objects to trigger Vue reactivity
    this.receiveQuantities = { ...newQuantities };
    this.receiveCosts = { ...newCosts };
  }

  private initializeReceiveData(): void {
    if (!this.purchaseOrder) return;

    // Pre-initialize with all item IDs to prevent reactive addition during render
    const quantities: { [key: number]: number | string } = {};

    this.purchaseOrder.items.forEach(item => {
      if (item.id) {
        quantities[item.id] = '';
      }
    });

    this.receiveQuantities = quantities;
    // US-092. Uniform across domestic and foreign POs: the input is the
    // SUPPLIER's clean unit price, so it pre-fills from the ordered unit price.
    // It used to substitute extra.landed_cost (store currency, shipping already
    // included) on foreign POs, which double-counted shipping on submit.
    this.receiveCosts = buildInitialReceiveCosts(this.purchaseOrder.items as any);
  }

  private scrollToSettings() {
    // Scroll to the settings section for small screens
    const settingsSection = document.getElementById('settings-section');
    if (settingsSection) {
      settingsSection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }

  private handleScroll() {
    // Show settings indicator only on small screens when settings are not visible
    if (!this.$vuetify.breakpoint.lgAndUp) {
      const scrollContainer = this.$refs.scrollContainer as HTMLElement;
      const settingsSection = document.getElementById('settings-section');
      
      if (scrollContainer && settingsSection) {
        const containerBottom = scrollContainer.scrollTop + scrollContainer.clientHeight;
        const settingsTop = settingsSection.offsetTop;
        
        // Show indicator if settings section is not visible
        this.showSettingsIndicator = containerBottom < settingsTop + 100;
      }
    } else {
      this.showSettingsIndicator = false;
    }
  }

  private persistCostUpdateMethod(): void {
    try {
      localStorage.setItem('po_receive_cost_method', this.shopifyCostUpdateMethod);
    } catch {
      // Ignore storage errors (quota exceeded, private browsing)
    }
  }

  private getShopifyCostUpdateHint(): string {
    switch (this.shopifyCostUpdateMethod) {
      case 'latest':
        return 'Product unit costs will be updated in Shopify using the received costs';
      case 'average':
        return 'Product unit costs will be calculated using weighted average with current Shopify inventory';
      case 'none':
      default:
        return 'Product unit costs in Shopify will remain unchanged';
    }
  }

  private showSuccessMessage(message: string): void {
    EventBus.$emit('show-snackbar', message, 'success');
  }

  private showErrorMessage(message: string): void {
    EventBus.$emit('show-snackbar', message, 'error');
  }


  // Add placeholder methods for import functionality
  private async downloadReceiveTemplate() {
    if (!this.purchaseOrder) return;
    
    this.downloadingTemplate = true;
    try {
      // Use the model method to download CSV template
      const blob = await this.purchaseOrder.downloadReceiveTemplate();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename
      const filename = `PO-${this.purchaseOrder.poNumber}-receive-template.csv`;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Show success message
      EventBus.$emit('show-snackbar', 'CSV template downloaded successfully', 'success');
      
    } catch (error: any) {
      console.error('Error downloading template:', error);
      EventBus.$emit('show-snackbar', `Failed to download template: ${error.message}`, 'error');
    } finally {
      this.downloadingTemplate = false;
    }
  }

  private async downloadPoLabels(): Promise<void> {
    if (!this.purchaseOrder) return;
    this.downloadingPoLabels = true;
    try {
      const token = getIdentityToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await ax.get(
        `/purchase_orders/${this.purchaseOrder.id}/export-labels`,
        { responseType: 'blob', headers }
      );

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      link.download = match ? match[1] : `PO-${this.purchaseOrder.poNumber}-labels.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      EventBus.$emit('show-snackbar', { message: 'Label CSV downloaded successfully', color: 'success' });
    } catch (error: any) {
      console.error('Error downloading label CSV:', error);
      EventBus.$emit('show-snackbar', { message: 'Failed to download label CSV', color: 'error' });
    } finally {
      this.downloadingPoLabels = false;
    }
  }

  private async downloadReceiveLabels(receive: IPurchaseReceived): Promise<void> {
    const key = String(receive.id);
    this.$set(this.downloadingReceiveLabels, key, true);
    try {
      const token = getIdentityToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await ax.get(
        `/purchase_orders/${this.purchaseOrder!.id}/received/${receive.id}/export-labels`,
        { responseType: 'blob', headers }
      );

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      link.download = match ? match[1] : `PO-${this.purchaseOrder!.poNumber}-receive-${receive.id}-labels.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      EventBus.$emit('show-snackbar', { message: 'Label CSV downloaded successfully', color: 'success' });
    } catch (error: any) {
      console.error('Error downloading receive label CSV:', error);
      EventBus.$emit('show-snackbar', { message: 'Failed to download label CSV', color: 'error' });
    } finally {
      this.$set(this.downloadingReceiveLabels, key, false);
    }
  }

  private async handleImportReceive() {
    if (!this.purchaseOrder || !this.importFile) return;
    
    // Show review dialog for user confirmation (like manual receive)
    this.showReceiveReviewDialog = true;
  }

  private async processImportReceive() {
    if (!this.purchaseOrder || !this.importFile) return;
    
    this.importingReceive = true;
    
    try {
      // Prepare cost data - always send cost data when costs are entered
      let costData: IPurchaseOrderCostData | undefined = undefined;
      const costItems = Object.entries(this.receiveCosts)
        .filter(([id, cost]) => Number(cost) > 0)
        .map(([id, cost]) => {
          const numId = Number(id);
          const quantity = Number(this.receiveQuantities[numId]) || 1; // Default to 1 for CSV import
          const unitCost = Number(cost);
          return {
            id: numId,
            totalCost: quantity * unitCost,
            unitCost: unitCost
          };
        });

      // For CSV import, always send cost settings even if no manual cost items
      // The CSV file itself may contain unit costs
      if (costItems.length > 0 || this.receiveMode === 'import') {
        costData = {
          enabled: this.shopifyCostUpdateMethod !== 'none',
          method: this.shopifyCostUpdateMethod as 'none' | 'latest' | 'average',
          updateShopifyCosts: this.shopifyCostUpdateMethod !== 'none',
          items: costItems
        };
      }

      // Call the model method to import CSV
      const response = await this.purchaseOrder.importReceiveFromCSV(
        this.importFile,
        this.deliveryNoteNumber,
        this.receiveNotes,
        this.updateShopifyInventory,
        costData,
        this.receiveIdempotencyKey,
        this.updateSupplierCostLoaded ? this.updateSupplierCost : undefined
      );

      this.persistCostUpdateMethod();

      // Close dialog and reset state
      this.showReceiveDialog = false;
      this.initializeReceiveData();
      this.receiveNotes = '';
      this.deliveryNoteNumber = '';
      this.importFile = null;
      this.receiveMode = 'manual';
      // This receive intent is complete — the next receive must use a new key
      this.receiveIdempotencyKey = '';

      if (response?.duplicate) {
        await this.handleDuplicateReceive();
        return;
      }

      // Show detailed import summary
      this.showImportSummary(response);

      // Refresh the purchase order data
      await this.fetchData();

    } catch (error: any) {
      // Extract error message from different possible error structures
      let errorMessage = 'Failed to import CSV';
      
      // Debug logging
      console.error('Import CSV Error:', error);
      if (error.response) {
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response.data);
        console.error('Error response data type:', typeof error.response.data);
      }
      
      // Check if it's an axios error with response data
      if (error.response && error.response.data) {
        // The response data might be already parsed by axios or might be a string
        const responseData = error.response.data;
        
        if (typeof responseData === 'object') {
          // Already parsed object
          if (responseData.message) {
            errorMessage = responseData.message;
          } else if (responseData.error) {
            errorMessage = responseData.error;
          } else {
            // Try to find any error-like property
            const errorKeys = ['msg', 'err', 'error_message', 'errorMessage'];
            for (const key of errorKeys) {
              if (responseData[key]) {
                errorMessage = responseData[key];
                break;
              }
            }
          }
        } else if (typeof responseData === 'string') {
          // Response is a string, might need parsing
          try {
            const parsedData = JSON.parse(responseData);
            if (parsedData.message) {
              errorMessage = parsedData.message;
            } else if (typeof parsedData === 'string') {
              errorMessage = parsedData;
            }
          } catch (e) {
            // If parsing fails, use the string as is
            errorMessage = responseData;
          }
        }
      } else if (error.body && error.body.message) {
        // Fetch-style error response
        errorMessage = error.body.message;
      } else if (error.data && error.data.message) {
        // Direct data property
        errorMessage = error.data.message;
      } else if (error.message) {
        // Standard error message
        errorMessage = error.message;
      }
      
      EventBus.$emit('show-snackbar', {
        message: errorMessage,
        color: 'error',
      });

      // The import may have succeeded server-side even though the client saw
      // an error (lost response). Refresh so the user sees the real state —
      // and if they retry anyway, the idempotency key prevents a duplicate.
      await this.refreshAfterReceiveError('import receive');
    } finally {
      this.importingReceive = false;
      this.showReceiveDialog = false;
    }
  }

  private showImportSummary(response: any) {
    const {
      totalRowsInCsv = 0,
      successfullyProcessedRows = 0,
      failedRows = 0,
      errorDetails = [],
      receiveRecordId = null,
      inventoryUpdateStatus = 'skipped',
      // NEW: Cost tracking fields
      costUpdateStatus = 'skipped',
      costCalculationMethod = null,
      itemsWithCosts = 0
    } = response;

    // Store the summary data
    this.importSummaryData = {
      totalRowsInCsv,
      successfullyProcessedRows,
      failedRows,
      errorDetails,
      receiveRecordId,
      inventoryUpdateStatus,
      // NEW: Cost tracking data
      costUpdateStatus,
      costCalculationMethod,
      itemsWithCosts
    };

    // Show the summary dialog
    this.showImportSummaryDialog = true;

    // Also show a brief snackbar for quick feedback
    if (failedRows > 0) {
      EventBus.$emit('show-snackbar', 
        `Import completed: ${successfullyProcessedRows} successful, ${failedRows} failed`, 
        'warning'
      );
    } else {
      EventBus.$emit('show-snackbar', 
        `All ${successfullyProcessedRows} items imported successfully`, 
        'success'
      );
    }
  }

  /**
   * Retry Shopify inventory sync for failed receive record
   */
  private snack(message: string, color: 'success' | 'warning' | 'error') {
    EventBus.$emit('show-snackbar', { message, color });
  }

  // US-066: open the per-row Relink-to-Shopify-product dialog seeded with the
  // PO line + the failed receive item so the dialog can post back to the
  // backend with the full context.
  private openRelinkDialog(poItem: any, receivedItem: any) {
    this.relinkDialogItem = poItem;
    this.relinkDialogReceivedItem = receivedItem;
    this.relinkDialogOpen = true;
  }

  // US-094: open the same dialog with no receive item. The receive never
  // committed, so there is nothing to re-point but the PO line itself.
  private openPreReceiveRelinkDialog(line: IUnresolvableReceiveLine) {
    const poItem = (this.purchaseOrder?.items || []).find(
      (item: any) => String(item.id) === String(line.purchaseOrderItemId),
    );
    if (!poItem) return;
    this.relinkDialogItem = poItem;
    this.relinkDialogReceivedItem = null;
    this.relinkDialogOpen = true;
  }

  private async onRelinked(
    payload?: {
      purchaseReceivedItem?: { id?: number | null } | null;
      purchaseOrderItem?: any;
    } | null,
  ) {
    const id = payload?.purchaseReceivedItem?.id;
    if (id != null) {
      this.recentlyRelinked = { ...this.recentlyRelinked, [Number(id)]: true };
    }
    this.relinkDialogOpen = false;

    // Pre-receive: no receive row came back, so this was a blocked line being
    // repaired. Patch it in place and leave the receive dialog exactly as it
    // is. Refetching would rebuild the dialog — which reads as a page reload —
    // and re-initialise the quantities the merchant already typed. It buys
    // nothing either: the receive payload carries only line ids and
    // quantities, so the retry picks the repaired variant up from the database
    // whether or not the client refetched. Once the list empties, Receive is
    // worth clicking again.
    const relinkedPoItem = payload?.purchaseOrderItem;
    if (id == null && relinkedPoItem?.id != null) {
      this.applyRelinkedPoItem(relinkedPoItem);
      this.receiveBlockedLines = this.receiveBlockedLines.filter(
        (line) => String(line.purchaseOrderItemId) !== String(relinkedPoItem.id),
      );
      return;
    }

    await this.fetchData();
  }

  // Mirror the relinked line onto the in-memory PO so the dialog shows the
  // product the merchant just picked. Assigns onto the existing item object —
  // these keys already exist, so Vue 2 tracks them without Vue.set.
  private applyRelinkedPoItem(relinked: any): void {
    const item = (this.purchaseOrder?.items || []).find(
      (i: any) => String(i.id) === String(relinked.id),
    );
    if (!item) return;
    item.variantId = relinked.variantId;
    item.inventoryItemId = relinked.inventoryItemId;
    if (relinked.sku != null) item.sku = relinked.sku;
    if (relinked.productName != null) item.productName = relinked.productName;
    if (relinked.variant != null) item.variant = relinked.variant;
  }

  private isReceiveLineBlocked(itemId: number | string): boolean {
    return this.receiveBlockedLines.some(
      (line) => String(line.purchaseOrderItemId) === String(itemId),
    );
  }

  private blockedReceiveLineFor(itemId: number | string): IUnresolvableReceiveLine | null {
    return this.receiveBlockedLines.find(
      (line) => String(line.purchaseOrderItemId) === String(itemId),
    ) || null;
  }

  // Only a variant Shopify positively answered "no such thing" for can be
  // fixed by picking a different one. A lookup that failed (expired token,
  // throttling) says nothing about the variant, so offering Relink there would
  // invite the merchant to rewrite a line that was never broken.
  private canRelinkBlockedLine(line: IUnresolvableReceiveLine | null): boolean {
    return line?.errorCategory === RELINKABLE_CATEGORY;
  }

  private isRecentlyRelinked(row: { receivedItem?: { id?: number | null } | null } | null | undefined): boolean {
    const id = row?.receivedItem?.id;
    if (id == null) return false;
    return Boolean(this.recentlyRelinked[Number(id)]);
  }

  // Clears just-relinked markers only for items belonging to the receive being
  // retried. Wiping the whole map would prematurely re-show the Relink button
  // on rows in *other* failed receives that the user hasn't retried yet.
  private clearRecentlyRelinkedForReceive(receive: IPurchaseReceived | null): void {
    if (!receive?.items?.length) {
      return;
    }
    const next = { ...this.recentlyRelinked };
    for (const item of receive.items) {
      if (item?.id != null) {
        delete next[Number(item.id)];
      }
    }
    this.recentlyRelinked = next;
  }

  private async retryShopifySync() {
    if (!this.purchaseOrder) return this.snack('Cannot retry: Purchase order not loaded', 'error');
    if (!this.importSummaryData.receiveRecordId) return this.snack('Cannot retry: Receive record ID not found', 'error');

    const receiveId = this.importSummaryData.receiveRecordId;
    await runRetry({
      call: () => this.purchaseOrder!.retryShopifyInventorySync(receiveId),
      successMessage: 'Shopify inventory sync completed successfully',
      setLoading: (v) => { this.retryingSyncStatus = v; },
      emit: this.snack,
      onSuccess: (r) => {
        this.importSummaryData.inventoryUpdateStatus =
          (r as any)?.inventoryUpdateStatus || r?.syncStatus || 'updated';
      },
      afterSettle: () => this.fetchData(),
    });
  }

  private async retryCostSync() {
    if (!this.purchaseOrder) return this.snack('Cannot retry: Purchase order not loaded', 'error');
    if (!this.importSummaryData.receiveRecordId) return this.snack('Cannot retry: Receive record ID not found', 'error');

    const receiveId = this.importSummaryData.receiveRecordId;
    await runRetry({
      call: () => this.purchaseOrder!.retryCostSync(receiveId),
      successMessage: 'Shopify cost sync completed successfully',
      fallbackMessage: 'Cost retry failed — please contact support.',
      setLoading: (v) => { this.retryingCostSyncStatus = v; },
      emit: this.snack,
      onSuccess: (r) => {
        this.importSummaryData.costUpdateStatus =
          (r as any)?.costUpdateStatus || r?.syncStatus || 'updated';
      },
      afterSettle: () => this.fetchData(),
    });
  }

  private async retryReceiveInventorySync(includeTerminal = false) {
    if (!this.purchaseOrder || !this.selectedReceive) return this.snack('Cannot retry: Receive record not selected', 'error');

    // Snapshot the receive BEFORE awaiting runRetry. If selectedReceive
    // changes during the retry, afterSettle would otherwise clear markers
    // on the wrong receive.
    const retriedReceive = this.selectedReceive;
    const receiveId = retriedReceive.id;
    this.resetRetryOutcome();
    await runRetry({
      call: () => this.purchaseOrder!.retryShopifyInventorySync(receiveId, { includeTerminal: includeTerminal === true }),
      successMessage: 'Shopify inventory sync retry initiated successfully',
      setLoading: (v) => { this.retryingInventorySync = v; },
      emit: this.snack,
      isCurrent: () => this.selectedReceive?.id === receiveId,
      onTerminal: (t) => { this.terminalRetry = t; this.retrySkippedLines = t.skipped; },
      onSkipped: (lines) => { this.retrySkippedLines = lines; },
      afterSettle: async () => {
        // The "Just relinked" badge has done its job once Retry Sync has run
        // — clear regardless of outcome so the row doesn't carry a stale
        // marker if the retry surfaced a different error. Scope the clear to
        // this receive's items so badges on other failed receives survive.
        this.clearRecentlyRelinkedForReceive(retriedReceive);
        await this.fetchData();
        // Refresh whichever receipt is shown now — not necessarily the one
        // retried, if the merchant opened another while this retry ran.
        const shownId = this.selectedReceive?.id;
        this.selectedReceive = this.purchaseOrder?.receiveds?.find(r => r.id === shownId) || null;
      },
    });
  }

  private async retryReceiveCostSync() {
    if (!this.purchaseOrder || !this.selectedReceive) return this.snack('Cannot retry: Receive record not selected', 'error');

    const receiveId = this.selectedReceive.id;
    await runRetry({
      call: () => this.purchaseOrder!.retryCostSync(receiveId),
      successMessage: 'Shopify cost sync retry initiated successfully',
      fallbackMessage: 'Cost retry failed — please contact support.',
      setLoading: (v) => { this.retryingCostSync = v; },
      emit: this.snack,
      afterSettle: async () => {
        await this.fetchData();
        this.selectedReceive = this.purchaseOrder?.receiveds?.find(r => r.id === receiveId) || null;
      },
    });
  }

  /**
   * Helper to determine if inventory retry button should be shown in Receipt Details modal
   */
  private shouldShowInventoryRetryButton(receive: IPurchaseReceived | null): boolean {
    if (!receive) return false;
    return isRetryableStatus(receive.shopifyInventorySyncStatus);
  }

  /**
   * Helper to determine if cost retry button should be shown in Receipt Details modal
   */
  private shouldShowCostRetryButton(receive: IPurchaseReceived | null): boolean {
    if (!receive) return false;

    // Check if cost updates were enabled for this receive
    if (!(receive as any).isUpdateUnitCostShopify) {
      return false;
    }

    const status = this.deriveShopifyCostStatus(receive);
    // Show retry for failed, pending, or partial statuses
    return status === 'failed' || status === 'pending' || status === 'partial';
  }

  private async retryHistoryInventorySync(receive: IPurchaseReceived) {
    if (!this.purchaseOrder) return this.snack('Cannot retry: Purchase order not loaded', 'error');

    await runRetry({
      call: () => this.purchaseOrder!.retryShopifyInventorySync(receive.id),
      successMessage: 'Shopify inventory sync retry initiated successfully',
      setLoading: (v) => { this.retryingHistoryInventory = v ? receive.id : null; },
      emit: this.snack,
      afterSettle: async () => {
        this.clearRecentlyRelinkedForReceive(receive);
        await this.fetchData();
      },
    });
  }

  private async retryHistoryCostSync(receive: IPurchaseReceived) {
    if (!this.purchaseOrder) return this.snack('Cannot retry: Purchase order not loaded', 'error');

    await runRetry({
      call: () => this.purchaseOrder!.retryCostSync(receive.id),
      successMessage: 'Shopify cost sync retry initiated successfully',
      fallbackMessage: 'Cost retry failed — please contact support.',
      setLoading: (v) => { this.retryingHistoryCost = v ? receive.id : null; },
      emit: this.snack,
      afterSettle: () => this.fetchData(),
    });
  }

  /**
   * Helper to determine if inventory retry button should be shown in history table
   */
  private shouldShowHistoryInventoryRetryButton(receive: IPurchaseReceived): boolean {
    return isRetryableStatus(receive.shopifyInventorySyncStatus);
  }

  /**
   * Helper to determine if cost retry button should be shown in history table
   */
  /**
   * US-093 / AC19. What this retry will actually push to Shopify.
   *
   * `retry-cost-sync` re-sends the cost stored on the receipt (D17b) — never the
   * PO's current finalised price — so the two can legitimately differ.
   */
  private costSyncRetryAmount(receive: IPurchaseReceived): number | null {
    const items = (receive as any)?.items || [];
    const costs = items
      .map((item: any) => Number(item?.finalUnitCost ?? item?.unitCost))
      .filter((cost: number) => Number.isFinite(cost) && cost > 0);

    if (!costs.length) { return null; }
    return costs.reduce((sum: number, cost: number) => sum + cost, 0) / costs.length;
  }

  /** True when costs were finalised after this receipt was recorded. */
  private isCostSyncRetryStale(receive: IPurchaseReceived): boolean {
    const finalisedAt = this.latestCostCorrectionAt;
    if (!finalisedAt) { return false; }

    const receivedAt = (receive as any)?.createdAt || (receive as any)?.receivedDate;
    if (!receivedAt) { return false; }

    const receivedTime = new Date(receivedAt).getTime();
    return Number.isFinite(receivedTime) && finalisedAt.getTime() > receivedTime;
  }

  private costSyncRetryTooltip(receive: IPurchaseReceived): string {
    const amount = this.costSyncRetryAmount(receive);
    const base = amount === null
      ? 'Retry Cost Sync — re-sends this receipt\'s recorded cost'
      : `Retry Cost Sync — pushes ${this.formatCurrency(amount)} per unit (this receipt's recorded cost)`;

    if (!this.isCostSyncRetryStale(receive)) { return base; }

    return `${base}. Costs on this PO were finalised after this receipt, so this will overwrite the finalised cost in Shopify.`;
  }

  private shouldShowHistoryCostRetryButton(receive: IPurchaseReceived): boolean {
    // Check if cost updates were enabled for this receive
    if (!(receive as any).isUpdateUnitCostShopify) {
      return false;
    }

    const status = this.deriveShopifyCostStatus(receive);
    // Show retry for failed, pending, or partial statuses
    return status === 'failed' || status === 'pending' || status === 'partial';
  }

  /**
   * Helper to determine if inventory retry button should be shown for returns
   */
  private shouldShowReturnInventoryRetryButton(returnRecord: any): boolean {
    const status = returnRecord.shopifyInventorySyncMetadata?.status || returnRecord.shopifyInventorySyncStatus;
    return isRetryableStatus(status);
  }

  // Return Detail Methods
  private viewReturnDetail(returnRecord: any): void {
    this.selectedReturn = returnRecord;
    this.showReturnDetailDialog = true;
  }

  // Transaction History row-click dispatcher — routes to receive / return / adjustment detail
  private viewTransactionDetail(item: any): void {
    if (item.type === 'adjustment') {
      this.showAdjustmentDetail(item);
      return;
    }
    if (item.type === 'return') {
      this.viewReturnDetail(item);
      return;
    }
    this.showReceiveDetail(item);
  }

  private confirmDeleteReturn(returnRecord: any): void {
    this.returnToDelete = returnRecord;
    this.showDeleteReturnDialog = true;
  }

  // F1.3: Computed properties for import mode UI enhancements
  get csvFileRules() {
    return [
      (v: File | null) => !!v || 'CSV file is required',
      (v: File | null) => !v || v.name.toLowerCase().endsWith('.csv') || 'File must be a CSV file',
      (v: File | null) => !v || v.size <= 5000000 || 'File size must be less than 5MB'
    ];
  }

  get fullyReceivedItemsCount(): number {
    if (!this.purchaseOrder?.items) return 0;
    // Same per-line rule as the write-off count (AS-11374), so the chip and
    // the Complete warning cannot disagree.
    return this.purchaseOrder.items.length - countOutstandingLines(this.purchaseOrder.items);
  }

  get partiallyReceivedItemsCount(): number {
    if (!this.purchaseOrder?.items) return 0;
    return this.purchaseOrder.items.filter(item => {
      const received = item.receivedQuantity || 0;
      return received > 0 && received < item.orderedQuantity;
    }).length;
  }

  get pendingItemsCount(): number {
    if (!this.purchaseOrder?.items) return 0;
    return this.purchaseOrder.items.filter(item => 
      (item.receivedQuantity || 0) === 0
    ).length;
  }

  // Add new methods for file handling
  handleFileSelection(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        this.fileError = 'Please select a valid CSV file';
        this.importFile = null;
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        this.fileError = 'File size must be less than 5MB';
        this.importFile = null;
        return;
      }
      
      this.importFile = file;
      this.fileError = '';
    }
  }

  clearFileSelection() {
    this.importFile = null;
    this.fileError = '';
    // Reset the file input
    if (this.$refs.csvFileInput) {
      (this.$refs.csvFileInput as HTMLInputElement).value = '';
    }
  }

  formatFileSize(size: number): string {
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(2)} ${units[i]}`;
  }

  openFileInput() {
    (this.$refs.csvFileInput as HTMLInputElement).click();
  }

  private checkImportFlag() {
    if (this.$route.query.import === 'true') {
      // Use nextTick to ensure the component has rendered before opening the dialog
      this.$nextTick(() => {
        this.showImportItemsDialog = true;
        // Clean up the URL
        const newQuery = { ...this.$route.query };
        delete newQuery.import;
        this.$router.replace({ query: newQuery }).catch(()=>{});
      });
    }
  }

  private onImportSuccess() {
    EventBus.$emit('show-snackbar', 'Items imported successfully!', 'success');
    this.fetchData(); // Refresh data to show new items
  }

  // Load saved email preferences from localStorage
  private loadEmailPreferences(): void {
    try {
      const saved = localStorage.getItem(this.localStorageKey);
      if (saved !== null) {
        const prefs = JSON.parse(saved);
        // Handle legacy formats
        if (typeof prefs === 'boolean') {
          this.includePricing = prefs;
        } else {
          this.includePricing = prefs.includePricing ?? prefs.includeUnitPrice ?? true;
        }
      }
    } catch (error) {
      console.warn('Failed to load email preferences:', error);
      this.includePricing = true;
    }
  }

  // Save email preferences to localStorage
  private saveEmailPreferences(): void {
    try {
      localStorage.setItem(
        this.localStorageKey,
        JSON.stringify({
          includePricing: this.includePricing
        })
      );
    } catch (error) {
      console.warn('Failed to save email preferences:', error);
    }
  }

  // --- PO Email Template ---

  private get hasPOEmailTemplate(): boolean {
    return !!(this.poEmailTemplate.subject || this.poEmailTemplate.body);
  }

  private get templateUserVariables(): string[] {
    return this.hasPOEmailTemplate ? listPOTemplateUserVariables(this.poEmailTemplate) : [];
  }

  private humanizeVariableLabel(key: string): string {
    return humanizePOTemplateVariable(key);
  }

  private async fetchPOEmailTemplate(): Promise<void> {
    try {
      const response: any = await get('/purchase_orders/config/email-template');
      const data = response?.data || { subject: '', body: '' };
      this.poEmailTemplate = { subject: data.subject || '', body: data.body || '' };
    } catch (error) {
      console.warn('Failed to fetch PO email template:', error);
      this.poEmailTemplate = { subject: '', body: '' };
    }
  }

  private formatTemplateDate(value: string | null | undefined): string {
    if (!value) return '';
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString();
    } catch {
      return String(value);
    }
  }

  private buildTemplateContext(): POTemplateContext {
    const po: any = this.purchaseOrder || {};
    const supplier: any = this.supplier || {};
    const supplierName: string = supplier?.name || '';
    const contactName: string =
      supplier?.contactName ||
      (supplier?.options && supplier.options.contactName) ||
      (supplierName ? supplierName.split(' ')[0] : '');
    const totalUnits = (this.items || []).reduce(
      (sum: number, item: any) => sum + Number(item.orderedQuantity || 0),
      0,
    );
    const currencyCode = po?.currencyCode || '';
    const formatMoney = (v: number): string => {
      const n = Number(v || 0);
      return currencyCode ? `${n.toFixed(2)} ${currencyCode}` : n.toFixed(2);
    };
    return {
      po: {
        number: String(po.poNumber || ''),
        order_date: this.formatTemplateDate(po.orderDate),
        expected_delivery_date: this.formatTemplateDate(po.expectedDeliveryDate),
        total_amount: formatMoney(po.totalAmount),
        total_units: totalUnits,
        items_count: (this.items || []).length,
      },
      supplier: {
        name: supplierName,
        contact_name: contactName,
        contact_email: supplier?.contactEmail || '',
        contact_phone: supplier?.contactPhone || '',
      },
      merchant: {
        name: this.companyName || (this.store && (this.store as any).name) || '',
      },
      today: new Date().toLocaleDateString(),
      userVariables: { ...this.userVariables },
    };
  }

  private applyPOEmailTemplate(): void {
    if (!this.hasPOEmailTemplate) return;
    const ctx = this.buildTemplateContext();
    const rendered = renderPOTemplate(this.poEmailTemplate, ctx);
    this.emailSubject = rendered.subject;
    this.emailMessage = rendered.body;
  }

  @Watch('showEmailDialog')
  async onShowEmailDialogChange(isOpen: boolean): Promise<void> {
    if (!isOpen) {
      // Reset per-send user variables + template on close so a later open doesn't
      // inherit stale rendered subject/body if the template was deleted server-side.
      this.userVariables = {};
      this.poEmailTemplate = { subject: '', body: '' };
      // AS-11593: CC is per send. After a Cancel or a send it must not reach another PO's email.
      this.ccRecipients = [];
      return;
    }
    // US-069: default the send dialog's column template to the on-screen selection.
    this.sendTemplateId = this.selectedTemplateId;
    const defaultSubject = this.purchaseOrder?.poNumber
      ? `Purchase Order #${this.purchaseOrder.poNumber}`
      : '';
    await this.fetchPOEmailTemplate();
    // Dialog may have closed while the fetch was in flight; bail so we don't stomp the reset.
    if (!this.showEmailDialog) return;
    if (this.hasPOEmailTemplate) {
      // Initialize any user-variable keys to empty strings so Vue reactivity picks up input
      const keys = this.templateUserVariables;
      const next: Record<string, string> = {};
      for (const k of keys) next[k] = this.userVariables[k] || '';
      this.userVariables = next;
      this.applyPOEmailTemplate();
    } else {
      // No template: reset to defaults so a prior template's rendered content doesn't linger.
      this.userVariables = {};
      this.emailSubject = defaultSubject;
      this.emailMessage = '';
    }
  }

  // US-069: default the export dialog's column template to the on-screen selection.
  @Watch('showExportDialog')
  onShowExportDialogChange(isOpen: boolean): void {
    if (isOpen) {
      this.exportTemplateId = this.selectedTemplateId;
    }
  }

  @Watch('userVariables', { deep: true })
  onUserVariablesChange(): void {
    if (this.showEmailDialog && this.hasPOEmailTemplate) {
      this.applyPOEmailTemplate();
    }
  }

  @Watch('companyName')
  onCompanyNameChange(): void {
    if (this.showEmailDialog && this.hasPOEmailTemplate) {
      this.applyPOEmailTemplate();
    }
  }

  private loadSavedEmails(): void {
    try {
      const saved = localStorage.getItem(this.savedEmailsKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.savedEmails = Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      console.warn('Failed to load saved emails:', error);
      this.savedEmails = [];
    }
    try {
      const savedNames = localStorage.getItem(this.savedCompanyNamesKey);
      if (savedNames) {
        const parsed = JSON.parse(savedNames);
        this.savedCompanyNames = Array.isArray(parsed) ? parsed : [];
      }
    } catch (error) {
      console.warn('Failed to load saved company names:', error);
      this.savedCompanyNames = [];
    }
  }

  private saveSavedEmails(): void {
    try {
      const allEmails = [
        ...this.emailRecipients,
        ...this.ccRecipients,
        ...this.replyToEmails,
      ];
      const unique = [...new Set([...this.savedEmails, ...allEmails])].filter(Boolean);
      this.savedEmails = unique.slice(-20);
      localStorage.setItem(this.savedEmailsKey, JSON.stringify(this.savedEmails));

      if (this.companyName) {
        const uniqueNames = [...new Set([...this.savedCompanyNames, this.companyName])].filter(Boolean);
        this.savedCompanyNames = uniqueNames.slice(-10);
        localStorage.setItem(this.savedCompanyNamesKey, JSON.stringify(this.savedCompanyNames));
      }
    } catch (error) {
      console.warn('Failed to save emails:', error);
    }
  }

  // Load export preferences from localStorage
  private loadExportPreferences(): void {
    try {
      const saved = localStorage.getItem('po_export_preferences');
      if (saved) {
        const prefs = JSON.parse(saved);
        // Handle legacy format with separate flags
        this.exportIncludePricing = prefs.includePricing ?? prefs.includeUnitPrice ?? true;
        this.exportGroupByProduct = prefs.groupByProduct ?? false;
      }
    } catch (error) {
      console.warn('Failed to load export preferences:', error);
    }
  }

  // Save export preferences to localStorage
  private saveExportPreferences(): void {
    try {
      localStorage.setItem('po_export_preferences', JSON.stringify({
        includePricing: this.exportIncludePricing,
        groupByProduct: this.exportGroupByProduct
      }));
    } catch (error) {
      console.warn('Failed to save export preferences:', error);
    }
  }

  private get canImportItems(): boolean {
    if (!this.purchaseOrder) return false;
    return this.purchaseOrder.status === 'draft';
  }

  private get canAddProducts(): boolean {
    if (!this.purchaseOrder) return false;
    const hasSupplierId = !!(this.supplier?.id || this.purchaseOrder.supplierId);
    return this.canEditPurchaseOrder && hasSupplierId;
  }

  // Attachment event handlers
  private async handleAttachmentsAdded(newAttachments: any[]): Promise<void> {
    if (!this.purchaseOrder) return;

    try {
      this.purchaseOrder.addAttachments(newAttachments);
      await this.saveExtraToServer();
      // Local state is already updated, no need to reload page
    } catch (error) {
      console.error('Failed to save attachments:', error);
      this.showMessage('Failed to save attachments', 'error');
    }
  }

  private async handleAttachmentRemoved(index: number): Promise<void> {
    if (!this.purchaseOrder) return;

    try {
      this.purchaseOrder.removeAttachment(index);
      await this.saveExtraToServer();
      // Local state is already updated, no need to reload page
    } catch (error) {
      console.error('Failed to remove attachment:', error);
      this.showMessage('Failed to remove attachment', 'error');
    }
  }

  // Return Items methods (Story 5)
  private getAvailableToReturn(item: IPurchaseOrderItem): number {
    const received = item.receivedQuantity || 0;
    const returned = item.returnedQuantity || 0;
    return Math.max(0, received - returned);
  }

  private validateReturnQuantity(item: IPurchaseOrderItem): void {
    if (!item.id) return;
    const qty = this.returnQuantities[item.id];
    const available = this.getAvailableToReturn(item);

    if (qty && qty > available) {
      Vue.set(this.returnQuantityErrors, item.id, `Maximum return quantity is ${available}`);
    } else if (qty && qty < 0) {
      Vue.set(this.returnQuantityErrors, item.id, 'Quantity must be positive');
    } else {
      Vue.delete(this.returnQuantityErrors, item.id);
    }
  }

  private getReturnQuantityError(item: IPurchaseOrderItem): string {
    return item.id ? (this.returnQuantityErrors[item.id] || '') : '';
  }

  private closeReturnDialog(): void {
    this.showReturnDialog = false;
    this.showReturnReviewDialog = false;
    this.returnQuantities = {};
    this.returnQuantityErrors = {};
    this.returnReason = '';
    this.returnNotes = '';
    this.returnSearchQuery = '';
  }

  private confirmAndReturn(): void {
    // Don't close the dialog here - let submitReturn() close it after API call
    this.submitReturn();
  }

  private cancelReturnReview(): void {
    this.showReturnReviewDialog = false;
  }

  private async submitReturn(): Promise<void> {
    try {
      this.submittingReturn = true;

      const items = this.returnableItems
        .filter(item => this.returnQuantities[item.id!] && this.returnQuantities[item.id!] > 0)
        .map(item => ({
          purchaseOrderItemId: item.id,
          returnedQuantity: this.returnQuantities[item.id!]
        }));

      const response: any = await post(`/purchase_orders/${this.purchaseOrder!.id}/return`, {
        items,
        reason: this.returnReason || null,
        notes: this.returnNotes || null
      });

      const { inventoryUpdateStatus } = response;
      let message = 'Return created successfully';
      if (inventoryUpdateStatus === 'synced') {
        message = 'Return created and inventory updated successfully';
      } else if (inventoryUpdateStatus === 'partial') {
        message = 'Return created (some inventory items failed to sync)';
      }

      EventBus.$emit('show-snackbar', message, inventoryUpdateStatus === 'synced' ? 'success' : 'warning');
      this.closeReturnDialog();
      await this.fetchData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to submit return';
      EventBus.$emit('show-snackbar', errorMessage, 'error');
      // Keep dialog open on error so user can retry
    } finally {
      this.submittingReturn = false;
    }
  }

  // Return History methods (Story 6)
  private async loadReturnHistory(): Promise<void> {
    if (!this.purchaseOrder?.id) return;
    this.loadingReturnHistory = true;
    try {
      const response: any = await get(`/purchase_orders/${this.purchaseOrder.id}/returns`);
      this.returnHistory = response.data || [];
    } catch (error: any) {
      console.error('Error loading return history:', error);
      EventBus.$emit('show-snackbar', 'Failed to load return history', 'error');
    } finally {
      this.loadingReturnHistory = false;
    }
  }

  private async onAdjustmentSaved(_result: any): Promise<void> {
    this.showAdjustDialog = false;
    EventBus.$emit('show-snackbar', 'Adjustment saved successfully', 'success');
    // fetchData() reloads the PO and fans out the adjustment/return
    // history fetches in parallel — don't duplicate loadAdjustmentHistory.
    await this.fetchData();
  }

  private onAdjustmentError(message: string): void {
    EventBus.$emit('show-snackbar', message || 'Failed to save adjustment', 'error');
  }

  private async loadAdjustmentHistory(): Promise<void> {
    if (!this.purchaseOrder?.id) return;
    this.loadingAdjustmentHistory = true;
    try {
      const response: any = await get(`/purchase_orders/${this.purchaseOrder.id}/adjustments`);
      this.adjustmentHistory = response.data || [];
    } catch (error: any) {
      console.error('Error loading adjustment history:', error);
      EventBus.$emit('show-snackbar', 'Failed to load adjustment history', 'error');
    } finally {
      this.loadingAdjustmentHistory = false;
    }
  }

  private showAdjustmentDetail(adjustmentRecord: any): void {
    this.selectedAdjustment = adjustmentRecord;
    this.showAdjustmentDetailDialog = true;
  }

  private showReturnDetail(returnRecord: any): void {
    this.selectedReturn = returnRecord;
    this.showReturnDetailDialog = true;
  }

  private async retryReturnSync(returnRecord: any): Promise<void> {
    if (!this.purchaseOrder?.id || !returnRecord.id) return;
    this.retryingReturnSync = returnRecord.id;
    try {
      await post(`/purchase_orders/${this.purchaseOrder.id}/returns/${returnRecord.id}/retry-shopify-sync`);
      EventBus.$emit('show-snackbar', 'Inventory sync retry initiated', 'success');
      await this.loadReturnHistory();
      if (this.selectedReturn && this.selectedReturn.id === returnRecord.id) {
        const updated = this.returnHistory.find(r => r.id === returnRecord.id);
        if (updated) this.selectedReturn = updated;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to retry inventory sync';
      EventBus.$emit('show-snackbar', errorMessage, 'error');
    } finally {
      this.retryingReturnSync = null;
    }
  }

  private async deleteReturn(): Promise<void> {
    if (!this.purchaseOrder?.id || !this.returnToDelete) return;
    this.deletingReturn = true;
    try {
      await del(`/purchase_orders/${this.purchaseOrder.id}/returns/${this.returnToDelete.id}`);
      EventBus.$emit('show-snackbar', 'Return deleted successfully', 'success');
      this.showDeleteReturnDialog = false;
      this.returnToDelete = null;
      await this.loadReturnHistory();
      await this.fetchData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete return';
      EventBus.$emit('show-snackbar', errorMessage, 'error');
    } finally {
      this.deletingReturn = false;
    }
  }

  // Return helper methods
  private getReturnSyncStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      synced: 'success',
      pending: 'info',
      partial: 'warning',
      failed: 'error',
      skipped: 'grey'
    };
    return colors[status] || 'grey';
  }

  private getReturnSyncStatusIcon(status: string): string {
    const icons: { [key: string]: string } = {
      synced: 'mdi-check-circle',
      pending: 'mdi-clock-outline',
      partial: 'mdi-alert',
      failed: 'mdi-close-circle',
      skipped: 'mdi-minus-circle'
    };
    return icons[status] || 'mdi-help-circle';
  }

  private getReturnSyncStatusIconColor(status: string): string {
    const colors: { [key: string]: string } = {
      synced: '#4CAF50',
      pending: '#59C8E4',
      partial: '#f57c00',
      failed: '#F44336',
      skipped: '#9E9E9E'
    };
    return colors[status] || '#9E9E9E';
  }

  private getReturnSyncStatusText(status: string): string {
    const texts: { [key: string]: string } = {
      synced: 'Synced',
      pending: 'Pending',
      partial: 'Partial',
      failed: 'Failed',
      skipped: 'Skipped'
    };
    return texts[status] || status;
  }

  private getReturnItemSyncStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      synced: 'success',
      failed: 'error',
      pending: 'info'
    };
    return colors[status] || 'grey';
  }

  private getReturnItemSyncStatusText(status: string): string {
    const texts: { [key: string]: string } = {
      synced: 'Synced',
      failed: 'Failed',
      pending: 'Pending'
    };
    return texts[status] || status;
  }

  private shouldShowReturnRetryButton(returnRecord: any): boolean {
    const status = returnRecord.shopifyInventorySyncMetadata?.status;
    return status === 'failed' || status === 'partial';
  }

  private calculateTotalReturnQuantity(items: any[]): number {
    if (!items) return 0;
    return items.reduce((sum, item) => sum + (Number(item.returnedQuantity) || 0), 0);
  }

  private getReturnTotalValue(returnRecord: any): number {
    if (!returnRecord?.items || !this.purchaseOrder) return 0;
    return returnRecord.items.reduce((sum: number, returnItem: any) => {
      const orderItem = this.purchaseOrder!.items.find(item => item.id === returnItem.purchaseOrderItemId);
      const unitPrice = orderItem ? Number(orderItem.unitPrice) || 0 : 0;
      return sum + (Number(returnItem.returnedQuantity) || 0) * unitPrice;
    }, 0);
  }

  private getReturnDetailItems(returnRecord: any): any[] {
    if (!returnRecord || !returnRecord.items || !this.purchaseOrder) {
      return [];
    }

    // Map return items to include product details from purchaseOrder.items (same pattern as receive items)
    return returnRecord.items.map((returnItem: any) => {
      const orderItem = this.purchaseOrder!.items.find(item => item.id === returnItem.purchaseOrderItemId);
      if (!orderItem) return null;

      return {
        productName: orderItem.productName,
        variant: orderItem.variant || 'Default',
        sku: orderItem.sku || 'N/A',
        returnedQuantity: Number(returnItem.returnedQuantity) || 0,
        unitPrice: orderItem.unitPrice != null ? Number(orderItem.unitPrice) : null,
        shopifySyncStatus: returnItem.shopifyInventorySyncMetadata?.status || 'pending',
        inventoryItemId: returnItem.inventoryItemId, // For Track Inventory button
        variantId: returnItem.variantId
      };
    }).filter((item: any) => item !== null);
  }

  private showMessage(message: string, type: string): void {
    // Use existing notification system
    EventBus.$emit('show-message', { message, type });
  }
}
